import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { axios } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import kareKursBulvarLogo from '../../images/karekursbluvar.png';
import WeeklyStudyProgramBuilder from './WeeklyStudyProgramBuilder';
import { generateCurriculumTopicBank } from './studyProgramTopics';

const TOPIC_BANK_STORAGE_KEY = 'asder_rehber_konu_havuzu_v1';
const REPEAT_TASK_STORAGE_KEY = 'asder_rehber_tekrarlar_v1';

const makeTopics = (sinif, ders, donem, unite, konular, idPrefix) => (
  konular.map((konu, index) => ({
    id: `${idPrefix}-${index + 1}`,
    sinif,
    ders,
    donem,
    unite,
    konu,
    sira: index + 1,
    aktif: true,
  }))
);

const parseTopicText = (raw, sinif, idPrefix) => {
  const subjects = new Set([
    'Türkçe',
    'Matematik',
    'Tarih',
    'Coğrafya',
    'Felsefe',
    'Din Kültürü ve Ahlak Bilgisi',
    'Geometri',
    'Fizik',
    'Kimya',
    'Biyoloji',
    'Türk Dili ve Edebiyatı',
    'Sosyoloji',
    'Psikoloji',
    'Mantık',
  ]);
  const rows = [];
  let currentSubject = '';
  let subjectOrder = 0;
  let topicOrder = 0;

  raw.split('\n').map(line => line.trim()).forEach((line) => {
    if (!line || line === 'Konu') return;
    if (subjects.has(line)) {
      currentSubject = line;
      subjectOrder += 1;
      topicOrder = 0;
      return;
    }
    if (!currentSubject) return;
    topicOrder += 1;
    rows.push({
      id: `${idPrefix}-${subjectOrder}-${topicOrder}`,
      sinif,
      ders: currentSubject,
      donem: 'Genel',
      unite: `${sinif} ${currentSubject}`,
      konu: line,
      sira: topicOrder,
      aktif: true,
    });
  });
  return rows;
};

const INITIAL_MATEMATIK_9_TOPICS = [
  { sinif: '9. Sınıf', ders: 'Matematik', donem: '1. Dönem', unite: '1. Ünite: SAYILAR', konu: 'Gerçek Sayıların Üslü ve Köklü Gösterimleri ile Yapılan İşlemler' },
  { sinif: '9. Sınıf', ders: 'Matematik', donem: '1. Dönem', unite: '1. Ünite: SAYILAR', konu: 'Gerçek Sayı Aralıkları ile Yapılan İşlemler' },
  { sinif: '9. Sınıf', ders: 'Matematik', donem: '1. Dönem', unite: '1. Ünite: SAYILAR', konu: 'Sayı Kümeleri ve İşlem Özellikleri' },
  { sinif: '9. Sınıf', ders: 'Matematik', donem: '1. Dönem', unite: '1. Ünite: SAYILAR', konu: 'İki Kare Farkı ve Tamkare Özdeşlikleri' },
  { sinif: '9. Sınıf', ders: 'Matematik', donem: '1. Dönem', unite: '2. Ünite: NİCELİKLER VE DEĞİŞİMLER', konu: 'Gerçek Sayılarda Tanımlı Doğrusal Fonksiyonlar ve Mutlak Değer Fonksiyonlarının Nitel Özellikleri' },
  { sinif: '9. Sınıf', ders: 'Matematik', donem: '1. Dönem', unite: '2. Ünite: NİCELİKLER VE DEĞİŞİMLER', konu: 'Doğrusal Fonksiyonlarla İfade Edilen Denklem ve Eşitsizlikler' },
  { sinif: '9. Sınıf', ders: 'Matematik', donem: '1. Dönem', unite: '3. Ünite: ALGORİTMA ve BİLİŞİM', konu: 'Algoritma Temelli Problemler' },
  { sinif: '9. Sınıf', ders: 'Matematik', donem: '1. Dönem', unite: '3. Ünite: ALGORİTMA ve BİLİŞİM', konu: 'Mantık Bağlaçları ve Niceleyiciler' },
  { sinif: '9. Sınıf', ders: 'Matematik', donem: '2. Dönem', unite: '4. Ünite: GEOMETRİK ŞEKİLLER', konu: 'Üçgende Açı ve Kenarla İlgili Özellikler' },
  { sinif: '9. Sınıf', ders: 'Matematik', donem: '2. Dönem', unite: '4. Ünite: GEOMETRİK ŞEKİLLER', konu: 'Üçgende Açı Özellikleri Arasındaki İlişkiler' },
  { sinif: '9. Sınıf', ders: 'Matematik', donem: '2. Dönem', unite: '4. Ünite: GEOMETRİK ŞEKİLLER', konu: 'Üçgende Kenar Özellikleri Arasındaki İlişkiler' },
  { sinif: '9. Sınıf', ders: 'Matematik', donem: '2. Dönem', unite: '5. Ünite: EŞLİK ve BENZERLİK', konu: 'Geometrik dönüşümlerle ilgili çıkarım yapabilme' },
  { sinif: '9. Sınıf', ders: 'Matematik', donem: '2. Dönem', unite: '5. Ünite: EŞLİK ve BENZERLİK', konu: 'Geometrik Şekillerin Yansıma' },
  { sinif: '9. Sınıf', ders: 'Matematik', donem: '2. Dönem', unite: '5. Ünite: EŞLİK ve BENZERLİK', konu: 'Öteleme ve Dönme Dönüşümleri Sonrası Görünüşü ve Bu Görünüşün Özellikleri' },
  { sinif: '9. Sınıf', ders: 'Matematik', donem: '2. Dönem', unite: '5. Ünite: EŞLİK ve BENZERLİK', konu: 'Üçgenlerde Eşlik ve Benzerlik Koşulları' },
  { sinif: '9. Sınıf', ders: 'Matematik', donem: '2. Dönem', unite: '6. Ünite: İSTATİSTİKSEL ve ARAŞTIRMA SÜRECİ', konu: 'Tek Nicel Değişken İçeren İstatistiksel Problemi Oluşturma' },
  { sinif: '9. Sınıf', ders: 'Matematik', donem: '2. Dönem', unite: '6. Ünite: İSTATİSTİKSEL ve ARAŞTIRMA SÜRECİ', konu: 'Verileri Toplama ve Analize Hazır Hâle Getirme' },
  { sinif: '9. Sınıf', ders: 'Matematik', donem: '2. Dönem', unite: '6. Ünite: İSTATİSTİKSEL ve ARAŞTIRMA SÜRECİ', konu: 'Bulgulara Ulaşma ve Bulguları Yorumlama' },
  { sinif: '9. Sınıf', ders: 'Matematik', donem: '2. Dönem', unite: '6. Ünite: İSTATİSTİKSEL ve ARAŞTIRMA SÜRECİ', konu: 'İstatistiksel Görsel, Özet, Sonuç, Yorum, Çıkarım veya Tahminleri Değerlendirme' },
  { sinif: '9. Sınıf', ders: 'Matematik', donem: '2. Dönem', unite: '7. Ünite: VERİDEN OLASILIĞA', konu: 'Olayların Olasılığını Deneysel ve Teorik Olarak İnceleme' },
  { sinif: '9. Sınıf', ders: 'Matematik', donem: '2. Dönem', unite: '7. Ünite: VERİDEN OLASILIĞA', konu: 'Olayların Deneysel ve Teorik Olasılığını İlişkilendirme' },
].map((item, index) => ({ ...item, id: `9-matematik-${index + 1}`, sira: index + 1, aktif: true }));

const INITIAL_BIYOLOJI_9_TOPICS = [
  { sinif: '9. Sınıf', ders: 'Biyoloji', donem: '1. Dönem', unite: '1. Ünite: YAŞAM / 1.1. Yaşam Bilimi: Biyoloji', konu: 'Biyolojinin Önemi' },
  { sinif: '9. Sınıf', ders: 'Biyoloji', donem: '1. Dönem', unite: '1. Ünite: YAŞAM / 1.1. Yaşam Bilimi: Biyoloji', konu: 'Biyoloji Biliminin Gelişimindeki Dönüm Noktaları' },
  { sinif: '9. Sınıf', ders: 'Biyoloji', donem: '1. Dönem', unite: '1. Ünite: YAŞAM / 1.1. Yaşam Bilimi: Biyoloji', konu: 'Bilimin Doğası, Bilimsel Araştırma Süreçleri, Bilim Etiği' },
  { sinif: '9. Sınıf', ders: 'Biyoloji', donem: '1. Dönem', unite: '1. Ünite: YAŞAM / 1.1. Yaşam Bilimi: Biyoloji', konu: 'Canlıların Ortak Özellikleri' },
  { sinif: '9. Sınıf', ders: 'Biyoloji', donem: '1. Dönem', unite: '1. Ünite: YAŞAM / 1.2. Sınıflandırma ve Biyoçeşitlilik', konu: 'Sınıflandırmada Temel Yaklaşımlar ve Modern Sınıflandırma' },
  { sinif: '9. Sınıf', ders: 'Biyoloji', donem: '1. Dönem', unite: '1. Ünite: YAŞAM / 1.2. Sınıflandırma ve Biyoçeşitlilik', konu: 'Üç Üst Âlem (Domain) Sisteminde Yer Alan Canlılar ve Genel Özellikleri (Bakteriler, Arkeler, Ökaryotlar)' },
  { sinif: '9. Sınıf', ders: 'Biyoloji', donem: '1. Dönem', unite: '1. Ünite: YAŞAM / 1.2. Sınıflandırma ve Biyoçeşitlilik', konu: 'Biyoçeşitlilik' },
  { sinif: '9. Sınıf', ders: 'Biyoloji', donem: '2. Dönem', unite: '2. Ünite: ORGANİZASYON / 2.1. İnorganik Moleküller', konu: 'Su' },
  { sinif: '9. Sınıf', ders: 'Biyoloji', donem: '2. Dönem', unite: '2. Ünite: ORGANİZASYON / 2.1. İnorganik Moleküller', konu: 'Mineraller' },
  { sinif: '9. Sınıf', ders: 'Biyoloji', donem: '2. Dönem', unite: '2. Ünite: ORGANİZASYON / 2.2. Organik Moleküller', konu: 'Karbonhidratlar' },
  { sinif: '9. Sınıf', ders: 'Biyoloji', donem: '2. Dönem', unite: '2. Ünite: ORGANİZASYON / 2.2. Organik Moleküller', konu: 'Yağlar' },
  { sinif: '9. Sınıf', ders: 'Biyoloji', donem: '2. Dönem', unite: '2. Ünite: ORGANİZASYON / 2.2. Organik Moleküller', konu: 'Proteinler' },
  { sinif: '9. Sınıf', ders: 'Biyoloji', donem: '2. Dönem', unite: '2. Ünite: ORGANİZASYON / 2.2. Organik Moleküller', konu: 'Nükleik Asitler' },
  { sinif: '9. Sınıf', ders: 'Biyoloji', donem: '2. Dönem', unite: '2. Ünite: ORGANİZASYON / 2.2. Organik Moleküller', konu: 'Vitaminler' },
  { sinif: '9. Sınıf', ders: 'Biyoloji', donem: '2. Dönem', unite: '2. Ünite: ORGANİZASYON / 2.3. Hücrenin Organizasyonu', konu: 'Prokaryot ve Ökaryot Hücre' },
  { sinif: '9. Sınıf', ders: 'Biyoloji', donem: '2. Dönem', unite: '2. Ünite: ORGANİZASYON / 2.3. Hücrenin Organizasyonu', konu: 'Hücre Zarı' },
  { sinif: '9. Sınıf', ders: 'Biyoloji', donem: '2. Dönem', unite: '2. Ünite: ORGANİZASYON / 2.3. Hücrenin Organizasyonu', konu: 'Sitoplazma' },
  { sinif: '9. Sınıf', ders: 'Biyoloji', donem: '2. Dönem', unite: '2. Ünite: ORGANİZASYON / 2.3. Hücrenin Organizasyonu', konu: 'Sitoplazmik Yapılar' },
  { sinif: '9. Sınıf', ders: 'Biyoloji', donem: '2. Dönem', unite: '2. Ünite: ORGANİZASYON / 2.3. Hücrenin Organizasyonu', konu: 'Organeller ve Çekirdek' },
  { sinif: '9. Sınıf', ders: 'Biyoloji', donem: '2. Dönem', unite: '2. Ünite: ORGANİZASYON / 2.3. Hücrenin Organizasyonu', konu: 'Hücre Zarından Madde Geçişleri' },
  { sinif: '9. Sınıf', ders: 'Biyoloji', donem: '2. Dönem', unite: '2. Ünite: ORGANİZASYON / 2.3. Hücrenin Organizasyonu', konu: 'Hücreden Doku' },
  { sinif: '9. Sınıf', ders: 'Biyoloji', donem: '2. Dönem', unite: '2. Ünite: ORGANİZASYON / 2.3. Hücrenin Organizasyonu', konu: 'Organ ve Sistemlerin Organizasyonu' },
].map((item, index) => ({ ...item, id: `9-biyoloji-${index + 1}`, sira: index + 1, aktif: true }));

const INITIAL_KIMYA_9_TOPICS = [
  { sinif: '9. Sınıf', ders: 'Kimya', donem: '1. Dönem', unite: '1. Ünite: ETKİLEŞİM / 1.1. Kimya Hayattır', konu: 'Günlük Hayatta Kimya' },
  { sinif: '9. Sınıf', ders: 'Kimya', donem: '1. Dönem', unite: '1. Ünite: ETKİLEŞİM / 1.1. Kimya Hayattır', konu: 'Kimyasal Maddelerin Kullanımı ve Güvenlik' },
  { sinif: '9. Sınıf', ders: 'Kimya', donem: '1. Dönem', unite: '1. Ünite: ETKİLEŞİM / 1.1. Kimya Hayattır', konu: 'Kimyanın Alt Disiplinleri' },
  { sinif: '9. Sınıf', ders: 'Kimya', donem: '1. Dönem', unite: '1. Ünite: ETKİLEŞİM / 1.1. Kimya Hayattır', konu: 'Kimya Alanında Kariyer Olanakları' },
  { sinif: '9. Sınıf', ders: 'Kimya', donem: '1. Dönem', unite: '1. Ünite: ETKİLEŞİM / 1.2. Atomdan Periyodik Tabloya', konu: 'Atom Teorileri ve Atomun Yapısı' },
  { sinif: '9. Sınıf', ders: 'Kimya', donem: '1. Dönem', unite: '1. Ünite: ETKİLEŞİM / 1.2. Atomdan Periyodik Tabloya', konu: 'Atom Orbitalleri ve Elektron Dizilimi' },
  { sinif: '9. Sınıf', ders: 'Kimya', donem: '1. Dönem', unite: '1. Ünite: ETKİLEŞİM / 1.2. Atomdan Periyodik Tabloya', konu: 'Periyodik Tabloda Yer Bulma' },
  { sinif: '9. Sınıf', ders: 'Kimya', donem: '1. Dönem', unite: '1. Ünite: ETKİLEŞİM / 1.2. Atomdan Periyodik Tabloya', konu: 'Periyodik Özellikler' },
  { sinif: '9. Sınıf', ders: 'Kimya', donem: '1. Dönem', unite: '2. Ünite: ÇEŞİTLİLİK / 2.1. Etkileşimler', konu: 'Metalik Bağ' },
  { sinif: '9. Sınıf', ders: 'Kimya', donem: '1. Dönem', unite: '2. Ünite: ÇEŞİTLİLİK / 2.1. Etkileşimler', konu: 'İyonik Bağ' },
  { sinif: '9. Sınıf', ders: 'Kimya', donem: '1. Dönem', unite: '2. Ünite: ÇEŞİTLİLİK / 2.1. Etkileşimler', konu: 'Kovalent Bağ' },
  { sinif: '9. Sınıf', ders: 'Kimya', donem: '1. Dönem', unite: '2. Ünite: ÇEŞİTLİLİK / 2.1. Etkileşimler', konu: 'Lewis Nokta Yapısı' },
  { sinif: '9. Sınıf', ders: 'Kimya', donem: '1. Dönem', unite: '2. Ünite: ÇEŞİTLİLİK / 2.1. Etkileşimler', konu: 'Molekül Polarlığı ve Apolarlığı' },
  { sinif: '9. Sınıf', ders: 'Kimya', donem: '1. Dönem', unite: '2. Ünite: ÇEŞİTLİLİK / 2.1. Etkileşimler', konu: 'Bileşiklerin Adlandırılması' },
  { sinif: '9. Sınıf', ders: 'Kimya', donem: '1. Dönem', unite: '2. Ünite: ÇEŞİTLİLİK / 2.2. Etkileşimden Maddeye', konu: 'Moleküller Arası Etkileşimler' },
  { sinif: '9. Sınıf', ders: 'Kimya', donem: '1. Dönem', unite: '2. Ünite: ÇEŞİTLİLİK / 2.2. Etkileşimden Maddeye', konu: 'Katılar ve Özellikleri' },
  { sinif: '9. Sınıf', ders: 'Kimya', donem: '1. Dönem', unite: '2. Ünite: ÇEŞİTLİLİK / 2.2. Etkileşimden Maddeye', konu: 'Sıvılar ve Özellikleri' },
  { sinif: '9. Sınıf', ders: 'Kimya', donem: '2. Dönem', unite: '3. Ünite: SÜRDÜRÜLEBİLİRLİK', konu: 'Nanoparçacıklar ve Ekolojik Sürdürülebilirlik' },
  { sinif: '9. Sınıf', ders: 'Kimya', donem: '2. Dönem', unite: '3. Ünite: SÜRDÜRÜLEBİLİRLİK', konu: 'Metal Nanoparçacıklar' },
  { sinif: '9. Sınıf', ders: 'Kimya', donem: '2. Dönem', unite: '3. Ünite: SÜRDÜRÜLEBİLİRLİK', konu: 'Metal, Alaşım ve Metal Nanoparçacıkların Çevresel Etkileri' },
  { sinif: '9. Sınıf', ders: 'Kimya', donem: '2. Dönem', unite: '3. Ünite: SÜRDÜRÜLEBİLİRLİK', konu: 'Yeşil Kimyanın Atık Önleme İlkesi' },
].map((item, index) => ({ ...item, id: `9-kimya-${index + 1}`, sira: index + 1, aktif: true }));

const INITIAL_FIZIK_9_TOPICS = [
  { sinif: '9. Sınıf', ders: 'Fizik', donem: '1. Dönem', unite: '1. Ünite: FİZİK BİLİMİ ve KARİYER KEŞFİ', konu: 'Fizik Bilimi' },
  { sinif: '9. Sınıf', ders: 'Fizik', donem: '1. Dönem', unite: '1. Ünite: FİZİK BİLİMİ ve KARİYER KEŞFİ', konu: 'Fizik Biliminin Alt Dalları' },
  { sinif: '9. Sınıf', ders: 'Fizik', donem: '1. Dönem', unite: '1. Ünite: FİZİK BİLİMİ ve KARİYER KEŞFİ', konu: 'Fizik Bilimine Yön Verenler' },
  { sinif: '9. Sınıf', ders: 'Fizik', donem: '1. Dönem', unite: '1. Ünite: FİZİK BİLİMİ ve KARİYER KEŞFİ', konu: 'Fizik Bilimi İle İlgili Kariyer Keşfi' },
  { sinif: '9. Sınıf', ders: 'Fizik', donem: '1. Dönem', unite: '2. Ünite: KUVVET ve HAREKET', konu: 'Temel ve Türetilmiş Nicelikler' },
  { sinif: '9. Sınıf', ders: 'Fizik', donem: '1. Dönem', unite: '2. Ünite: KUVVET ve HAREKET', konu: 'Skaler ve Vektörel Nicelikler' },
  { sinif: '9. Sınıf', ders: 'Fizik', donem: '1. Dönem', unite: '2. Ünite: KUVVET ve HAREKET', konu: 'Vektörler' },
  { sinif: '9. Sınıf', ders: 'Fizik', donem: '1. Dönem', unite: '2. Ünite: KUVVET ve HAREKET', konu: 'Doğadaki Temel Kuvvetler' },
  { sinif: '9. Sınıf', ders: 'Fizik', donem: '1. Dönem', unite: '2. Ünite: KUVVET ve HAREKET', konu: 'Hareket ve Hareket Türleri' },
  { sinif: '9. Sınıf', ders: 'Fizik', donem: '2. Dönem', unite: '3. Ünite: AKIŞKANLAR', konu: 'Basınç' },
  { sinif: '9. Sınıf', ders: 'Fizik', donem: '2. Dönem', unite: '3. Ünite: AKIŞKANLAR', konu: 'Sıvılarda Basınç' },
  { sinif: '9. Sınıf', ders: 'Fizik', donem: '2. Dönem', unite: '3. Ünite: AKIŞKANLAR', konu: 'Açık Hava Basıncı' },
  { sinif: '9. Sınıf', ders: 'Fizik', donem: '2. Dönem', unite: '3. Ünite: AKIŞKANLAR', konu: 'Kaldırma Kuvveti' },
  { sinif: '9. Sınıf', ders: 'Fizik', donem: '2. Dönem', unite: '3. Ünite: AKIŞKANLAR', konu: 'Bernoulli İlkesi' },
  { sinif: '9. Sınıf', ders: 'Fizik', donem: '2. Dönem', unite: '4. Ünite: ENERJİ', konu: 'İç Enerji, Isı ve Sıcaklık Arasındaki İlişki' },
  { sinif: '9. Sınıf', ders: 'Fizik', donem: '2. Dönem', unite: '4. Ünite: ENERJİ', konu: 'Isı, Öz Isı, Isı Sığası ve Sıcaklık Farkı Arasındaki İlişki' },
  { sinif: '9. Sınıf', ders: 'Fizik', donem: '2. Dönem', unite: '4. Ünite: ENERJİ', konu: 'Hâl Değişimi' },
  { sinif: '9. Sınıf', ders: 'Fizik', donem: '2. Dönem', unite: '4. Ünite: ENERJİ', konu: 'Isıl Denge' },
  { sinif: '9. Sınıf', ders: 'Fizik', donem: '2. Dönem', unite: '4. Ünite: ENERJİ', konu: 'Isı Aktarım Yolları' },
  { sinif: '9. Sınıf', ders: 'Fizik', donem: '2. Dönem', unite: '4. Ünite: ENERJİ', konu: 'Isı İletim Hızı' },
].map((item, index) => ({ ...item, id: `9-fizik-${index + 1}`, sira: index + 1, aktif: true }));

const INITIAL_EDEBIYAT_9_TOPICS = [
  { sinif: '9. Sınıf', ders: 'Edebiyat', donem: '1. Dönem', unite: '1. ÜNİTE: GİRİŞ', konu: 'Edebiyat Nedir?' },
  { sinif: '9. Sınıf', ders: 'Edebiyat', donem: '1. Dönem', unite: '1. ÜNİTE: GİRİŞ', konu: 'Edebiyatın Güzel Sanatlarla İlişkisi' },
  { sinif: '9. Sınıf', ders: 'Edebiyat', donem: '1. Dönem', unite: '1. ÜNİTE: GİRİŞ', konu: 'Edebiyatın Diğer Bilimlerle İlişkisi' },
  { sinif: '9. Sınıf', ders: 'Edebiyat', donem: '1. Dönem', unite: '1. ÜNİTE: GİRİŞ', konu: 'Metinlerin Sınıflandırılması' },
  { sinif: '9. Sınıf', ders: 'Edebiyat', donem: '1. Dönem', unite: '1. ÜNİTE: GİRİŞ', konu: 'İletişim Nedir?' },
  { sinif: '9. Sınıf', ders: 'Edebiyat', donem: '1. Dönem', unite: '1. ÜNİTE: GİRİŞ', konu: 'İletişim Öğeleri' },
  { sinif: '9. Sınıf', ders: 'Edebiyat', donem: '1. Dönem', unite: '1. ÜNİTE: GİRİŞ', konu: 'Gösterge Çeşitleri' },
  { sinif: '9. Sınıf', ders: 'Edebiyat', donem: '1. Dönem', unite: '1. ÜNİTE: GİRİŞ', konu: 'Düşünceyi Geliştirme Yolları' },
  { sinif: '9. Sınıf', ders: 'Edebiyat', donem: '1. Dönem', unite: '1. ÜNİTE: GİRİŞ', konu: 'Dil Nedir?' },
  { sinif: '9. Sınıf', ders: 'Edebiyat', donem: '1. Dönem', unite: '1. ÜNİTE: GİRİŞ', konu: 'Dilin İşlevleri' },
  { sinif: '9. Sınıf', ders: 'Edebiyat', donem: '1. Dönem', unite: '1. ÜNİTE: GİRİŞ', konu: 'Dil-Kültür İlişkisi' },
  { sinif: '9. Sınıf', ders: 'Edebiyat', donem: '1. Dönem', unite: '1. ÜNİTE: GİRİŞ', konu: 'Dilin Kullanımından Doğan Türler' },
  { sinif: '9. Sınıf', ders: 'Edebiyat', donem: '1. Dönem', unite: '2. ÜNİTE: HİKAYE (ÖYKÜ)', konu: 'Hikaye Nedir?' },
  { sinif: '9. Sınıf', ders: 'Edebiyat', donem: '1. Dönem', unite: '2. ÜNİTE: HİKAYE (ÖYKÜ)', konu: 'Hikaye Türleri' },
  { sinif: '9. Sınıf', ders: 'Edebiyat', donem: '1. Dönem', unite: '2. ÜNİTE: HİKAYE (ÖYKÜ)', konu: 'Hikayenin Tarihsel Gelişimi' },
  { sinif: '9. Sınıf', ders: 'Edebiyat', donem: '1. Dönem', unite: '2. ÜNİTE: HİKAYE (ÖYKÜ)', konu: 'Yazım Kuralları' },
  { sinif: '9. Sınıf', ders: 'Edebiyat', donem: '1. Dönem', unite: '2. ÜNİTE: HİKAYE (ÖYKÜ)', konu: 'Noktalama İşaretleri' },
  { sinif: '9. Sınıf', ders: 'Edebiyat', donem: '1. Dönem', unite: '2. ÜNİTE: HİKAYE (ÖYKÜ)', konu: 'İsimler' },
  { sinif: '9. Sınıf', ders: 'Edebiyat', donem: '1. Dönem', unite: '3. ÜNİTE: ŞİİR', konu: 'Şiir Nedir?' },
  { sinif: '9. Sınıf', ders: 'Edebiyat', donem: '1. Dönem', unite: '3. ÜNİTE: ŞİİR', konu: 'Şiir Bilgisi' },
  { sinif: '9. Sınıf', ders: 'Edebiyat', donem: '1. Dönem', unite: '3. ÜNİTE: ŞİİR', konu: 'Şiir Türleri' },
  { sinif: '9. Sınıf', ders: 'Edebiyat', donem: '1. Dönem', unite: '3. ÜNİTE: ŞİİR', konu: 'Sıfatlar' },
  { sinif: '9. Sınıf', ders: 'Edebiyat', donem: '1. Dönem', unite: '4. ÜNİTE: MASAL / FABL', konu: 'Masal Nedir?' },
  { sinif: '9. Sınıf', ders: 'Edebiyat', donem: '1. Dönem', unite: '4. ÜNİTE: MASAL / FABL', konu: 'Fabl Nedir?' },
  { sinif: '9. Sınıf', ders: 'Edebiyat', donem: '1. Dönem', unite: '4. ÜNİTE: MASAL / FABL', konu: 'Dinleme Türleri ve Etkili Dinlemenin İlkeleri' },
  { sinif: '9. Sınıf', ders: 'Edebiyat', donem: '1. Dönem', unite: '4. ÜNİTE: MASAL / FABL', konu: 'Edatlar' },
  { sinif: '9. Sınıf', ders: 'Edebiyat', donem: '1. Dönem', unite: '4. ÜNİTE: MASAL / FABL', konu: 'Bağlaçlar' },
  { sinif: '9. Sınıf', ders: 'Edebiyat', donem: '1. Dönem', unite: '4. ÜNİTE: MASAL / FABL', konu: 'Ünlemler' },
  { sinif: '9. Sınıf', ders: 'Edebiyat', donem: '1. Dönem', unite: '5. ÜNİTE: ROMAN', konu: 'Roman Nedir?' },
  { sinif: '9. Sınıf', ders: 'Edebiyat', donem: '1. Dönem', unite: '5. ÜNİTE: ROMAN', konu: 'Roman Türleri' },
  { sinif: '9. Sınıf', ders: 'Edebiyat', donem: '1. Dönem', unite: '5. ÜNİTE: ROMAN', konu: 'Romanda Bakış Açıları' },
  { sinif: '9. Sınıf', ders: 'Edebiyat', donem: '1. Dönem', unite: '5. ÜNİTE: ROMAN', konu: 'Romanın Yapı Unsurları' },
  { sinif: '9. Sınıf', ders: 'Edebiyat', donem: '1. Dönem', unite: '5. ÜNİTE: ROMAN', konu: 'Zamirler' },
  { sinif: '9. Sınıf', ders: 'Edebiyat', donem: '2. Dönem', unite: '6. ÜNİTE: TİYATRO', konu: 'Tiyatro Nedir?' },
  { sinif: '9. Sınıf', ders: 'Edebiyat', donem: '2. Dönem', unite: '6. ÜNİTE: TİYATRO', konu: 'Tiyatronun Yapı Unsurları' },
  { sinif: '9. Sınıf', ders: 'Edebiyat', donem: '2. Dönem', unite: '6. ÜNİTE: TİYATRO', konu: 'Tiyatro Terimleri' },
  { sinif: '9. Sınıf', ders: 'Edebiyat', donem: '2. Dönem', unite: '6. ÜNİTE: TİYATRO', konu: 'Geleneksel Türk Tiyatrosu' },
  { sinif: '9. Sınıf', ders: 'Edebiyat', donem: '2. Dönem', unite: '6. ÜNİTE: TİYATRO', konu: 'Zarflar' },
  { sinif: '9. Sınıf', ders: 'Edebiyat', donem: '2. Dönem', unite: '7. ÜNİTE: BİYOGRAFİ / OTOBİYOGRAFİ', konu: 'Biyografi' },
  { sinif: '9. Sınıf', ders: 'Edebiyat', donem: '2. Dönem', unite: '7. ÜNİTE: BİYOGRAFİ / OTOBİYOGRAFİ', konu: 'Otobiyografi' },
  { sinif: '9. Sınıf', ders: 'Edebiyat', donem: '2. Dönem', unite: '7. ÜNİTE: BİYOGRAFİ / OTOBİYOGRAFİ', konu: 'CV / Öz Geçmiş' },
  { sinif: '9. Sınıf', ders: 'Edebiyat', donem: '2. Dönem', unite: '7. ÜNİTE: BİYOGRAFİ / OTOBİYOGRAFİ', konu: 'Dilekçe Nedir?' },
  { sinif: '9. Sınıf', ders: 'Edebiyat', donem: '2. Dönem', unite: '7. ÜNİTE: BİYOGRAFİ / OTOBİYOGRAFİ', konu: 'Tutanak Nedir?' },
  { sinif: '9. Sınıf', ders: 'Edebiyat', donem: '2. Dönem', unite: '7. ÜNİTE: BİYOGRAFİ / OTOBİYOGRAFİ', konu: 'Fiil' },
  { sinif: '9. Sınıf', ders: 'Edebiyat', donem: '2. Dönem', unite: '8. ÜNİTE: MEKTUP / E-POSTA', konu: 'Mektup Nedir?' },
  { sinif: '9. Sınıf', ders: 'Edebiyat', donem: '2. Dönem', unite: '8. ÜNİTE: MEKTUP / E-POSTA', konu: 'Mektup Türleri' },
  { sinif: '9. Sınıf', ders: 'Edebiyat', donem: '2. Dönem', unite: '8. ÜNİTE: MEKTUP / E-POSTA', konu: 'E-Posta Nedir?' },
  { sinif: '9. Sınıf', ders: 'Edebiyat', donem: '2. Dönem', unite: '9. ÜNİTE: GÜNLÜK / BLOG', konu: 'Günlük Nedir?' },
  { sinif: '9. Sınıf', ders: 'Edebiyat', donem: '2. Dönem', unite: '9. ÜNİTE: GÜNLÜK / BLOG', konu: 'Blog Nedir?' },
].map((item, index) => ({ ...item, id: `9-edebiyat-${index + 1}`, sira: index + 1, aktif: true }));

const INITIAL_10_TOPICS = [
  ...makeTopics('10. Sınıf', 'Edebiyat', '1. Dönem', '1. Ünite: Giriş', [
    'Edebiyat-Tarih İlişkisi',
    'Edebiyat-Din İlişkisi',
    'Türklerin Kullandığı Alfabeler',
    'Yazım Kuralları',
    'Noktalama İşaretleri',
  ], '10-edebiyat-giris'),
  ...makeTopics('10. Sınıf', 'Edebiyat', '1. Dönem', '2. Ünite: Hikaye', [
    'Halk Hikayesi',
    'Dede Korkut Hikayeleri',
    'Mesnevi',
    'Hikaye Nedir?',
    'Hikaye Türleri',
    'Fiilimsiler',
  ], '10-edebiyat-hikaye'),
  ...makeTopics('10. Sınıf', 'Edebiyat', '1. Dönem', '3. Ünite: Şiir', [
    'İslamiyetin Kabulünden Önceki Türk Edebiyatı',
    'Koşuk Nedir?',
    'Sagu Nedir?',
    'Sav Nedir?',
    'Geçiş Dönemi Eserleri ve Özellikleri',
    'İlahi Nedir?',
    'Nefes Nedir?',
    'Koşma Nedir?',
    'Mani',
    'Türkü',
    'Gazel',
    'Kaside',
    'Şarkı',
  ], '10-edebiyat-siir'),
  ...makeTopics('10. Sınıf', 'Edebiyat', '1. Dönem', '4. Ünite: Destan / Efsane', [
    'Destan Nedir?',
    'Destan Türleri',
    'Destan Örnekleri',
    'Efsane Nedir?',
    'Efsane Örnekleri',
    'Dünya Destanları',
    'İslamiyet’in Kabulünden Önceki Türk Edebiyatında Destan',
    'İslamiyet’in Kabulünden Sonraki Dönem Destanları',
    '1923-1980 Dönemi Türk Yapma Destanları',
    'Sıfat Tamlamaları',
  ], '10-edebiyat-destan'),
  ...makeTopics('10. Sınıf', 'Edebiyat', '2. Dönem', '5. Ünite: Roman', [
    'Roman Nedir?',
    'Dünya Edebiyatında Roman',
    'Tanzimat Dönemi Türk Edebiyatında Roman',
    'Serveti Fünun Döneminde Roman',
    'Milli Edebiyat Döneminde Roman',
    'Cümle Çeşitleri',
  ], '10-edebiyat-roman'),
  ...makeTopics('10. Sınıf', 'Edebiyat', '2. Dönem', '6. Ünite: Tiyatro', [
    'Tiyatro Nedir?',
    'Geleneksel Türk Tiyatrosu',
    'Modern Türk Tiyatrosu',
  ], '10-edebiyat-tiyatro'),
  ...makeTopics('10. Sınıf', 'Edebiyat', '2. Dönem', '7. Ünite: Anı', [
    'Anı Nedir?',
    'Serveti Fünun Dönemi Türk Edebiyatında Anı',
    'Cumhuriyet Dönemi Türk Edebiyatında Anı',
  ], '10-edebiyat-ani'),
  ...makeTopics('10. Sınıf', 'Edebiyat', '2. Dönem', '8. Ünite: Haber Metni', [
    'Haber Yazısı Nedir?',
    'Güncel Gazete ve Dergi Haberleri',
    'Genel Ağ Haberleri',
  ], '10-edebiyat-haber'),
  ...makeTopics('10. Sınıf', 'Edebiyat', '2. Dönem', '9. Ünite: Gezi Yazısı', [
    'Gezi Yazısı Nedir?',
    'Divan Edebiyatında Gezi Yazısı',
    'Cumhuriyet Edebiyatında Gezi Yazısı',
  ], '10-edebiyat-gezi'),
  ...makeTopics('10. Sınıf', 'Matematik', '1. Dönem', '10. Sınıf Matematik', [
    'Sayma ve Olasılık',
    'Fonksiyonlar',
    'Polinomlar',
    'İkinci Dereceden Denklemler',
    'Dörtgenler ve Çokgenler',
  ], '10-matematik-1'),
  ...makeTopics('10. Sınıf', 'Matematik', '2. Dönem', '10. Sınıf Matematik', [
    'Dörtgenler ve Çokgenler',
    'Katı Cisimler',
  ], '10-matematik-2'),
  ...makeTopics('10. Sınıf', 'Fizik', '1. Dönem', '1. Ünite: Elektrik ve Manyetizma', [
    'Elektrik Akımı',
    'Potansiyel Fark',
    'Direnç',
    'Ohm Yasası',
    'Eşdeğer Direnç',
    'İç Direnç',
    'Elektromotor Kuvvet',
    'Elektrik Enerjisi',
    'Elektriksel Güç',
    'Manyetizma',
  ], '10-fizik-elektrik'),
  ...makeTopics('10. Sınıf', 'Fizik', '1. Dönem', '2. Ünite: Basınç ve Kaldırma Kuvveti', [
    'Katı Basıncı',
    'Akışkan Basıncı',
    'Basınç Kuvveti',
    'Pascal Prensibi',
    'Bernoulli İlkesi',
    'Archimedes İlkesi',
    'Kaldırma Kuvveti',
  ], '10-fizik-basinc'),
  ...makeTopics('10. Sınıf', 'Fizik', '2. Dönem', '3. Ünite: Dalgalar', [
    'Dalga, Titreşim',
    'Dalga Hareketi',
    'Dalga Boyu',
    'Periyot, Frekans',
    'Hız',
    'Genlik',
    'Atma',
    'Dalga Tepesi',
    'Dalga Çukuru',
    'Odak Noktası',
    'Merkez',
    'Stroboskop',
    'Rezonans',
    'Ses Yüksekliği',
    'Ses Şiddeti',
    'Tını, Yankı, Uğultu',
    'Gürültü, Ses kirliliği',
    'Deprem Dalgası',
  ], '10-fizik-dalgalar'),
  ...makeTopics('10. Sınıf', 'Fizik', '2. Dönem', '4. Ünite: Optik', [
    'Aydınlanma Şiddeti',
    'Işık Şiddeti',
    'Işık Akısı',
    'Gölge',
    'Yarı Gölge',
    'Yansıma',
    'Odak Noktası, Merkez',
    'Tepe Noktası, Asal Eksen',
    'Kırılma, Kırıcılık İndisi',
    'Snell Yasası',
    'Tam Yansıma, Sınır Açısı',
    'Görünür Uzaklık',
  ], '10-fizik-optik'),
  ...makeTopics('10. Sınıf', 'Kimya', '1. Dönem', '10. Sınıf Kimya', [
    'Kimyanın Temel Kanunları',
    'Mol Kavramı',
    'Kimyasal Tepkimeler ve Denklemler',
    'Kimyasal Tepkimelerde Hesaplamalar',
    'Homojen ve Heterojen Karışımlar',
    'Ayrıştırma ve Saflaştırma Teknikleri',
  ], '10-kimya-1'),
  ...makeTopics('10. Sınıf', 'Kimya', '2. Dönem', '10. Sınıf Kimya', [
    'Homojen ve Heterojen Karışımlar',
    'Ayrıştırma ve Saflaştırma Teknikleri',
    'Asitler',
    'Asitler ve Bazların Tepkimeleri',
    'Hayatımızda Asitler ve Bazlar',
    'Tuzlar',
    'Yaygın Günlük Hayat Kimyasalları',
    'Gıdalar',
  ], '10-kimya-2'),
  ...makeTopics('10. Sınıf', 'Biyoloji', '1. Dönem', '10. Sınıf Biyoloji', [
    'Mitoz ve Eşeysiz Üreme',
    'Mayoz ve Eşeyli Üreme',
    'Kalıtım ve Biyolojik Çeşitlilik',
  ], '10-biyoloji-1'),
  ...makeTopics('10. Sınıf', 'Biyoloji', '2. Dönem', '10. Sınıf Biyoloji', [
    'Kalıtım ve Biyolojik Çeşitlilik',
    'Ekosistem Ekolojisi',
    'Güncel Çevre Sorunları ve İnsan',
    'Doğal Kaynaklar',
    'Biyolojik Çeşitliliğin Korunması',
  ], '10-biyoloji-2'),
  ...makeTopics('10. Sınıf', 'Tarih', '1. Dönem', '10. Sınıf Tarih', [
    'Yerleşme ve Devletleşme Sürecinde Selçuklu Devleti',
    'Beylikten Devlete Osmanlı Siyaseti (1302-1453)',
    'Devletleşme Sürecinde Savaşçılar ve Askerler',
    'Beylikten Devlete Osmanlı Medeniyeti',
  ], '10-tarih-1'),
  ...makeTopics('10. Sınıf', 'Tarih', '2. Dönem', '10. Sınıf Tarih', [
    'Dünya Gücü Osmanlı Devleti (1453-1595)',
    'Sultan ve Osmanlı Merkez Teşkilatı',
    'Klasik Çağda Osmanlı Toplum Düzeni',
  ], '10-tarih-2'),
  ...makeTopics('10. Sınıf', 'Coğrafya', '1. Dönem', '10. Sınıf Coğrafya', [
    'Dünya’nın Tektonik Oluşumu',
    'Jeolojik Zamanlar',
    'Türkiye’nin Tektonizması',
    'İç Kuvvetler',
    'Kayaçlar ve Yeryüzü Şekilleri',
    'İç Kuvvetlerin Türkiye’deki Yeryüzü Şekillerinin Oluşum Sürecine Etkisi',
    'Dış Kuvvetler ve Yer Şekilleri',
    'Türkiye’de Dış Kuvvetler ve Yer Şekilleri',
    'Türkiye’nin Dağları, Ovaları ve Platoları',
    'Yeryüzündeki Su Kaynakları',
    'Okyanuslar',
    'Denizler',
    'Göller',
    'Akarsular',
    'Yeraltı Suları ve Kaynakları',
    'Türkiye’nin Su Kaynakları',
    'Yeryüzündeki Toprak Çeşitliliği',
    'Toprağın Oluşum Süreci',
  ], '10-cografya-1'),
  ...makeTopics('10. Sınıf', 'Coğrafya', '2. Dönem', '10. Sınıf Coğrafya', [
    'Türkiye’deki Toprakların Dağılışı',
    'Türkiye Topraklarının Kullanımı',
    'Bitki Topluluklarının Sınıflandırılması ve Dağılışı',
    'Türkiye’deki Bitki Toplulukları ve Dağılışı',
    'Nüfus Özellikleri',
    'Dünya Nüfusunun Tarihsel Değişimi',
    'Nüfus Artış Hızı İle Ülkelerin Kalkınması Arasındaki İlişki',
    'Nüfusun Dağılışı, Nüfus Yoğunluğu ve Nüfus Piramitleri',
    'Türkiye Nüfusunun Dağılışı ve Yapısal Özellikleri',
    'Dünyada ve Türkiye’de Göçler',
    'Ekonomik Faaliyetler',
    'Uluslararası Ulaşım Hatlarının Küresel ve Bölgesel Etkileri',
    'Afetlerin Oluşum Nedenleri, Dağılışı ve Etkileri',
  ], '10-cografya-2'),
];

const INITIAL_11_TOPICS = [
  ...makeTopics('11. Sınıf', 'Edebiyat', '1. Dönem', '1. Ünite: Giriş', [
    'Edebiyat-Toplum İlişkisi',
    'Edebiyatın Sanat Akımları ile İlişkisi',
    'Yazım Kuralları',
    'Noktalama İşaretleri',
  ], '11-edebiyat-giris'),
  ...makeTopics('11. Sınıf', 'Edebiyat', '1. Dönem', '2. Ünite: Hikaye', [
    'Hikaye Nedir?',
    'Cumhuriyet Dönemi’nde (1923-1940 arası) Hikaye',
    'Cümlenin Öğeleri',
  ], '11-edebiyat-hikaye'),
  ...makeTopics('11. Sınıf', 'Edebiyat', '1. Dönem', '3. Ünite: Şiir', [
    'Şiir Nedir?',
    'Tanzimat Dönemi Şiir',
    'Servetifünun Şiir',
    'Saf Şiir Anlayışına Bağlı Şiir',
    'Milli Edebiyat Döneminde Şiir',
    'Türk Dünyası Edebiyatında Şiir',
  ], '11-edebiyat-siir'),
  ...makeTopics('11. Sınıf', 'Edebiyat', '1. Dönem', '4. Ünite: Makale', [
    'Makale Nedir?',
    'Bilimsel Makale',
    'Edebi Makale',
  ], '11-edebiyat-makale'),
  ...makeTopics('11. Sınıf', 'Edebiyat', '2. Dönem', '5. Ünite: Sohbet ve Fıkra', [
    'Sohbet (Söyleşi) Nedir?',
    'Fıkra (Köşe Yazısı) Nedir?',
    'Cumhuriyet Öncesi Dönemde ve Cumhuriyet Döneminde Sohbet',
    'Cumhuriyet Öncesi Dönemde ve Cumhuriyet Döneminde Fıkra',
  ], '11-edebiyat-sohbet'),
  ...makeTopics('11. Sınıf', 'Edebiyat', '2. Dönem', '6. Ünite: Roman', [
    'Roman Nedir?',
    'Cumhuriyet Dönemi Romanı',
    'Dünya Edebiyatında Roman',
    'Anlatım Bozukluğu',
  ], '11-edebiyat-roman'),
  ...makeTopics('11. Sınıf', 'Edebiyat', '2. Dönem', '7. Ünite: Tiyatro', [
    'Tiyatro Nedir?',
    'Cumhuriyet Dönemi’nde Tiyatro',
    'Dünya Edebiyatında Tiyatro',
  ], '11-edebiyat-tiyatro'),
  ...makeTopics('11. Sınıf', 'Edebiyat', '2. Dönem', '8. Ünite: Eleştiri', [
    'Eleştiri Nedir?',
    'Cumhuriyet Döneminde Eleştiri',
    'Cumhuriyet Öncesi Dönem’de Eleştiri',
  ], '11-edebiyat-elestiri'),
  ...makeTopics('11. Sınıf', 'Edebiyat', '2. Dönem', '9. Ünite: Mülakat/Röportaj', [
    'Mülakat Nedir?',
    'Röportaj Nedir?',
    'Cumhuriyet Öncesi Dönem’de Mülakat',
    'Cumhuriyet Dönemi’nde Röportaj',
  ], '11-edebiyat-mulakat'),
  ...makeTopics('11. Sınıf', 'Matematik', '1. Dönem', '1. Ünite: Trigonometri', [
    'Yönlü Açılar',
    'Trigonometrik Fonksiyonlar',
  ], '11-matematik-trigonometri'),
  ...makeTopics('11. Sınıf', 'Matematik', '1. Dönem', '2. Ünite: Analitik Geometri', [
    'Doğrunun Analitik İncelemesi',
    'Modüler Aritmetik ve İşlemler',
  ], '11-matematik-analitik'),
  ...makeTopics('11. Sınıf', 'Matematik', '1. Dönem', '3. Ünite: Fonksiyonlarda Uygulamalar', [
    'Fonksiyonlarla İlgili Uygulamalar',
    'İkinci Dereceden Fonksiyonlar ve Grafikleri',
    'Fonksiyonların Dönüşümleri',
  ], '11-matematik-fonksiyon'),
  ...makeTopics('11. Sınıf', 'Matematik', '2. Dönem', '4. Ünite: Denklem ve Eşitsizlik Sistemleri', [
    'İkinci Dereceden İki Bilinmeyenli Denklem Sistemleri',
    'İkinci Dereceden Bir Bilinmeyenli Eşitsizlikler ve Eşitsizlik Sistemleri',
  ], '11-matematik-denklem'),
  ...makeTopics('11. Sınıf', 'Matematik', '2. Dönem', '5. Ünite: Çember ve Daire', [
    'Çemberin Temel Elemanları',
    'Çemberde Açılar',
    'Çemberde Teğet',
    'Dairenin Çevresi ve Alanı',
  ], '11-matematik-cember'),
  ...makeTopics('11. Sınıf', 'Matematik', '2. Dönem', '6. Ünite: Uzay Geometri', [
    'Katı Cisimler',
  ], '11-matematik-uzay'),
  ...makeTopics('11. Sınıf', 'Matematik', '2. Dönem', '7. Ünite: Olasılık', [
    'Olasılık',
  ], '11-matematik-olasilik'),
  ...makeTopics('11. Sınıf', 'Fizik', '1. Dönem', '1. Ünite: Vektörler', ['Vektörler'], '11-fizik-vektor'),
  ...makeTopics('11. Sınıf', 'Fizik', '1. Dönem', '2. Ünite: Bağıl Hareket', ['Bağıl Hareket'], '11-fizik-bagil'),
  ...makeTopics('11. Sınıf', 'Fizik', '1. Dönem', '3. Ünite: Newton’un Hareket Yasaları', ['Newton’un Hareket Yasaları'], '11-fizik-newton'),
  ...makeTopics('11. Sınıf', 'Fizik', '1. Dönem', '4. Ünite: Bir Boyutta Sabit İvmeli Hareket', [
    'İvmeli Hareket',
    'Serbest Düşme',
    'Limit Hız',
    'Düşey Atış',
    'Yatay Atış',
  ], '11-fizik-ivme'),
  ...makeTopics('11. Sınıf', 'Fizik', '1. Dönem', '5. Ünite: İki Boyutta Sabit İvmeli Hareket', ['Eğik Atış'], '11-fizik-egik'),
  ...makeTopics('11. Sınıf', 'Fizik', '1. Dönem', '6. Ünite: Enerji ve Hareket', ['Hooke Yasası'], '11-fizik-enerji'),
  ...makeTopics('11. Sınıf', 'Fizik', '1. Dönem', '7. Ünite: İtme ve Çizgisel Momentum', [
    'İtme',
    'Çizgisel Momentum',
    'Çizgisel Momentumun Korunumu',
  ], '11-fizik-momentum'),
  ...makeTopics('11. Sınıf', 'Fizik', '1. Dönem', '8. Ünite: Tork ve Denge', [
    'Tork',
    'Denge',
    'Kütle Merkezi',
    'Ağırlık Merkezi',
  ], '11-fizik-tork'),
  ...makeTopics('11. Sınıf', 'Fizik', '1. Dönem', '9. Ünite: Elektrik ve Manyetizma', [
    'Elektriksel kuvvet',
    'Elektrik Alan',
    'Elektriksel Potansiyel Enerji',
    'Elektriksel Potansiyel',
    'Elektriksel Potansiyel Farkı',
  ], '11-fizik-elektrik-1'),
  ...makeTopics('11. Sınıf', 'Fizik', '2. Dönem', '9. Ünite: Elektrik ve Manyetizma', [
    'Sığa (Kapasite)',
    'Manyetik Alan',
    'Manyetik Kuvvet',
    'Manyetik Akı',
    'Elektromotor Kuvveti',
    'Alternatif Akım',
    'İndüktans, Kapasitans, Empedans',
    'Rezonans',
    'İndüksiyon Akımı',
    'Özindüksiyon Akımı',
    'Transformatör',
  ], '11-fizik-elektrik-2'),
  ...makeTopics('11. Sınıf', 'Kimya', '1. Dönem', '1. Ünite: Modern Atom Teorisi', [
    'Atomun Kuantum Modeli',
    'Periyodik Sistem ve Elektron Dizilimleri',
    'Periyodik Özellikler',
    'Elementleri Tanıyalım',
    'Yükseltgenme Basamakları',
  ], '11-kimya-atom'),
  ...makeTopics('11. Sınıf', 'Kimya', '1. Dönem', '2. Ünite: Gazlar', [
    'Gazların Özellikleri ve Gaz Yasaları',
    'İdeal Gaz Yasası',
    'Gazlarda Kinetik Teori',
    'Gaz Karışımları',
    'Gerçek Gazlar',
  ], '11-kimya-gazlar'),
  ...makeTopics('11. Sınıf', 'Kimya', '1. Dönem', '3. Ünite: Sıvı Çözeltiler ve Çözünürlük', [
    'Çözücü ve Çözünen Etkileşimleri',
    'Derişim Bilimleri',
    'Koligatif Özellikler',
    'Çözünürlük',
  ], '11-kimya-cozelti-1'),
  ...makeTopics('11. Sınıf', 'Kimya', '2. Dönem', '3. Ünite: Sıvı Çözeltiler ve Çözünürlük', [
    'Koligatif Özellikler',
    'Çözünürlük',
    'Çözünürlüğe Etki Eden Faktörler',
  ], '11-kimya-cozelti-2'),
  ...makeTopics('11. Sınıf', 'Kimya', '2. Dönem', '4. Ünite: Kimyasal Tepkimelerde Enerji', [
    'Tepkimelerde Isı Değişimi Oluşum Entalpisi',
    'Bağ Enerjileri Tepkime Isılarının Toplanabilirliği',
    'Tepkime Hızları',
    'Tepkime Hızına Etki Eden Faktörler',
  ], '11-kimya-enerji'),
  ...makeTopics('11. Sınıf', 'Kimya', '2. Dönem', '5. Ünite: Kimyasal Denge', [
    'Kimyasal Denge',
    'Dengeye Etki Eden Faktörler',
    'Sulu Çözelti Dengeleri',
  ], '11-kimya-denge'),
  ...makeTopics('11. Sınıf', 'Biyoloji', '1. Dönem', '11. Sınıf Biyoloji', [
    'Denetleyici ve Düzenleyici Sistem',
    'Duyu Organları',
    'Destek ve Hareket Sistemi',
    'Sindirim Sistemi',
    'Dolaşım Sistemi',
  ], '11-biyoloji-1'),
  ...makeTopics('11. Sınıf', 'Biyoloji', '2. Dönem', '11. Sınıf Biyoloji', [
    'Dolaşım Sistemi',
    'Solunum Sistemi',
    'Üriner Sistem',
    'Üreme Sistemi ve Embriyonik Gelişim',
    'Komünite Ekolojisi',
    'Popülasyon Ekolojisi',
  ], '11-biyoloji-2'),
  ...makeTopics('11. Sınıf', 'Tarih', '1. Dönem', '11. Sınıf Tarih', [
    'Değişen Dünya Dengeleri Karşısında Osmanlı Siyaseti',
    'Değişim Çağında Avrupa ve Osmanlı',
    'Uluslararası İlişkilerde Denge Stratejisi',
  ], '11-tarih-1'),
  ...makeTopics('11. Sınıf', 'Tarih', '2. Dönem', '11. Sınıf Tarih', [
    'Uluslararası İlişkilerde Denge Stratejisi',
    'Devrimler Çağında Değişen Devlet-Toplum İlişkileri',
    'Sermaye ve Emek',
    '19. ve 20. Yüzyılda Değişen Gündelik Hayat',
  ], '11-tarih-2'),
  ...makeTopics('11. Sınıf', 'Coğrafya', '1. Dönem', '11. Sınıf Coğrafya', [
    'Biyoçeşitlilik',
    'Madde Döngüleri ve Enerji Akışı',
    'Su Ekosistemleri',
    'Nüfus Politikaları',
    'Türkiye’nin Nüfus Politikaları',
    'Türkiye’nin Nüfus Projeksiyonları',
    'Şehirlerin Fonksiyonel Özellikleri ve Etki Alanları',
    'Türkiye’deki Şehirlerin Fonksiyonel Özellikleri ve Kır Yerleşme Tipler',
    'Doğal ve Beşeri Unsurların Üretim, Dağıtım ve Tüketim Üzerindeki Etkisi',
    'Doğal Kaynaklar',
    'Türkiye’deki Doğal Kaynaklar',
    'Türkiye’de Uygulanan Ekonomi Politikaları',
    'Türkiye Ekonomisinin Sektörel Dağılımı',
    'Türkiye’de Tarım, Hayvancılık ve Ormancılık',
  ], '11-cografya-1'),
  ...makeTopics('11. Sınıf', 'Coğrafya', '2. Dönem', '11. Sınıf Coğrafya', [
    'Türkiye’de Tarım, Hayvancılık ve Ormancılık',
    'Türkiye’de Madenler ve Enerji Kaynakları',
    'Türkiye’de Sanayi',
    'İlk Kültür Merkezleri',
    'Medeniyetlerin Merkezi Türkiye',
    'Kültür Bölgeleri ve Türk Kültürü',
    'Küresel Ticaret ile Hammadde, Üretim ve Pazar İlişkisi',
    'Turizm Faaliyetlerinin Ülkeler Arası Etkileşimde Rolü',
    'Ülkelerin Sanayileşme Süreçleri, Tarım-Ekonomi İlişkisi',
    'Bölgesel ve Küresel Örgütler',
    'Çevre Sorunlarının Oluşum Sebepleri',
    'Doğal Kaynak Kullanımı ve Çevresel Etkileri',
    'Arazi Kullanımına İlişkin Farklı Uygulamaların Çevre Üzerindeki Etkileri',
    'Atıklardan Korunma Yöntemleri',
    'Doğal Kaynakların Sürdürülebilir Kullanımı',
    'Geri Dönüşüm',
  ], '11-cografya-2'),
  ...makeTopics('11. Sınıf', 'Felsefe', '1. Dönem', '1. Ünite: MÖ 6. Yüzyıl - MS 2. Yüzyıl Felsefesi', [
    'Felsefenin Ortaya Çıkışı',
    'MÖ 6. Yüzyıl- MS 2. Yüzyıl Felsefesinin Karakteristik Özellikleri',
    'MÖ 6. Yüzyıl- MS 2. Yüzyıl Filozoflarının Felsefi Görüşleri',
  ], '11-felsefe-1'),
  ...makeTopics('11. Sınıf', 'Felsefe', '1. Dönem', '2. Ünite: MS 2. Yüzyıl - MS 15. Yüzyıl Felsefesi', [
    'MS 2. Yüzyıl - MS 15. Yüzyıl Felsefesini Hazırlayan Düşünce Ortamı',
    'MS 2. Yüzyıl - MS 15. Yüzyıl Felsefesinin Karakteristik Özellikleri',
    'MS 2. Yüzyıl - MS 15. Yüzyıl Filozoflarının Felsefi Görüşleri',
  ], '11-felsefe-2'),
  ...makeTopics('11. Sınıf', 'Felsefe', '2. Dönem', '3. Ünite: 15. Yüzyıl - 17. Yüzyıl Felsefesi', [
    '15. Yüzyıl - 17. Yüzyıl Felsefesi’ni Hazırlayan Düşünce Ortamı',
    '15. Yüzyıl - 17. Yüzyıl Filozoflarının Felsefi Görüşleri',
  ], '11-felsefe-3'),
  ...makeTopics('11. Sınıf', 'Felsefe', '2. Dönem', '4. Ünite: 18. Yüzyıl - 19. Yüzyıl Felsefesi', [
    '15. Yüzyıl-17. Yüzyıl Felsefesinin 18. Yüzyıl -19. Yüzyıl Felsefesi Üzerindeki Etkileri',
    '18. Yüzyıl -19. Yüzyıl Felsefesinin Karakteristik Özellikleri',
    '18. Yüzyıl -19. Yüzyıl Filozoflarının Felsefi Görüşleri',
  ], '11-felsefe-4'),
  ...makeTopics('11. Sınıf', 'Felsefe', '2. Dönem', '20. Yüzyıl Felsefesi', [
    '18. Yüzyıl -19. Yüzyıl Felsefesinin 20. Yüzyıl Felsefi Akımları Üzerindeki Etkileri',
    '20. Yüzyıl Felsefesinin Karakteristik Özellikleri',
    '20. Yüzyıl Filozoflarının Felsefi Görüşleri',
    '20. Yüzyıl Felsefesi Örnek Düşünce ve Argümanlar',
  ], '11-felsefe-20'),
];

const INITIAL_12_TOPICS = parseTopicText(`
Türkçe
Konu
Sözcükte ve Söz Öbeklerinde Anlam
Cümlede Anlam
Anlatım Biçimleri ve Düşünceyi Geliştirme Yolları
Paragrafta Yapı
Paragrafta Ana Düşünce
Paragrafta Yardımcı Düşünce
Paragrafta Konu ve Başlık
Ses Bilgisi
Yazım Kuralları
Noktalama İşaretleri
Sözcükte Yapı
İsim - İsim Tamlaması
Sıfat - Sıfat Tamlaması
Zamir
Zarf
Edat - Bağlaç - Ünlem
Fiilde Kip ve Kişi / Fiilde Yapı / Ek fiil
Fiilde Çatı
Fiilimsi (Eylemsi)
Cümlenin Öğeleri
Cümle Türleri
Anlatım Bozuklukları

Matematik
Konu
Temel Kavramlar
Tek ve Çift Sayılar
Ardışık Tam Sayılar
Faktöriyel
Asal ve Aralarında Asal Sayılar
Sayı Basamakları
Bölme - Bölünebilme
Asal Çarpanlara Ayırma
Ebob - Ekok
Periyodik Problemler
Rasyonel Sayılar
1. Dereceden Bir Bilinmeyenli Denklemler
Basit Eşitsizlikler
Mutlak Değer
Üslü Sayılar
Köklü Sayılar
Çarpanlara Ayırma
Oran - Orantı
Sayı Problemleri
Kesir Problemleri
Yaş Problemleri
İşçi Problemleri
Hız Problemleri
Yüzde Problemleri
Karışım Problemleri
Grafik Problemleri
Veri
Kümeler ve Kartezyen Çarpım
Mantık
Fonksiyonlar
Polinomlar
İkinci Dereceden Denklemler
Karmaşık Sayılar
Permütasyon
Kombinasyon
Binom
Olasılık

Tarih
Konu
Tarih ve Zaman
İnsanlığın İlk Dönemleri
Orta Çağ'da Dünya
İlk ve Orta Çağlarda Türk Dünyası
İslam Medeniyetinin Doğuşu
Türklerin İslamiyet'i Kabulü ve İlk Türk İslam Devletleri
Yerleşme ve Devletleşme Sürecinde Selçuklu Türkiyesi
Beylikten Devlete Osmanlı Siyaseti (1302-1453)
Devletleşme Sürecinde Savaşçılar ve Askerler
Beylikten Devlete Osmanlı Medeniyeti
Dünya Gücü Osmanlı (1453-1595)
Sultan ve Osmanlı Merkez Teşkilatı
Klasik Çağda Osmanlı Toplum Düzeni
Değişen Dünya Dengeleri Karşısında Osmanlı Siyaseti (1595-1774)
Değişim Çağında Avrupa ve Osmanlı
Devrimler Çağında Değişen Devlet-Toplum İlişkileri
Uluslararası İlişkilerde Denge Stratejisi (1774-1914)
XIX. ve XX. Yüzyılda Değişen Sosyo-Ekonomik Hayat
XX. Yüzyıl Başlarında Osmanlı Devleti ve Dünya
Milli Mücadele'ye Hazırlık Dönemi
Milli Mücadele Dönemi
Atatürkçülük ve Türk İnkılabı
İki Savaş Arası Dönemde Türkiye ve Dünya
II. Dünya Savaşı Sürecinde Türkiye ve Dünya
II. Dünya Savaşı Sonrasında Türkiye ve Dünya
Toplumsal Devrim Çağında Dünya ve Türkiye
XXI. Yüzyılın Eşiğinde Türkiye ve Dünya

Coğrafya
Konu
Doğa ve İnsan
Dünyanın Şekli ve Günlük Hareket
Dünyanın Yıllık Hareketi ve Özel Tarihler
Coğrafi Konum
Türkiye'nin Coğrafi Konumu
Harita ve Ölçek
Harita'da Yer Şekillerinin Gösterilmesi
Atmosfer ve Özellikleri
Sıcaklık
Basınç ve Rüzgarlar
Nem ve Yağış
İklim Tipleri
Türkiye'nin İklimi
Yerin Yapısı, Jeolojik Zamanlar ve Kayaçlar
İç Kuvvetler
Dış Kuvvetler
Türkiye'nin Yer Şekilleri
Sular
Topraklar
Bitkiler
Yerleşme
Nüfus
Göçler
Ekonomik Faaliyetler
Bölgeler
Ulaşım Ağları
Doğal Afetler

Felsefe
Konu
Felsefeyi Tanıma
Felsefe ile Düşünme
Varlık Felsefesi
Bilgi Felsefesi
Bilim Felsefesi
Ahlak Felsefesi
Din Felsefesi
Siyaset Felsefesi
M.Ö. 6. Yy. - M.S 2. Yüzyıl Felsefesi
M.S. 2. Yy. - M.S 15. Yüzyıl Felsefesi
15. Yy. - 17 Yy. Felsefesi
18. Yy. - 19. Yy. Felsefesi
20. Yy. Felsefesi

Din Kültürü ve Ahlak Bilgisi
Konu
İslam - İnanç - Bilgi - Bilim
Din - İslam - Hayat
İslam ve İbadet
Hz. Muhammed, Gençlik ve Değerler
Tasavvuf, Anadolu'da İslam ve Gönül Coğrafyamız
Ahlak ve İnsan İlişkisi
Ahlaki Tutum ve Davranışlar
İslam Düşüncesinde İtikadi, Siyasi ve Fıkhi Yorumlar
Dünya ve Ahiret
Kur'an'a Göre Hz. Muhammed
Kur'an'da Bazı Kavramlar
Yaşayan Dinler
Güncel Dini Meseleler

Geometri
Konu
Temel Geometrik Kavramlar
Doğruda Açılar
Üçgende Açılar
Açı - Kenar Bağıntıları
Dik Üçgen
İkizkenar Üçgen
Eşkenar Üçgen
Üçgende Açıortay
Üçgende Kenarortay
Üçgende Merkezler
Üçgeninin Eşliği ve Benzerliği
Üçgende Alan
Çokgenler
Genel Dörtgenler
Deltoid
Yamuk
Paralelkenar
Eşkenar Dörtgen
Dikdörtgen
Kare
Dik Prizmalar
Dik Piramitler

Fizik
Konu
Fizik Bilimine Giriş
Madde ve Özellikleri
Dayanıklılık-Akışkanların Genel Özellikleri
Katı Basıncı
Sıvı Basıncı
Gaz Basıncı
Kaldırma Kuvveti
Sıcaklık-Termometreler
Isı-İç Enerji
Hal Değişimi-Denge Sıcaklığı
Isı Enerjisinin İletimi ve Genleşme
Düzgün Doğrusal Hareket
Kuvvet ve Newton'un Hareket Yasaları
Sürtünme Kuvveti
İş-Güç-Enerji
Mekanik Enerji
Enerji Korunumu-Dönüşümü ve Verim
Elektrik Yüklerinin Özellikleri-Elektriklenme
Elektroskop ve Yük Dağılımı
Elektriksel Kuvvet ve Alan
Elektrik Devreleri
Üreteçlerin Bağlanması
Elektriksel Güç ve Enerji
Lambalı Devreler
Mıknatıslar ve Manyetik Alan
Dalgalar ile İlgili Temel Kavramlar
Yay Dalgaları
Su Dalgaları
Ses ve Deprem Dalgaları
Aydınlanma
Gölge
Işığın Yansıması ve Düzlem Ayna
Küresel Aynalar
Işığın Kırılması
Mercekler
Prizmalar ve Renkler

Kimya
Konu
Kimya Bilimi
Atom modelleri ve atomun yapısı
Periyodik tablo ve periyodik değişimler
Güçlü etkileşimler(İyonik bağ)
Güçlü etkileşimler(Kovalent bağ-metalik bağ)
Zayıf etkileşimler
Katılar
Sıvılar
Gazlar
Hal değişim olayları
Doğa ve Kimya
Kimyanın temel kanunları
Mol kavramı
Kimyasal tepkimeler ve türleri
Kimyasal hesaplamalar
Karışımların sınıflandırılması
Derişim birimleri
Kolligatif özellikler
Karışımların saflaştırılması ve ayrıştırılması
Asitlerin ve bazların özellikleri
Asitlerin ve bazların tepkimeleri
Hayatımızdaki asitler,bazlar ve tuzlar
Kimya her yerde

Biyoloji
Konu
Canlıların Ortak Özellikleri
İnorganik Moleküller
Karbonhidratlar
Lipitler
Proteinler
Enzimler
Vitaminler ve Hormonlar
Nükleik Asitler ve ATP
Hücre Organelleri
Hücre Zarından Madde Geçişleri
Bilimsel Yöntem
Canlıların Çeşitliliği ve Sınıflandırılması
Bakteriler ve Arkeler
Protista ve Mantarlar
Bitkiler
Hayvanlar
Virüsler
Mitoz Bölünme ve Eşeysiz Üreme
Mayoz Bölünme ve Eşeyli Üreme
Gamet Oluşumu ve Çaprazlamalar
Kan Grupları
Cinsiyete Bağlı Kalıtım
Soyağaçları
Ekosistem Ekolojisi
Güncel Çevre Sorunları
`, '12. Sınıf', '12');

const INITIAL_MEZUN_TOPICS = parseTopicText(`
Türk Dili ve Edebiyatı
Konu
Güzel Sanatlar ve Edebiyat
Şiirde Ahenk (Ölçü-Uyak-Redif)
Şiirde Yapı ve Türleri
Edebi Sanatlar (Söz Sanatları)
Düzyazı Türleri
Edebi Akımlar
İslamiyet Öncesi Sözlü Edebiyat
İslamiyet Öncesi Yazılı Edebiyat
İslamiyet Etkisindeki Geçiş Dönemi
Halk Edebiyatı
Divan Edebiyatı
Tanzimat Dönemi
Servet-i Fünun Dönemi
Fecr-i Ati Topluluğu
Milli Edebiyat Dönemi
Cumhuriyet Dönemi - Şiir
Cumhuriyet Dönemi - Hikaye - Roman
Cumhuriyet Dönemi - Tiyatro
Cumhuriyet Dönemi - Öğretici Metinler
Türk Dünyası Edebiyatı - Dünya Edebiyatı

Tarih
Konu
Tarih ve Zaman
İnsanlığın İlk Dönemleri
Orta Çağ'da Dünya
İlk ve Orta Çağlarda Türk Dünyası
İslam Medeniyetinin Doğuşu
Türklerin İslamiyet'i Kabulü ve İlk Türk İslam Devletleri
Yerleşme ve Devletleşme Sürecinde Selçuklu Türkiyesi
Beylikten Devlete Osmanlı Siyaseti (1302-1453)
Devletleşme Sürecinde Savaşçılar ve Askerler
Beylikten Devlete Osmanlı Medeniyeti
Dünya Gücü Osmanlı (1453-1595)
Sultan ve Osmanlı Merkez Teşkilatı
Klasik Çağda Osmanlı Toplum Düzeni
Değişen Dünya Dengeleri Karşısında Osmanlı Siyaseti (1595-1774)
Değişim Çağında Avrupa ve Osmanlı
Devrimler Çağında Değişen Devlet-Toplum İlişkileri
Uluslararası İlişkilerde Denge Stratejisi (1774-1914)
XIX. ve XX. Yüzyılda Değişen Sosyo-Ekonomik Hayat
XX. Yüzyıl Başlarında Osmanlı Devleti ve Dünya
Milli Mücadele'ye Hazırlık Dönemi
Milli Mücadele Dönemi
Atatürkçülük ve Türk İnkılabı
İki Savaş Arası Dönemde Türkiye ve Dünya
II. Dünya Savaşı Sürecinde Türkiye ve Dünya
II. Dünya Savaşı
II. Dünya Savaşı Sonrasında Türkiye ve Dünya
Soğuk Savaş Dönemi
Yumuşama Dönemi ve Sonrası
Toplumsal Devrim Çağında Dünya ve Türkiye
XXI. Yüzyılın Eşiğinde Türkiye ve Dünya
Küreselleşen Dünya

Coğrafya
Konu
Biyoçeşitlilik
Ekosistem, Enerji Akışı ve Madde Döngüleri
Su Ekosistemleri
Doğadaki Ekstrem Olaylar
Doğa ve Değişim
Nüfus Politikaları
Türkiye'nin Nüfus Projeksiyonları
Şehirler ve Etki Alanları
Ekonomik Faaliyet Türleri
Ekonomik Faaliyetlerin Sosyal ve Kültürel Etkileri
Doğal Kaynaklar ve Ekonomi
Türkiye Ekonomisi ve Ekonomi Politikaları
Medeniyetler Merkezi Anadolu
Türkiye'de Arazi Kullanımı
Türkiye'de Tarım
Türkiye'de Hayvancılık
Türkiye'de Madenler ve Enerji Kaynakları
Türkiye'de Sanayi
Türkiye'de Ulaşım
Türkiye'de Ticaret
Türkiye'de Turizm
Türkiye'nin Bölgesel Kalkınma Projeleri
Türkiye'de Bölge Sınıflandırması
Türkiye'nin Coğrafi Konumu ve Jeopolitiği
Coğrafi Keşifler ve Sömürgecilik
İlk Kültür Merkezleri
Kültür Bölgelerinin Oluşumu ve Türk Kültürü
Küresel Ticaret ve Turizm
Ülkelerin Gelişmişlik Düzeyi ve Doğal Kaynak Potansiyelleri
Gelişmiş ve Gelişmekte Olan Ülkeler
Enerji Taşımacılığı
Uluslararası Örgütler
Çatışma Bölgeleri
Doğal Çevrenin Sınırlılığı
Doğal Kaynak Kullanımının Çevresel Etkileri
Arazi Kullanımının Çevresel Etkileri
Küresel Çevre Sorunları
Doğal Kaynakların Sürdürülebilir Kullanımı

Matematik
Konu
Polinomlar
Çarpanlara Ayırma
İkinci Dereceden Denklemler
Fonksiyonların Dönüşümleri, Ötelenmeleri, Simetri
Parabol
İkinci Dereceden Eşitsizlikler
Toplama Çarpma Yoluyla Seyma
Permütasyon
Kombinasyon
Binom
Olasılık
Trigonometri
Logaritma
Diziler
Limit - Süreklilik
Türev
İntegral

Geometri
Konu
Temel Geometrik Kavramlar
Doğruda Açılar
Üçgende Açılar
Açı - Kenar Bağıntıları
Dik Üçgen
İkizkenar Üçgen
Eşkenar Üçgen
Üçgende Açıortay
Üçgende Kenarortay
Üçgende Merkezler
Üçgenin Eşliği ve Benzerliği
Üçgende Alan
Çokgenler
Genel Dörtgenler
Deltoid
Yamuk
Paralelkenar
Eşkenar Dörtgen
Dikdörtgen
Kare
Noktanın Analitik İncelemesi
Doğrunun Analitik İncelemesi
Çemberde Açı
Çemberde Uzunluk
Dairede Alan
Çemberin Analitik İncelemesi
Dik Prizmalar
Dik Piramitler
Silindir
Koni
Küre
Dönüşüm Geometrisi

Fizik
Konu
Vektör ve Kuvvet
Bağıl Hareket
Newton'un Hareket Yasaları
Doğrusal Hareket
Yeryüzünde Hareket
İş ve Enerji
İtme ve Momentum
Kuvvet - Tork - Denge
Ağırlık Merkezi
Basit Makineler
Düzgün Çembersel Hareket
Dönme Kinetik Enerjisi- Açısal Momentum
Genel Çekim Kanunu ve Kepler Kanunları
Basit Harmonik Hareket
Elektriksel Kuvvet
Elektriksel Alan
Elektriksel Potansiyel Enerji
Elektriksel Potansiyel - Elektriksel İş
Düzgün Elektrik Alan ve Paralel Levhalar
Sığaçlar
Manyetik Alan
Manyetik Kuvvet
Elektromanyetik İndüksiyon
Alternatif Akım ve Transformatörler
Su Dalgalarında Kırınım - Girişim
Işık Teorileri
Elektromanyetik Dalgalar
Atom Modelleri
Atomaltı Parçacıklar
Radyoaktivite
Özel Görelilik
Siyah Cisim Işıması - Fotoelektrik Olay
Modern Fiziğin Uygulamaları - Görüntüleme Teknolojisi

Kimya
Konu
Atomun kuantum modeli ve kuantum sayıları
Elektron dizilişi, grup ve periyot bulma
Periyodik tablo ve periyodik değişimler
Yükseltgenme basamağı
Gazların özellikleri, gaz kanunları ve ideal gaz denklemi
Gazların kinetiği
Gazlarda kısmi basınç
Gazların karşılaştırılması ve gazların yoğunluğu
Gazların karıştırılması
Su üstünde gaz toplanması ve denge buhar basıncı
Gerçek gazlar
Çözücü-çözünen etkileşimleri
Derişim birimleri
Koligatif özellikler
Çözünürlük
Endotermik-ekzotermik tepkimeler ve grafikleri
Oluşum entalpileri
Bağ enerjileri ve Hess yasası
Ortalama tepkime hızı, hız ölçümü ve çarpışma teorisi
Hız bağıntısı
Hıza etki eden faktörler
Kademeli tepkimelerde hız ve deneysel verilerle hız
Denge oluşumu ve şartları
Denge sabitleri (Kc-Kp) ve hesaplamaları
Dengede Hess prensipleri ve denge kesri
Dengeye etki eden faktörler
Asit-baz özellikleri ve konjuge asit-baz çifti
Suyun otoiyonizasyonu ve pH-pOH kavramı
Kuvvetli asit ve bazlarda pH-pOH
Zayıf asit ve bazlarda pH-pOH
Tampon çözeltilerde pH-pOH ve hidroliz
Nötrleşme ve titrasyon
Çözünürlük dengesi ve sabiti (Kçç)
Çözünürlüğe etki eden faktörler
Çözünürlüğe ortak iyon etkisi
İndirgenme-yükseltgenme tepkimeleri (Redoks)
Aktiflik ve aşınma
Piller ve pil geriliminin hesaplanması
Pil potansiyeline etki eden faktörler
Elektroliz
Faraday yasaları ve hesaplamalar
Karbon kimyasına giriş ve Lewis yapıları
Hibritleşme ve molekül geometrisi (VSEPR)
Hidrokarbonların genel özellikleri
Alkanlar
Alkenler
Alkinler
Aromatik bileşikler
Alkoller
Eterler
Aldehitler
Ketonlar
Karboksilik asitler
Esterler
Enerji kaynakları ve bilimsel gelişmeler

Biyoloji
Konu
Sinir Sistemi
Endokrin Sistem
Duyu Organları
Destek ve Hareket Sistemi
Sindirim Sistemi
Dolaşım Sistemleri ve Vücut Savunmaları
Solunum Sistemi
Üriner Sistem
Üreme Sistemi ve Embriyonik Gelişim
Komünite Ekolojisi
Popülasyon Ekolojisi
Nükleik Asitler
Genetik Şifre ve Protein Sentezi
Genellik Mühendisliği ve Biyoteknoloji
Fotosentez ve Kemosentez
Hücresel Solunum
Bitkisel Dokular ve Organlar
Bitkilerde Hormonlar ve Bitkilerde Hareket
Bitkilerde Beslenme ve Madde Taşınması
Bitkilerde Eşeyli Üreme ve Çimlenme
Canlılar ve Çevre

Felsefe
Konu
Felsefeyi Tanıma
Bilgi Felsefesi
Varlık Felsefesi
Ahlak Felsefesi
Sanat Felsefesi
Din Felsefesi
Siyaset Felsefesi
Bilim Felsefesi
İlk Çağ Felsefesi
MÖ 6. Yüzyıl-MS. Yüzyıl Felsefesi
MS 2. Yüzyıl-MS 25.Yüzyıl Felsefesi
15. Yüzyıl-17.Yüzyıl Felsefesi
18. Yüzyıl-19.Yüzyıl Felsefesi
20. Yüzyıl Felsefesi

Sosyoloji
Konu
Sosyoloji Giriş
Birey Toplum İlişkisi
Toplum Yapısı
Toplumda Değişme ve Gelişme
Toplum ile Kültür
Toplumun Kurumları

Psikoloji
Konu
Psikoloji Giriş
Psikoloji Temel Süreçleri
Öğrenme, Bellek ve Düşünme
Ruh Sağlığı Temelleri

Mantık
Konu
Mantık Giriş
Klasik Mantık
Mantık ile Dil
Sembolik Mantık
`, 'Mezun', 'mezun');

const INITIAL_TOPIC_BANK = [
  ...generateCurriculumTopicBank(),
  ...INITIAL_MATEMATIK_9_TOPICS,
  ...INITIAL_BIYOLOJI_9_TOPICS,
  ...INITIAL_KIMYA_9_TOPICS,
  ...INITIAL_FIZIK_9_TOPICS,
  ...INITIAL_EDEBIYAT_9_TOPICS,
  ...INITIAL_10_TOPICS,
  ...INITIAL_11_TOPICS,
  ...INITIAL_12_TOPICS,
  ...INITIAL_MEZUN_TOPICS,
];

const normalizeTopicText = (value) => String(value || '')
  .toLowerCase()
  .replace(/ı/g, 'i')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/\s+/g, ' ')
  .trim();
const normalizeLessonTopicToken = (value) => {
  const token = normalizeTopicText(value);
  if (token.includes('matamatik') || token.includes('matematik')) return 'matematik';
  if (token.includes('edebiyat')) return 'edebiyat';
  if (token.includes('turkce')) return 'turkce';
  if (token.includes('geometri')) return 'geometri';
  if (token.includes('fizik')) return 'fizik';
  if (token.includes('kimya')) return 'kimya';
  if (token.includes('biyoloji')) return 'biyoloji';
  if (token.includes('cografya')) return 'cografya';
  if (token.includes('tarih')) return 'tarih';
  if (token.includes('felsefe')) return 'felsefe';
  if (token.includes('sosyoloji')) return 'sosyoloji';
  if (token.includes('psikoloji')) return 'psikoloji';
  if (token.includes('mantik')) return 'mantik';
  if (token.startsWith('din kulturu')) return 'din kulturu';
  return token;
};

function loadTopicBank() {
  if (typeof window === 'undefined') return INITIAL_TOPIC_BANK;
  try {
    const saved = JSON.parse(window.localStorage.getItem(TOPIC_BANK_STORAGE_KEY) || '[]');
    const savedList = Array.isArray(saved) ? saved : [];
    const savedKeys = new Set(savedList.map(item => `${normalizeTopicText(item.sinif)}|${normalizeTopicText(item.ders)}|${normalizeTopicText(item.konu)}`));
    const missingInitial = INITIAL_TOPIC_BANK.filter(item => !savedKeys.has(`${normalizeTopicText(item.sinif)}|${normalizeTopicText(item.ders)}|${normalizeTopicText(item.konu)}`));
    const merged = [...savedList, ...missingInitial];
    window.localStorage.setItem(TOPIC_BANK_STORAGE_KEY, JSON.stringify(merged));
    return merged;
  } catch {
    return INITIAL_TOPIC_BANK;
  }
}

function saveTopicBank(items) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(TOPIC_BANK_STORAGE_KEY, JSON.stringify(items));
}

function loadRepeatTasks() {
  if (typeof window === 'undefined') return [];
  try {
    const saved = JSON.parse(window.localStorage.getItem(REPEAT_TASK_STORAGE_KEY) || '[]');
    return Array.isArray(saved) ? saved : [];
  } catch {
    return [];
  }
}

function saveRepeatTasks(items) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(REPEAT_TASK_STORAGE_KEY, JSON.stringify(items));
}

function getTopicSuggestions(topicBank, lessonName, className = '') {
  const lessonToken = normalizeLessonTopicToken(lessonName);
  const classToken = normalizeTopicText(className).match(/\d+/)?.[0] || '';
  return topicBank.filter(item => {
    if (!item.aktif) return false;
    const lessonMatches = !lessonToken || normalizeLessonTopicToken(item.ders) === lessonToken;
    const classMatches = !classToken || normalizeTopicText(item.sinif).includes(classToken);
    return lessonMatches && classMatches;
  });
}

export function RehberKonularPage() {
  const [topics, setTopics] = useState(() => loadTopicBank());
  const [filters, setFilters] = useState({ sinif: '9. Sınıf', ders: 'Matematik', donem: '' });
  const [form, setForm] = useState({ sinif: '9. Sınıf', ders: 'Matematik', donem: '1. Dönem', unite: '', konu: '' });

  const uniqueValues = (field) => [...new Set(topics.map(item => item[field]).filter(Boolean))];
  const filteredTopics = topics.filter(item => (
    (!filters.sinif || item.sinif === filters.sinif) &&
    (!filters.ders || item.ders === filters.ders) &&
    (!filters.donem || item.donem === filters.donem)
  ));
  const groupedTopics = filteredTopics.reduce((groups, item) => {
    const key = `${item.donem} • ${item.unite || 'Ünite Yok'}`;
    if (!groups[key]) groups[key] = [];
    groups[key].push(item);
    return groups;
  }, {});

  const persist = (next) => {
    setTopics(next);
    saveTopicBank(next);
  };

  const addTopic = () => {
    if (!form.sinif || !form.ders || !form.donem || !form.konu.trim()) {
      toast.error('Sınıf, ders, dönem ve konu zorunlu');
      return;
    }
    const duplicate = topics.some(item => (
      normalizeTopicText(item.sinif) === normalizeTopicText(form.sinif) &&
      normalizeTopicText(item.ders) === normalizeTopicText(form.ders) &&
      normalizeTopicText(item.konu) === normalizeTopicText(form.konu)
    ));
    if (duplicate) {
      toast.error('Bu konu zaten kayıtlı');
      return;
    }
    const next = [
      ...topics,
      {
        id: `topic-${Date.now()}`,
        sinif: form.sinif.trim(),
        ders: form.ders.trim(),
        donem: form.donem.trim(),
        unite: form.unite.trim(),
        konu: form.konu.trim(),
        sira: topics.length + 1,
        aktif: true,
      },
    ];
    persist(next);
    setFilters({ sinif: form.sinif.trim(), ders: form.ders.trim(), donem: form.donem.trim() });
    setForm(prev => ({ ...prev, konu: '' }));
    toast.success('Konu eklendi');
  };

  const removeTopic = (id) => {
    persist(topics.filter(item => item.id !== id));
    toast.success('Konu kaldırıldı');
  };

  const resetInitial = () => {
    const currentKeys = new Set(topics.map(item => `${normalizeTopicText(item.sinif)}|${normalizeTopicText(item.ders)}|${normalizeTopicText(item.konu)}`));
    const missing = INITIAL_TOPIC_BANK.filter(item => !currentKeys.has(`${normalizeTopicText(item.sinif)}|${normalizeTopicText(item.ders)}|${normalizeTopicText(item.konu)}`));
    if (missing.length === 0) {
      toast('Varsayılan konular zaten yüklü');
      return;
    }
    persist([...topics, ...missing]);
    toast.success(`${missing.length} konu eklendi`);
  };

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      <div className="card" style={{ padding: 22 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap', alignItems: 'center' }}>
          <div>
            <h2 style={{ fontFamily: 'var(--font-ui)', fontWeight: 900, margin: 0 }}>📚 Konular</h2>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.86rem', margin: '6px 0 0' }}>
              Rehberlik çalışma programlarında kullanılacak konu havuzunu yönetin.
            </p>
          </div>
          <button className="btn btn-outline" onClick={resetInitial}>Varsayılan Konuları Yükle</button>
        </div>
      </div>

      <div className="card" style={{ padding: 18 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12 }}>
          <div className="form-group">
            <label className="form-label">Sınıf</label>
            <input className="form-input" value={form.sinif} onChange={e => setForm({ ...form, sinif: e.target.value })} placeholder="9. Sınıf" />
          </div>
          <div className="form-group">
            <label className="form-label">Ders</label>
            <input className="form-input" value={form.ders} onChange={e => setForm({ ...form, ders: e.target.value })} placeholder="Matematik" />
          </div>
          <div className="form-group">
            <label className="form-label">Dönem</label>
            <select className="form-select" value={form.donem} onChange={e => setForm({ ...form, donem: e.target.value })}>
              <option>1. Dönem</option>
              <option>2. Dönem</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Ünite</label>
            <input className="form-input" value={form.unite} onChange={e => setForm({ ...form, unite: e.target.value })} placeholder="1. Ünite: SAYILAR" />
          </div>
        </div>
        <div className="form-group" style={{ marginTop: 12 }}>
          <label className="form-label">Konu</label>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <input
              className="form-input"
              style={{ flex: '1 1 320px' }}
              value={form.konu}
              onChange={e => setForm({ ...form, konu: e.target.value })}
              onKeyDown={e => e.key === 'Enter' && addTopic()}
              placeholder="Konu başlığı..."
            />
            <button className="btn btn-primary" onClick={addTopic}>+ Konu Ekle</button>
          </div>
        </div>
      </div>

      <div className="card" style={{ padding: 18 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12, marginBottom: 16 }}>
          <div className="form-group">
            <label className="form-label">Sınıf Filtresi</label>
            <select className="form-select" value={filters.sinif} onChange={e => setFilters({ ...filters, sinif: e.target.value })}>
              <option value="">Tümü</option>
              {uniqueValues('sinif').map(value => <option key={value} value={value}>{value}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Ders Filtresi</label>
            <select className="form-select" value={filters.ders} onChange={e => setFilters({ ...filters, ders: e.target.value })}>
              <option value="">Tümü</option>
              {uniqueValues('ders').map(value => <option key={value} value={value}>{value}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Dönem Filtresi</label>
            <select className="form-select" value={filters.donem} onChange={e => setFilters({ ...filters, donem: e.target.value })}>
              <option value="">Tümü</option>
              {uniqueValues('donem').map(value => <option key={value} value={value}>{value}</option>)}
            </select>
          </div>
        </div>

        {Object.entries(groupedTopics).map(([group, items]) => (
          <div key={group} style={{ marginBottom: 18 }}>
            <div style={{ fontWeight: 900, color: 'var(--text-primary)', marginBottom: 8 }}>{group}</div>
            <div style={{ display: 'grid', gap: 8 }}>
              {items.map((item, index) => (
                <div key={item.id} style={{ display: 'grid', gridTemplateColumns: '36px 1fr auto', gap: 10, alignItems: 'center', padding: '10px 12px', border: '1px solid var(--border)', borderRadius: 10, background: 'var(--bg-soft)' }}>
                  <span style={{ fontWeight: 900, color: 'var(--text-muted)' }}>{index + 1}</span>
                  <div>
                    <div style={{ fontWeight: 800 }}>{item.konu}</div>
                    <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)', marginTop: 2 }}>{item.sinif} • {item.ders}</div>
                  </div>
                  <button className="btn btn-ghost" onClick={() => removeTopic(item.id)}>Sil</button>
                </div>
              ))}
            </div>
          </div>
        ))}
        {filteredTopics.length === 0 && <div className="empty-state"><p>Bu filtrede konu bulunamadı.</p></div>}
      </div>
    </div>
  );
}

export function RehberTekrarlarPage() {
  const [ogrenciler, setOgrenciler] = useState([]);
  const [topicBank, setTopicBank] = useState(() => loadTopicBank());
  const [tasks, setTasks] = useState(() => loadRepeatTasks());
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ durum: 'aktif', ogrenci_id: '' });
  const [form, setForm] = useState({
    ogrenci_id: '',
    ders: '',
    konu: '',
    sure: '3',
    sure_birimi: 'gün',
    not: '',
  });

  useEffect(() => {
    axios.get('/ogrenciler?limit=1000')
      .then((res) => {
        if (res.data.success) setOgrenciler(res.data.data || []);
      })
      .catch(() => toast.error('Öğrenciler yüklenemedi'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const refresh = () => {
      setTopicBank(loadTopicBank());
      setTasks(loadRepeatTasks());
    };
    window.addEventListener('storage', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      window.removeEventListener('storage', refresh);
      window.removeEventListener('focus', refresh);
    };
  }, []);

  const persistTasks = (next) => {
    setTasks(next);
    saveRepeatTasks(next);
  };

  const selectedStudent = ogrenciler.find(item => String(item.id) === String(form.ogrenci_id));
  const selectedStudentClass = selectedStudent
    ? String(selectedStudent.sinif_ad || selectedStudent.sinif_adi || selectedStudent.sinif?.ad || selectedStudent.sinif || '')
    : '';
  const topicLessons = useMemo(() => (
    [...new Set(topicBank.map(item => item.ders).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b, 'tr'))
  ), [topicBank]);
  const konuOnerileri = useMemo(
    () => getTopicSuggestions(topicBank, form.ders, selectedStudentClass),
    [topicBank, form.ders, selectedStudentClass]
  );
  const filteredTasks = tasks.filter(task => {
    if (filters.ogrenci_id && String(task.ogrenci_id) !== String(filters.ogrenci_id)) return false;
    if (filters.durum === 'aktif' && task.tamamlandi) return false;
    if (filters.durum === 'tamamlandi' && !task.tamamlandi) return false;
    return true;
  });

  const addRepeatTask = () => {
    if (!form.ogrenci_id) return toast.error('Öğrenci seçin');
    if (!form.ders) return toast.error('Ders seçin');
    if (!form.konu.trim()) return toast.error('Tekrar konusu seçin');
    if (!form.sure || Number(form.sure) <= 0) return toast.error('Tekrar süresi girin');
    const student = ogrenciler.find(item => String(item.id) === String(form.ogrenci_id));
    const next = [
      {
        id: `repeat-${Date.now()}`,
        ogrenci_id: form.ogrenci_id,
        ogrenci_ad: `${student?.ad || ''} ${student?.soyad || ''}`.trim(),
        sinif: selectedStudentClass,
        ders: form.ders,
        konu: form.konu.trim(),
        sure: Number(form.sure),
        sure_birimi: form.sure_birimi,
        not: form.not.trim(),
        tamamlandi: false,
        olusturma_tarihi: new Date().toISOString(),
        tamamlanma_tarihi: null,
      },
      ...tasks,
    ];
    persistTasks(next);
    setForm(prev => ({ ...prev, konu: '', not: '' }));
    toast.success('Tekrar görevi eklendi');
  };

  const toggleTask = (id) => {
    const target = tasks.find(task => task.id === id);
    const willComplete = target && !target.tamamlandi;
    const next = tasks.map(task => (
      task.id === id
        ? {
            ...task,
            tamamlandi: !task.tamamlandi,
            tamamlanma_tarihi: !task.tamamlandi ? new Date().toISOString() : null,
          }
        : task
    ));
    persistTasks(next);
    if (willComplete && filters.durum === 'aktif') {
      setFilters(prev => ({ ...prev, durum: 'tum' }));
    }
  };

  const removeTask = (id) => {
    persistTasks(tasks.filter(task => task.id !== id));
    toast.success('Tekrar görevi silindi');
  };

  const escapePrintValue = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const printRepeatTasks = () => {
    if (filteredTasks.length === 0) return toast.error('Çıktı alınacak tekrar görevi yok');
    const rows = filteredTasks.map((task, index) => `
      <tr>
        <td>${index + 1}</td>
        <td>${escapePrintValue(task.ogrenci_ad || '')}</td>
        <td>${escapePrintValue(task.sinif || '')}</td>
        <td>${escapePrintValue(task.ders || '')}</td>
        <td>${escapePrintValue(task.konu || '')}</td>
        <td>${escapePrintValue(task.tamamlandi ? 'Tamamlandı' : `${task.sure} ${task.sure_birimi}`)}</td>
        <td>${escapePrintValue(task.not || '')}</td>
      </tr>
    `).join('');
    const popup = window.open('', '_blank', 'width=1100,height=760');
    if (!popup) return toast.error('Yazdırma penceresi engellendi');
    popup.document.write(`
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Tekrarlar</title>
          <style>
            @page { size: A4 landscape; margin: 10mm; }
            * { box-sizing: border-box; }
            body { margin: 0; font-family: Tahoma, Arial, sans-serif; color: #111827; background: #fff; }
            .page { position: relative; min-height: 190mm; overflow: hidden; }
            .watermark { position: fixed; inset: 0; display: flex; align-items: center; justify-content: center; pointer-events: none; z-index: 0; }
            .watermark img { width: 58%; max-width: 560px; opacity: 0.075; }
            .content { position: relative; z-index: 1; }
            header { display: flex; justify-content: space-between; align-items: center; gap: 18px; margin-bottom: 14px; border-bottom: 2px solid #D9E2F2; padding-bottom: 10px; }
            h1 { margin: 0; font-size: 22px; letter-spacing: .02em; }
            .meta { color: #64748B; font-size: 12px; margin-top: 5px; }
            .logo { width: 78px; height: 78px; object-fit: contain; }
            table { width: 100%; border-collapse: collapse; table-layout: fixed; background: rgba(255,255,255,.9); }
            th, td { border: 1px solid #CBD5E1; padding: 7px 8px; font-size: 11px; vertical-align: top; word-break: break-word; }
            th { background: #EAF1FF; color: #1E3764; text-align: left; font-weight: 900; }
            td:first-child, th:first-child { width: 36px; text-align: center; }
            th:nth-child(2) { width: 145px; }
            th:nth-child(3) { width: 80px; }
            th:nth-child(4) { width: 100px; }
            th:nth-child(6) { width: 100px; }
            @media print { body { print-color-adjust: exact; -webkit-print-color-adjust: exact; } }
          </style>
        </head>
        <body>
          <div class="page">
            <div class="watermark"><img src="${kareKursBulvarLogo}" alt="" /></div>
            <div class="content">
              <header>
                <div>
                  <h1>Tekrar Takip Listesi</h1>
                  <div class="meta">KareKurs Bulvar • ${new Date().toLocaleDateString('tr-TR')}</div>
                </div>
                <img class="logo" src="${kareKursBulvarLogo}" alt="KareKurs Bulvar" />
              </header>
              <table>
                <thead>
                  <tr>
                    <th>No</th>
                    <th>Öğrenci</th>
                    <th>Sınıf</th>
                    <th>Ders</th>
                    <th>Konu</th>
                    <th>Durum / Süre</th>
                    <th>Not</th>
                  </tr>
                </thead>
                <tbody>${rows}</tbody>
              </table>
            </div>
          </div>
        </body>
      </html>
    `);
    popup.document.close();
    popup.focus();
    setTimeout(() => popup.print(), 250);
  };

  if (loading) return <div className="page-loader"><div className="spinner" style={{ width: 36, height: 36 }} /></div>;

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      <div className="card" style={{ padding: 22 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap', alignItems: 'center' }}>
          <div>
            <h2 style={{ fontFamily: 'var(--font-ui)', fontWeight: 900, margin: 0 }}>🔁 Tekrarlar</h2>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.86rem', margin: '6px 0 0' }}>
              Öğrenciye tekrar etmesi gereken konuları ve sürelerini atayın; öğrenci tamamladığında tikleyin.
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span className="badge badge-muted">{tasks.filter(task => !task.tamamlandi).length} aktif tekrar</span>
            <button className="btn btn-outline" onClick={printRepeatTasks}>🖨 Çıktı Al</button>
          </div>
        </div>
      </div>

      <div className="card" style={{ padding: 18 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
          <div className="form-group">
            <label className="form-label">Öğrenci *</label>
            <select className="form-select" value={form.ogrenci_id} onChange={e => setForm(prev => ({ ...prev, ogrenci_id: e.target.value }))}>
              <option value="">Öğrenci seçin</option>
              {ogrenciler.map(ogr => (
                <option key={ogr.id} value={ogr.id}>
                  {ogr.ad} {ogr.soyad}{ogr.sinif_adi || ogr.sinif_ad ? ` • ${ogr.sinif_adi || ogr.sinif_ad}` : ''}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Ders *</label>
            <select className="form-select" value={form.ders} onChange={e => setForm(prev => ({ ...prev, ders: e.target.value, konu: '' }))}>
              <option value="">Ders seçin</option>
              {topicLessons.map(ders => <option key={ders} value={ders}>{ders}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Tekrar Süresi *</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 110px', gap: 8 }}>
              <input className="form-input" type="number" min="1" value={form.sure} onChange={e => setForm(prev => ({ ...prev, sure: e.target.value }))} />
              <select className="form-select" value={form.sure_birimi} onChange={e => setForm(prev => ({ ...prev, sure_birimi: e.target.value }))}>
                <option>dakika</option>
                <option>saat</option>
                <option>gün</option>
                <option>hafta</option>
              </select>
            </div>
          </div>
        </div>

        <div className="form-group" style={{ marginTop: 12 }}>
          <label className="form-label">Konu *</label>
          <input
            className="form-input"
            list="tekrar-konu-onerileri"
            value={form.konu}
            onChange={e => setForm(prev => ({ ...prev, konu: e.target.value }))}
            placeholder="Tekrar edilecek konu..."
          />
          <datalist id="tekrar-konu-onerileri">
            {konuOnerileri.map(item => (
              <option key={item.id} value={item.konu}>{item.sinif} • {item.donem} • {item.unite}</option>
            ))}
          </datalist>
          {konuOnerileri.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
              {konuOnerileri.slice(0, 8).map(item => (
                <button
                  key={item.id}
                  type="button"
                  className="btn btn-ghost"
                  style={{ fontSize: '0.74rem', padding: '6px 9px' }}
                  onClick={() => setForm(prev => ({ ...prev, konu: item.konu }))}
                >
                  {item.konu}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="form-group" style={{ marginTop: 12 }}>
          <label className="form-label">Not</label>
          <textarea
            className="form-input"
            rows={3}
            value={form.not}
            onChange={e => setForm(prev => ({ ...prev, not: e.target.value }))}
            placeholder="Örn. Önce konu özeti, sonra 30 soru çöz..."
          />
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
          <button className="btn btn-outline" onClick={() => setForm({ ogrenci_id: '', ders: '', konu: '', sure: '3', sure_birimi: 'gün', not: '' })}>Alanları Temizle</button>
          <button className="btn btn-primary" onClick={addRepeatTask}>Tekrar Ekle</button>
        </div>
      </div>

      <div className="card" style={{ padding: 18 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 12, marginBottom: 14 }}>
          <div className="form-group">
            <label className="form-label">Durum</label>
            <select className="form-select" value={filters.durum} onChange={e => setFilters(prev => ({ ...prev, durum: e.target.value }))}>
              <option value="aktif">Aktif</option>
              <option value="tamamlandi">Tamamlanan</option>
              <option value="tum">Tümü</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Öğrenci Filtresi</label>
            <select className="form-select" value={filters.ogrenci_id} onChange={e => setFilters(prev => ({ ...prev, ogrenci_id: e.target.value }))}>
              <option value="">Tümü</option>
              {ogrenciler.map(ogr => <option key={ogr.id} value={ogr.id}>{ogr.ad} {ogr.soyad}</option>)}
            </select>
          </div>
        </div>

        {filteredTasks.length === 0 ? (
          <div className="empty-state"><p>Bu filtrede tekrar görevi yok.</p></div>
        ) : (
          <div style={{ display: 'grid', gap: 10 }}>
            {filteredTasks.map(task => (
              <div
                key={task.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '34px 1fr auto',
                  gap: 12,
                  alignItems: 'center',
                  padding: 14,
                  border: '1px solid var(--border)',
                  borderRadius: 14,
                  background: task.tamamlandi ? 'rgba(16,185,129,0.08)' : 'var(--bg-soft)',
                }}
              >
                <button
                  type="button"
                  onClick={() => toggleTask(task.id)}
                  title="Öğrenci tekrar edip geldiyse işaretle"
                  aria-pressed={Boolean(task.tamamlandi)}
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 6,
                    border: task.tamamlandi ? '2px solid #10B981' : '2px solid #94A3B8',
                    background: task.tamamlandi ? '#10B981' : '#FFFFFF',
                    color: '#FFFFFF',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 900,
                    fontSize: 18,
                    cursor: 'pointer',
                    lineHeight: 1,
                  }}
                >
                  {task.tamamlandi ? '✓' : ''}
                </button>
                <div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    <strong>{task.ogrenci_ad || 'Öğrenci'}</strong>
                    {task.sinif && <span className="badge badge-info">{task.sinif}</span>}
                    <span className="badge badge-muted">{task.ders}</span>
                    <span className={task.tamamlandi ? 'badge badge-success' : 'badge badge-warning'}>
                      {task.tamamlandi ? 'Tamamlandı' : `${task.sure} ${task.sure_birimi}`}
                    </span>
                  </div>
                  <div style={{ color: 'var(--text-primary)', fontWeight: 800, marginTop: 6 }}>{task.konu}</div>
                  {task.not && <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginTop: 5, whiteSpace: 'pre-line' }}>{task.not}</div>}
                  <div style={{ color: 'var(--text-muted)', fontSize: '0.72rem', marginTop: 6 }}>
                    Oluşturma: {new Date(task.olusturma_tarihi).toLocaleDateString('tr-TR')}
                    {task.tamamlanma_tarihi ? ` • Tamamlanma: ${new Date(task.tamamlanma_tarihi).toLocaleDateString('tr-TR')}` : ''}
                  </div>
                </div>
                <button className="btn btn-ghost" onClick={() => removeTask(task.id)}>Sil</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function RehberDevamsizlikPage() {
  const [loading, setLoading] = useState(false);
  const [ogrenciListesi, setOgrenciListesi] = useState([]);
  const [gununDersleri, setGununDersleri] = useState([]);
  const [siniflar, setSiniflar] = useState([]);
  const [dersListesi, setDersListesi] = useState([]);

  const [filtre, setFiltre] = useState({
    sinif_id: '',
    ders_id: '',
    tarih: new Date().toISOString().slice(0, 10)
  });

  const DURUM = {
    mevcut: { icon: '✅', label: 'Var', color: '#10B981' },
    devamsiz: { icon: '❌', label: 'Yok', color: '#EF4444' },
    gec: { icon: '⏰', label: 'Geç', color: '#F59E0B' },
    yok: { icon: '—', label: 'Girilmedi', color: '#CBD5E1' }
  };

  useEffect(() => {
    Promise.all([
      axios.get('/siniflar'),
      axios.get('/dersler')
    ]).then(([sRes, dRes]) => {
      setSiniflar(sRes.data.data || []);
      setDersListesi(dRes.data.data || []);
    }).catch(() => toast.error("Tanımlamalar yüklenemedi"));
  }, []);

  const loadPivotVeri = useCallback(async () => {
    if (!filtre.sinif_id) {
      toast.error("Lütfen bir sınıf seçin");
      return;
    }

    setLoading(true);
    try {
      const { data } = await axios.get(`/yoklama/rehber/detayli?sinif_id=${filtre.sinif_id}&tarih=${filtre.tarih}`);

      if (data.success) {
        setOgrenciListesi(data.data || []);

        if (filtre.ders_id && data.dersler) {
          setGununDersleri(data.dersler.filter(d => d.ders_id == filtre.ders_id));
        } else {
          setGununDersleri(data.dersler || []);
        }
      }
    } catch (err) {
      toast.error("Veriler çekilemedi. API rotasını kontrol edin.");
    } finally {
      setLoading(false);
    }
  }, [filtre]);

  const handleExcelExport = () => {
    if (ogrenciListesi.length === 0) return toast.error("Aktarılacak veri yok");

    const exportData = ogrenciListesi.map(ogr => {
      const obj = {
        "Öğrenci No": ogr.ogrenci_no,
        "Ad Soyad": `${ogr.ad} ${ogr.soyad}`
      };
      gununDersleri.forEach(d => {
        const dKey = `${d.baslangic_saati.slice(0, 5)} ${d.ders_adi}`;
        const durumKey = ogr.yoklamalar?.[d.id] || 'yok';
        obj[dKey] = DURUM[durumKey].label;
      });
      return obj;
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Devamsızlık");
    XLSX.writeFile(wb, `Yoklama_${filtre.tarih}_Sinif_${filtre.sinif_id}.xlsx`);
    toast.success("Excel indirildi!");
  };

  const handlePdfExport = () => {
    if (ogrenciListesi.length === 0) return toast.error("Aktarılacak veri yok");

    const doc = new jsPDF('l', 'mm', 'a4');
    doc.setFont("helvetica", "bold");
    doc.text(`${filtre.tarih} Tarihli Yoklama Çizelgesi`, 14, 15);

    const headers = [["Öğrenci", ...gununDersleri.map(d => `${d.baslangic_saati.slice(0, 5)}\n${d.ders_adi}`), "Toplam Yok"]];

    const body = ogrenciListesi.map(ogr => [
      `${ogr.ad} ${ogr.soyad}`,
      ...gununDersleri.map(d => {
        const dKey = ogr.yoklamalar?.[d.id] || 'yok';
        return dKey === 'mevcut' ? 'VAR' : dKey === 'devamsiz' ? 'YOK' : dKey === 'gec' ? 'GEC' : '-';
      }),
      Object.values(ogr.yoklamalar || {}).filter(v => v === 'devamsiz').length
    ]);

    autoTable(doc, {
      head: headers,
      body: body,
      startY: 20,
      theme: 'grid',
      styles: { fontSize: 8, halign: 'center' },
      headStyles: { fillColor: [220, 38, 38], textColor: 255 },
      columnStyles: { 0: { halign: 'left', fontStyle: 'bold', cellWidth: 40 } },
      didParseCell: (data) => {
        if (data.cell.raw === 'YOK') data.cell.styles.textColor = [239, 68, 68];
        if (data.cell.raw === 'VAR') data.cell.styles.textColor = [16, 185, 129];
      }
    });

    doc.save(`Yoklama_Raporu_${filtre.tarih}.pdf`);
  };

  return (
    <div className="page-container" style={{ display: 'flex', flexDirection: 'column', gap: 24, padding: '10px' }}>
      {/* Mobil Uyum İçin Dinamik CSS */}
      <style>{`
        @media (max-width: 768px) {
          .section-header { flex-direction: column; align-items: flex-start !important; gap: 15px; }
          .section-header div:last-child { width: 100%; justify-content: space-between; }
          .section-header button { flex: 1; font-size: 0.8rem; }
          
          /* Tabloyu kart yapısına çevir */
          .mobile-responsive-table thead { display: none; }
          .mobile-responsive-table, .mobile-responsive-table tbody, .mobile-responsive-table tr, .mobile-responsive-table td { 
            display: block; 
            width: 100%; 
          }
          .mobile-responsive-table tr { 
            margin-bottom: 20px; 
            border: 1px solid var(--border); 
            border-radius: 12px; 
            background: var(--bg-card);
            box-shadow: 0 2px 8px rgba(0,0,0,0.05);
            overflow: hidden;
          }
          .mobile-responsive-table td { 
            text-align: right !important; 
            padding: 12px 15px !important; 
            border-bottom: 1px solid var(--border-subtle); 
            position: relative;
            min-height: 45px;
            display: flex;
            justify-content: space-between;
            align-items: center;
          }
          .mobile-responsive-table td:last-child { border-bottom: none; background: #fff5f5; }
          .mobile-responsive-table td::before {
            content: attr(data-label);
            font-weight: 700;
            text-align: left;
            font-size: 0.8rem;
            color: var(--text-muted);
          }
          /* Sticky özelliği mobilde bozulmaması için sıfırla */
          .mobile-responsive-table td:first-child { 
            position: relative !important; 
            background: var(--accent-dim) !important; 
            color: var(--accent) !important; 
            text-align: left !important;
            justify-content: flex-start;
          }
        }
      `}</style>

      <div className="section-header animate-fadeInUp" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2 style={{ fontFamily: 'var(--font-ui)', fontSize: '1.6rem', fontWeight: 800 }}>📊 Yoklama Takibi</h2>
          <div className="section-subtitle">Öğrenci bazlı detaylı ders katılım dökümü</div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-outline" onClick={handlePdfExport}>📄 PDF</button>
          <button className="btn btn-success" onClick={handleExcelExport}>📥 Excel</button>
        </div>
      </div>

      <div className="card animate-fadeInUp stagger-1" style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 15, alignItems: 'end', padding: '20px'
      }}>
        <div className="form-group">
          <label className="form-label">📅 Tarih</label>
          <input type="date" className="form-input" value={filtre.tarih} onChange={e => setFiltre({ ...filtre, tarih: e.target.value })} />
        </div>
        <div className="form-group">
          <label className="form-label">🏫 Sınıf Seçimi</label>
          <select className="form-select" value={filtre.sinif_id} onChange={e => setFiltre({ ...filtre, sinif_id: e.target.value })}>
            <option value="">Seçiniz...</option>
            {siniflar.map(s => <option key={s.id} value={s.id}>{s.ad}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">📚 Ders Filtresi</label>
          <select className="form-select" value={filtre.ders_id} onChange={e => setFiltre({ ...filtre, ders_id: e.target.value })}>
            <option value="">Tüm Günün Dersleri</option>
            {dersListesi.map(d => <option key={d.id} value={d.id}>{d.ad}</option>)}
          </select>
        </div>
        <button className="btn btn-primary" onClick={loadPivotVeri} style={{ height: 42 }}>🔍 Sorgula</button>
      </div>

      <div className="card animate-fadeInUp stagger-2" style={{ padding: 0, overflow: 'hidden', border: 'none' }}>
        <div className="table-wrapper">
          <table className="table mobile-responsive-table">
            <thead>
              <tr style={{ background: 'var(--bg-elevated)' }}>
                <th style={{ minWidth: 220, position: 'sticky', left: 0, zIndex: 10, background: 'var(--bg-elevated)' }}>Öğrenci Bilgisi</th>
                {gununDersleri.map(d => (
                  <th key={d.id} style={{ textAlign: 'center', minWidth: 100 }}>
                    <div style={{ fontSize: '0.65rem', color: 'var(--accent)', fontWeight: 800 }}>{d.baslangic_saati.slice(0, 5)}</div>
                    <div style={{ fontSize: '0.75rem' }}>{d.ders_adi}</div>
                  </th>
                ))}
                <th style={{ textAlign: 'center', minWidth: 80 }}>Yok Sayısı</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="30" style={{ textAlign: 'center', padding: 60 }}><div className="spinner" /></td></tr>
              ) : ogrenciListesi.length === 0 ? (
                <tr><td colSpan="30" className="empty-state" style={{ textAlign: 'center', padding: '40px' }}>Sorgulama yapmak için sınıf seçiniz.</td></tr>
              ) : ogrenciListesi.map((ogr) => (
                <tr key={ogr.id} className="hover-scale">
                  <td style={{ fontWeight: 700, position: 'sticky', left: 0, background: 'var(--bg-card)', zIndex: 5, borderRight: '1px solid var(--border)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div className="avatar avatar-xs" style={{ background: 'var(--accent-dim)', color: 'var(--accent)', fontWeight: 800 }}>{ogr.ad?.[0]}</div>
                      <div>
                        <div style={{ fontSize: '0.85rem' }}>{ogr.ad} {ogr.soyad}</div>
                        <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>No: {ogr.ogrenci_no}</div>
                      </div>
                    </div>
                  </td>
                  {gununDersleri.map(d => {
                    const status = ogr.yoklamalar?.[d.id] || 'yok';
                    return (
                      <td key={d.id} data-label={`${d.baslangic_saati.slice(0, 5)} - ${d.ders_adi}`} style={{ textAlign: 'center' }}>
                        <span style={{ fontSize: '1.2rem', color: DURUM[status].color }}>{DURUM[status].icon}</span>
                        <span className="mobile-only-label" style={{ display: 'none' }}>{DURUM[status].label}</span>
                      </td>
                    );
                  })}
                  <td data-label="Toplam Yok" style={{ textAlign: 'center', fontWeight: 800, color: '#EF4444' }}>
                    {Object.values(ogr.yoklamalar || {}).filter(v => v === 'devamsiz').length}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const DEMO_SEANS_OGRENCILER = [
  { id: 9001, ad: 'Ahmet', soyad: 'Aydın', ogrenci_no: '1001', sinif_id: 1, sinif_ad: '10.VYV', telefon: '05xx xxx xx xx', veli_telefon: '05xx xxx xx xx' },
  { id: 9002, ad: 'Aleyna', soyad: 'Çoban', ogrenci_no: '1002', sinif_id: 2, sinif_ad: '8. Sınıf LGS - A Şubesi', telefon: '05xx xxx xx xx', veli_telefon: '05xx xxx xx xx' },
  { id: 9003, ad: 'Ali', soyad: 'Yılmaz', ogrenci_no: '1003', sinif_id: 3, sinif_ad: '12 A', telefon: '05xx xxx xx xx', veli_telefon: '05xx xxx xx xx' },
];

const DEMO_SEANS_SINIFLAR = [
  { id: 1, ad: '10.VYV' },
  { id: 2, ad: '8. Sınıf LGS - A Şubesi' },
  { id: 3, ad: '12 A' },
];

function getOgrenciSinifId(ogrenci) {
  return ogrenci.sinif_id || ogrenci.sinif?.id || ogrenci.sinifId || '';
}

function getOgrenciSinifAdi(ogrenci) {
  return ogrenci.sinif_ad || ogrenci.sinif_adi || ogrenci.sinif?.ad || ogrenci.sinif || 'Sınıf yok';
}

function getOgrenciAdSoyad(ogrenci) {
  return `${ogrenci.ad || ''} ${ogrenci.soyad || ''}`.trim() || ogrenci.full_name || 'İsimsiz öğrenci';
}

function formatSeansDateTime(tarih, saat) {
  if (!tarih) return 'Tarih seçilmedi';
  const date = new Date(`${tarih}T${saat || '00:00'}:00`);
  if (Number.isNaN(date.getTime())) return `${tarih} ${saat || ''}`;
  return date.toLocaleString('tr-TR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function addMinutesToTime(time, minutes) {
  const [hourRaw, minuteRaw] = String(time || '09:00').split(':');
  const date = new Date();
  date.setHours(Number(hourRaw) || 9, Number(minuteRaw) || 0, 0, 0);
  date.setMinutes(date.getMinutes() + Number(minutes || 0));
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

const WEEK_DAYS = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'];

function getWeekStartIso(dateStr) {
  const date = new Date(`${dateStr || new Date().toISOString().slice(0, 10)}T12:00:00`);
  const day = date.getDay() || 7;
  date.setDate(date.getDate() - day + 1);
  return date.toISOString().slice(0, 10);
}

function addDaysIso(dateStr, days) {
  const date = new Date(`${dateStr}T12:00:00`);
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
}

function formatShortDate(dateStr) {
  const date = new Date(`${dateStr}T12:00:00`);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' });
}

const EXCEL_PLAN_ROWS = Array.from({ length: 18 }, (_, index) => index + 1);
const SEANS_KONULARI = ['Sınav Motivasyonu', 'Ders Çalışma Programı', 'Devamsızlık', 'Hedef Belirleme', 'Veli Görüşmesi'];
const SEANS_SURE_ONERILERI = [5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 75, 90, 120];

function SeansDurationInput({ id, value, onChange }) {
  return (
    <>
      <input
        className="form-input"
        type="number"
        min="5"
        step="1"
        list={id}
        value={value ?? ''}
        onChange={event => onChange(event.target.value === '' ? '' : Number(event.target.value))}
        placeholder="Dakika"
        aria-label="Seans süresi dakika"
      />
      <datalist id={id}>
        {SEANS_SURE_ONERILERI.map(minutes => <option key={minutes} value={minutes} label={`${minutes} dk`} />)}
      </datalist>
    </>
  );
}

// ── Öğrenci Seansı Oluştur ───────────────────────────────────
export function RehberSeansOlusturPage() {
  const [ogrenciler, setOgrenciler] = useState([]);
  const [siniflar, setSiniflar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [arama, setArama] = useState('');
  const [sinifFilter, setSinifFilter] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedSchedules, setSelectedSchedules] = useState({});
  const [seanslar, setSeanslar] = useState([]);
  const [activeTab, setActiveTab] = useState('planlama');
  const [mode, setMode] = useState('takvim');
  const [weekStart, setWeekStart] = useState(getWeekStartIso(new Date().toISOString().slice(0, 10)));
  const [weeklySessions, setWeeklySessions] = useState([]);
  const [plannerCell, setPlannerCell] = useState(null);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [form, setForm] = useState({
    tarih: new Date().toISOString().slice(0, 10),
    saat: '10:00',
    sure_dakika: 30,
    konu: 'Rehberlik görüşmesi',
    not: '',
    notify_student: true,
    notify_parent: true,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ogrRes, sinifRes] = await Promise.all([
        axios.get('/ogrenciler?limit=1000'),
        axios.get('/siniflar'),
      ]);
      const nextOgrenciler = ogrRes.data?.success ? (ogrRes.data.data || []) : [];
      const nextSiniflar = sinifRes.data?.success ? (sinifRes.data.data || []) : [];
      setOgrenciler(nextOgrenciler.length ? nextOgrenciler : DEMO_SEANS_OGRENCILER);
      setSiniflar(nextSiniflar.length ? nextSiniflar : DEMO_SEANS_SINIFLAR);
    } catch (err) {
      setOgrenciler(DEMO_SEANS_OGRENCILER);
      setSiniflar(DEMO_SEANS_SINIFLAR);
      toast.error('Öğrenci listesi alınamadı, örnek liste gösteriliyor');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const normalizedSearch = arama.trim().toLowerCase();
  const filteredOgrenciler = ogrenciler.filter((ogrenci) => {
    if (sinifFilter && String(getOgrenciSinifId(ogrenci)) !== String(sinifFilter)) return false;
    if (!normalizedSearch) return true;
    const haystack = [
      getOgrenciAdSoyad(ogrenci),
      ogrenci.ogrenci_no,
      ogrenci.numara,
      getOgrenciSinifAdi(ogrenci),
    ].filter(Boolean).join(' ').toLowerCase();
    return haystack.includes(normalizedSearch);
  });

  const selectedOgrenciler = ogrenciler.filter((ogrenci) => selectedIds.includes(String(ogrenci.id)));
  const filteredIds = filteredOgrenciler.map((ogrenci) => String(ogrenci.id));
  const allFilteredSelected = filteredIds.length > 0 && filteredIds.every((id) => selectedIds.includes(id));
  const weekDates = WEEK_DAYS.map((label, index) => ({
    label,
    date: addDaysIso(weekStart, index),
  }));

  const createDefaultSchedule = (index = 0) => ({
    tarih: form.tarih,
    saat: addMinutesToTime(form.saat, index * Number(form.sure_dakika || 30)),
    sure_dakika: Number(form.sure_dakika || 30),
  });

  const toggleStudent = (id) => {
    const strId = String(id);
    setSelectedIds(prev => {
      if (prev.includes(strId)) {
        setSelectedSchedules(current => {
          const next = { ...current };
          delete next[strId];
          return next;
        });
        return prev.filter(item => item !== strId);
      }
      setSelectedSchedules(current => ({ ...current, [strId]: createDefaultSchedule(prev.length) }));
      return [...prev, strId];
    });
  };

  const toggleFilteredSelection = () => {
    setSelectedIds(prev => {
      if (allFilteredSelected) {
        setSelectedSchedules(current => {
          const next = { ...current };
          filteredIds.forEach(id => delete next[id]);
          return next;
        });
        return prev.filter(id => !filteredIds.includes(id));
      }
      const missingIds = filteredIds.filter(id => !prev.includes(id));
      setSelectedSchedules(current => {
        const next = { ...current };
        missingIds.forEach((id, index) => {
          next[id] = current[id] || createDefaultSchedule(prev.length + index);
        });
        return next;
      });
      return Array.from(new Set([...prev, ...filteredIds]));
    });
  };

  const handleSinifTopluSec = (sinifId) => {
    const classIds = ogrenciler
      .filter((ogrenci) => String(getOgrenciSinifId(ogrenci)) === String(sinifId))
      .map((ogrenci) => String(ogrenci.id));
    setSinifFilter(String(sinifId));
    setSelectedIds(prev => {
      const missingIds = classIds.filter(id => !prev.includes(id));
      setSelectedSchedules(current => {
        const next = { ...current };
        missingIds.forEach((id, index) => {
          next[id] = current[id] || createDefaultSchedule(prev.length + index);
        });
        return next;
      });
      return Array.from(new Set([...prev, ...classIds]));
    });
  };

  const updateSelectedSchedule = (id, field, value) => {
    const strId = String(id);
    setSelectedSchedules(prev => ({
      ...prev,
      [strId]: {
        ...(prev[strId] || createDefaultSchedule(0)),
        [field]: field === 'sure_dakika' && value !== '' ? Number(value) : value,
      },
    }));
  };

  const applySequentialTimes = () => {
    setSelectedSchedules(prev => {
      const next = { ...prev };
      selectedIds.forEach((id, index) => {
        next[id] = createDefaultSchedule(index);
      });
      return next;
    });
  };

  const listSessionItems = selectedOgrenciler.map((ogrenci, index) => {
    const id = String(ogrenci.id);
    const schedule = selectedSchedules[id] || createDefaultSchedule(index);
    return {
      ogrenci_id: Number(ogrenci.id),
      tarih: schedule.tarih || form.tarih,
      saat: schedule.saat || form.saat,
      sure_dakika: Number(schedule.sure_dakika || form.sure_dakika),
      konu: form.konu.trim(),
      not: form.not.trim(),
    };
  });

  const weeklySessionItems = weeklySessions.map((item) => ({
    ogrenci_id: Number(item.ogrenci_id),
    tarih: item.tarih,
    saat: item.saat,
    sure_dakika: Number(item.sure_dakika || form.sure_dakika),
    konu: form.konu.trim(),
    not: form.not.trim(),
  }));

  const sessionItems = mode === 'haftalik' ? weeklySessionItems : listSessionItems;
  const activeCount = mode === 'haftalik' ? weeklySessions.length : selectedIds.length;

  const buildPayload = () => ({
    tarih: sessionItems[0]?.tarih || form.tarih,
    saat: sessionItems[0]?.saat || form.saat,
    sure_dakika: Number(sessionItems[0]?.sure_dakika || form.sure_dakika || 30),
    konu: form.konu.trim(),
    not: form.not.trim(),
    ogrenci_ids: sessionItems.map(item => Number(item.ogrenci_id)),
    seanslar: sessionItems,
    notify_student: form.notify_student,
    notify_parent: form.notify_parent,
    channels: ['whatsapp'],
  });

  const appendLocalSeanslar = (source = {}) => {
    const createdAt = Date.now();
    const sourceStudents = mode === 'haftalik'
      ? weeklySessions.map((item) => ({
          id: item.ogrenci_id,
          adSoyad: item.ogrenci_ad_soyad,
          sinifAd: item.sinif_ad,
        }))
      : selectedOgrenciler.map((ogrenci) => ({
          id: ogrenci.id,
          adSoyad: getOgrenciAdSoyad(ogrenci),
          sinifAd: getOgrenciSinifAdi(ogrenci),
        }));
    const newRows = sourceStudents.map((ogrenci, index) => ({
      id: source.ids?.[index] || `local-${createdAt}-${ogrenci.id}-${index}`,
      ogrenci_id: ogrenci.id,
      ogrenci_ad_soyad: ogrenci.adSoyad,
      sinif_ad: ogrenci.sinifAd,
      tarih: sessionItems[index]?.tarih || form.tarih,
      saat: sessionItems[index]?.saat || form.saat,
      sure_dakika: Number(sessionItems[index]?.sure_dakika || form.sure_dakika),
      konu: form.konu.trim(),
      durum: 'planlandi',
      bildirim: source.remote ? 'WhatsApp kuyruğuna alındı' : 'Frontend önizleme',
    }));
    setSeanslar(prev => [...newRows, ...prev]);
  };

  const handleCreateSessions = async () => {
    if (!activeCount) return toast.error('En az bir seans ekleyin');
    if (!form.konu.trim()) return toast.error('Görüşme konusu zorunlu');
    if (sessionItems.some(item => !Number.isFinite(Number(item.sure_dakika)) || Number(item.sure_dakika) < 5)) {
      return toast.error('Seans süresini 5 dakika veya daha uzun bir değer olarak girin.');
    }

    const payload = buildPayload();
    setSaving(true);
    try {
      const { data } = await axios.post('/rehberlik/seanslar', payload);
      appendLocalSeanslar({ remote: true, ids: data?.data?.ids });
      toast.success(data?.message || `${activeCount} seans oluşturuldu`);
      setSelectedIds([]);
      setSelectedSchedules({});
      if (mode === 'haftalik') setWeeklySessions([]);
      setSummaryOpen(true);
    } catch (err) {
      appendLocalSeanslar({ remote: false });
      toast.success('Seanslar frontend önizleme olarak oluşturuldu');
      toast.error('Backend hazır olunca WhatsApp gönderimi otomatik bağlanacak');
      setSummaryOpen(true);
    } finally {
      setSaving(false);
    }
  };

  const openPlannerCell = (date, rowIndex) => {
    const cellKey = `${date}__row-${rowIndex}`;
    const existing = weeklySessions.find(item => item.cellKey === cellKey);
    setPlannerCell({
      date,
      rowIndex,
      cellKey,
      time: existing?.saat || addMinutesToTime('09:00', (rowIndex - 1) * Number(form.sure_dakika || 30)),
      ogrenci_id: existing?.ogrenci_id ? String(existing.ogrenci_id) : '',
      sure_dakika: Number(existing?.sure_dakika || form.sure_dakika || 30),
    });
  };

  const handlePlannerSave = () => {
    if (!plannerCell?.ogrenci_id) return toast.error('Öğrenci seçin');
    const ogrenci = ogrenciler.find(item => String(item.id) === String(plannerCell.ogrenci_id));
    if (!ogrenci) return toast.error('Öğrenci bulunamadı');
    const cellKey = plannerCell.cellKey || `${plannerCell.date}__row-${plannerCell.rowIndex || 0}`;
    setWeeklySessions(prev => [
      ...prev.filter(item => item.cellKey !== cellKey),
      {
        id: `week-${cellKey}-${plannerCell.ogrenci_id}`,
        cellKey,
        ogrenci_id: Number(plannerCell.ogrenci_id),
        ogrenci_ad_soyad: getOgrenciAdSoyad(ogrenci),
        sinif_ad: getOgrenciSinifAdi(ogrenci),
        tarih: plannerCell.date,
        saat: plannerCell.time,
        sure_dakika: Number(plannerCell.sure_dakika || form.sure_dakika),
      },
    ]);
    setPlannerCell(null);
  };

  const handleStudentDragStart = (event, ogrenci) => {
    event.dataTransfer.setData('text/plain', String(ogrenci.id));
    event.dataTransfer.effectAllowed = 'copy';
  };

  const handleCalendarDrop = (event, date, rowIndex) => {
    event.preventDefault();
    const ogrenciId = event.dataTransfer.getData('text/plain');
    if (!ogrenciId) return;
    const ogrenci = ogrenciler.find(item => String(item.id) === String(ogrenciId));
    if (!ogrenci) return;
    const cellKey = `${date}__row-${rowIndex}`;
    const defaultTime = addMinutesToTime('09:00', (rowIndex - 1) * Number(form.sure_dakika || 30));
    setWeeklySessions(prev => [
      ...prev.filter(item => item.cellKey !== cellKey),
      {
        id: `week-${cellKey}-${ogrenciId}`,
        cellKey,
        ogrenci_id: Number(ogrenciId),
        ogrenci_ad_soyad: getOgrenciAdSoyad(ogrenci),
        sinif_ad: getOgrenciSinifAdi(ogrenci),
        tarih: date,
        saat: defaultTime,
        sure_dakika: Number(form.sure_dakika || 30),
      },
    ]);
    setPlannerCell({
      date,
      rowIndex,
      cellKey,
      time: defaultTime,
      ogrenci_id: ogrenciId,
      sure_dakika: Number(form.sure_dakika || 30),
    });
  };

  const removeWeeklySession = (cellKey) => {
    setWeeklySessions(prev => prev.filter(item => item.cellKey !== cellKey));
  };

  const handleGelmedi = async (seans) => {
    try {
      if (!String(seans.id).startsWith('local-')) {
        await axios.put(`/rehberlik/seanslar/${seans.id}/durum`, { durum: 'gelmedi', notify_parent: true });
      }
      setSeanslar(prev => prev.map(item => item.id === seans.id ? { ...item, durum: 'gelmedi', bildirim: 'Veliye gelmedi mesajı gönderilecek' } : item));
      toast.success('Gelmedi olarak işaretlendi, veli WhatsApp bildirimi tetiklendi');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Durum güncellenemedi');
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <style>{`
        .seans-layout {
          display: grid;
          grid-template-columns: minmax(0, 0.95fr) minmax(420px, 1.05fr);
          gap: 16px;
          align-items: start;
        }
        .seans-filter-grid {
          display: grid;
          grid-template-columns: minmax(180px, 1fr) minmax(150px, 210px);
          gap: 10px;
          align-items: end;
        }
        .seans-student-list {
          border: 1px solid var(--border);
          border-radius: 12px;
          overflow: hidden;
          background: var(--bg-card);
          max-height: 520px;
          overflow-y: auto;
        }
        .seans-student-row {
          display: grid;
          grid-template-columns: 30px minmax(0, 1fr) auto;
          align-items: center;
          gap: 8px;
          padding: 10px 12px;
          border-bottom: 1px solid var(--border);
          cursor: pointer;
        }
        .seans-student-row:last-child { border-bottom: none; }
        .seans-student-row:hover { background: var(--bg-card-hover); }
        .seans-student-row.selected { background: rgba(37,99,235,0.07); }
        .seans-check {
          width: 20px;
          height: 20px;
          border-radius: 6px;
          border: 1.5px solid var(--border-strong);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.78rem;
          color: white;
          background: white;
        }
        .seans-student-row.selected .seans-check {
          border-color: var(--blue);
          background: var(--blue);
        }
        .seans-side {
          position: sticky;
          top: 88px;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .seans-selected-list {
          border: 1px solid var(--border);
          border-radius: 12px;
          overflow: hidden;
          background: var(--bg-card);
        }
        .seans-selected-row {
          display: grid;
          grid-template-columns: minmax(150px, 1fr) 140px 112px 96px 32px;
          gap: 8px;
          align-items: end;
          padding: 10px 12px;
          border-bottom: 1px solid var(--border);
        }
        .seans-selected-row:last-child { border-bottom: none; }
        .seans-selected-name {
          min-width: 0;
          padding-bottom: 4px;
        }
        .seans-week-shell {
          display: grid;
          grid-template-columns: minmax(260px, 0.3fr) minmax(620px, 1fr) 320px;
          gap: 14px;
          align-items: start;
        }
        .seans-pool-card {
          position: sticky;
          top: 88px;
          max-height: calc(100vh - 120px);
          display: flex;
          flex-direction: column;
        }
        .seans-pool-list {
          overflow-y: auto;
          display: flex;
          flex-direction: column;
          gap: 8px;
          padding-right: 4px;
        }
        .seans-pool-item {
          border: 1px solid var(--border);
          border-radius: 10px;
          padding: 10px 12px;
          background: white;
          cursor: grab;
          transition: all var(--dur-normal) var(--ease);
        }
        .seans-pool-item:hover {
          border-color: var(--blue-border);
          box-shadow: var(--shadow-sm);
          transform: translateY(-1px);
        }
        .seans-pool-item:active {
          cursor: grabbing;
        }
        .seans-week-table-wrap {
          overflow: auto;
          border: 1px solid var(--border);
          border-radius: 12px;
          background: var(--bg-card);
        }
        .seans-week-table {
          width: 100%;
          min-width: 760px;
          border-collapse: collapse;
        }
        .seans-week-table th,
        .seans-week-table td {
          border-right: 1px solid var(--border);
          border-bottom: 1px solid var(--border);
        }
        .seans-week-table th {
          position: sticky;
          top: 0;
          z-index: 2;
          background: var(--bg-elevated);
          padding: 9px 10px;
          font-size: 0.76rem;
          color: var(--text-secondary);
          text-align: center;
        }
        .seans-week-cell {
          min-height: 74px;
          width: 100%;
          border: none;
          background: white;
          padding: 8px;
          text-align: left;
          cursor: pointer;
          display: flex;
          flex-direction: column;
          gap: 3px;
        }
        .seans-week-cell:hover {
          background: rgba(37,99,235,0.06);
          box-shadow: inset 0 0 0 2px rgba(37,99,235,0.18);
        }
        .seans-week-cell.drop-ready {
          outline: 2px dashed rgba(37,99,235,0.28);
          outline-offset: -5px;
        }
        .seans-week-cell.filled {
          background: rgba(16,185,129,0.08);
        }
        .seans-week-student {
          font-size: 0.78rem;
          font-weight: 800;
          color: var(--text-primary);
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .seans-week-meta {
          font-size: 0.68rem;
          color: var(--text-muted);
        }
        .seans-chip-row {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-top: 8px;
        }
        .seans-chip {
          border: 1px solid var(--border-strong);
          background: white;
          color: var(--text-secondary);
          border-radius: 999px;
          padding: 6px 10px;
          font-size: 0.78rem;
          font-weight: 800;
          cursor: pointer;
        }
        .seans-chip.active {
          border-color: var(--blue);
          background: var(--blue-dim);
          color: var(--blue);
        }
        .seans-switch-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 14px;
          padding: 10px 0;
          border-top: 1px solid var(--border);
          cursor: pointer;
        }
        .seans-switch {
          width: 44px;
          height: 24px;
          border-radius: 999px;
          background: #CBD5E1;
          padding: 3px;
          transition: background var(--dur-normal);
          flex-shrink: 0;
        }
        .seans-switch span {
          display: block;
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: white;
          box-shadow: var(--shadow-sm);
          transition: transform var(--dur-normal);
        }
        .seans-switch.on {
          background: var(--blue);
        }
        .seans-switch.on span {
          transform: translateX(20px);
        }
        .seans-drawer {
          position: fixed;
          top: 0;
          right: 0;
          width: min(420px, 94vw);
          height: 100vh;
          background: white;
          z-index: 230;
          box-shadow: -12px 0 32px rgba(15,30,61,0.18);
          padding: 20px;
          overflow-y: auto;
          animation: slideInRight 0.22s var(--ease) both;
        }
        .seans-drawer-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(15,30,61,0.28);
          z-index: 220;
        }
        @media (max-width: 1050px) {
          .seans-layout { grid-template-columns: 1fr; }
          .seans-week-shell { grid-template-columns: 1fr; }
          .seans-side,
          .seans-pool-card { position: static; max-height: none; }
        }
        @media (max-width: 820px) {
          .seans-selected-row {
            grid-template-columns: 1fr 1fr;
            align-items: start;
          }
          .seans-selected-name { grid-column: 1 / -1; }
        }
        @media (max-width: 680px) {
          .seans-filter-grid { grid-template-columns: 1fr; }
          .seans-student-row { grid-template-columns: 28px minmax(0, 1fr); }
          .seans-student-row .badge { grid-column: 2; width: fit-content; }
        }
      `}</style>

      <div className="section-header animate-fadeInUp" style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ fontFamily: 'var(--font-ui)', fontSize: '1.25rem' }}>📅 Öğrenci Seansı Oluştur</h2>
          <div className="section-subtitle">Öğrencileri havuzdan takvime sürükleyin veya toplu önizleme hazırlayın.</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span className="badge badge-info">{activeCount} seçili</span>
          <button className="btn btn-outline btn-sm" onClick={() => { setSelectedIds([]); setSelectedSchedules({}); setWeeklySessions([]); setSinifFilter(''); setArama(''); }}>Seçimi Temizle</button>
        </div>
      </div>

      <div className="tabs animate-fadeInUp stagger-1">
        <button className={`tab ${activeTab === 'planlama' ? 'active' : ''}`} onClick={() => setActiveTab('planlama')}>Yeni Planlama</button>
        <button className={`tab ${activeTab === 'seanslar' ? 'active' : ''}`} onClick={() => setActiveTab('seanslar')}>Geçmiş/Gelecek Seanslar</button>
      </div>

      {activeTab === 'planlama' && (
        <div className="tabs animate-fadeInUp stagger-1">
          <button className={`tab ${mode === 'haftalik' ? 'active' : ''}`} onClick={() => setMode('haftalik')}>Sürükle-Bırak Takvim</button>
          <button className={`tab ${mode === 'liste' ? 'active' : ''}`} onClick={() => setMode('liste')}>Toplu Önizleme</button>
        </div>
      )}

      {activeTab === 'planlama' && mode === 'liste' && (
      <div className="seans-layout animate-fadeInUp stagger-2">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="card">
            <div className="seans-filter-grid">
              <div className="form-group">
                <label className="form-label">Öğrenci Ara</label>
                <input className="form-input" placeholder="Ad, soyad, numara veya sınıf" value={arama} onChange={(e) => setArama(e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Sınıf Filtresi</label>
                <select className="form-input" value={sinifFilter} onChange={(e) => setSinifFilter(e.target.value)}>
                  <option value="">Tüm sınıflar</option>
                  {siniflar.map((sinif) => <option key={sinif.id} value={sinif.id}>{sinif.ad || sinif.name}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
              <button className="btn btn-blue" onClick={toggleFilteredSelection} disabled={!filteredIds.length}>
                {allFilteredSelected ? 'Filtredekileri Kaldır' : 'Filtredekileri Seç'}
              </button>
              {sinifFilter && (
                <button className="btn btn-outline" onClick={() => handleSinifTopluSec(sinifFilter)}>
                  Bu Sınıfı Seç
                </button>
              )}
            </div>
          </div>

          <div className="seans-student-list">
            {loading ? (
              Array(5).fill(0).map((_, index) => <div key={index} className="skeleton" style={{ height: 58, borderRadius: 0 }} />)
            ) : filteredOgrenciler.length === 0 ? (
              <div className="empty-state" style={{ padding: 34 }}>Öğrenci bulunamadı.</div>
            ) : filteredOgrenciler.map((ogrenci) => {
              const selected = selectedIds.includes(String(ogrenci.id));
              return (
                <div key={ogrenci.id} className={`seans-student-row ${selected ? 'selected' : ''}`} onClick={() => toggleStudent(ogrenci.id)}>
                  <span className="seans-check">{selected ? '✓' : ''}</span>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 800, color: 'var(--text-primary)' }}>{getOgrenciAdSoyad(ogrenci)}</div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>No: {ogrenci.ogrenci_no || ogrenci.numara || '-'} · {getOgrenciSinifAdi(ogrenci)}</div>
                  </div>
                  <span className="badge badge-muted">{getOgrenciSinifAdi(ogrenci)}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="seans-side">
          <div className="card">
            <h3 style={{ fontSize: '1rem', marginBottom: 12 }}>Tarih / Saat Ayarları</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 110px', gap: 10, marginBottom: 12 }}>
              <div className="form-group">
                <label className="form-label">Tarih</label>
                <input className="form-input" type="date" value={form.tarih} onChange={(e) => setForm({ ...form, tarih: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Saat</label>
                <input className="form-input" type="time" value={form.saat} onChange={(e) => setForm({ ...form, saat: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Süre</label>
                <SeansDurationInput id="seans-duration-list" value={form.sure_dakika} onChange={sure_dakika => setForm(current => ({ ...current, sure_dakika }))} />
              </div>
            </div>
            <button className="btn btn-outline btn-sm" style={{ marginBottom: 12 }} onClick={applySequentialTimes} disabled={!selectedIds.length}>
              Seçili Öğrencilere Sırayla Saat Ata
            </button>
          </div>

          <div className="card">
            <h3 style={{ fontSize: '1rem', marginBottom: 12 }}>Görüşme Detayları</h3>
            <div className="form-group" style={{ marginBottom: 10 }}>
              <label className="form-label">Konu</label>
              <input className="form-input" value={form.konu} onChange={(e) => setForm({ ...form, konu: e.target.value })} />
              <div className="seans-chip-row">
                {SEANS_KONULARI.map((konu) => (
                  <button key={konu} className={`seans-chip ${form.konu === konu ? 'active' : ''}`} onClick={() => setForm({ ...form, konu })}>{konu}</button>
                ))}
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Not</label>
              <textarea className="form-textarea" style={{ minHeight: 76 }} placeholder="Görüşme ön notu" value={form.not} onChange={(e) => setForm({ ...form, not: e.target.value })} />
            </div>
          </div>

          <div className="card">
            <h3 style={{ fontSize: '1rem', marginBottom: 8 }}>Bildirim Ayarları</h3>
            <label className="seans-switch-row">
              <span>Öğrenciye WhatsApp bildirimi gönder</span>
              <input type="checkbox" checked={form.notify_student} onChange={(e) => setForm({ ...form, notify_student: e.target.checked })} style={{ display: 'none' }} />
              <span className={`seans-switch ${form.notify_student ? 'on' : ''}`}><span /></span>
            </label>
            <label className="seans-switch-row">
              <span>Veliye WhatsApp bildirimi gönder</span>
              <input type="checkbox" checked={form.notify_parent} onChange={(e) => setForm({ ...form, notify_parent: e.target.checked })} style={{ display: 'none' }} />
              <span className={`seans-switch ${form.notify_parent ? 'on' : ''}`}><span /></span>
            </label>
          </div>

          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' }}>
              <h3 style={{ fontSize: '1rem' }}>Önizleme</h3>
              <span className="badge badge-muted">{selectedIds.length}</span>
            </div>
            {selectedOgrenciler.length === 0 ? (
              <div className="empty-state" style={{ padding: 28 }}>Öğrenci seçin, sırayla saat atayın ve oluşacak listeyi kontrol edin.</div>
            ) : (
              <div className="seans-selected-list" style={{ border: 'none', borderRadius: 0 }}>
                {selectedOgrenciler.map((ogrenci, index) => {
                  const schedule = selectedSchedules[String(ogrenci.id)] || createDefaultSchedule(index);
                  return (
                    <div className="seans-selected-row" key={ogrenci.id}>
                      <div className="seans-selected-name">
                        <div style={{ fontWeight: 800, color: 'var(--text-primary)' }}>{getOgrenciAdSoyad(ogrenci)}</div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{getOgrenciSinifAdi(ogrenci)}</div>
                      </div>
                      <div className="form-group">
                        <label className="form-label">Tarih</label>
                        <input className="form-input" type="date" value={schedule.tarih} onChange={(e) => updateSelectedSchedule(ogrenci.id, 'tarih', e.target.value)} />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Saat</label>
                        <input className="form-input" type="time" value={schedule.saat} onChange={(e) => updateSelectedSchedule(ogrenci.id, 'saat', e.target.value)} />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Süre</label>
                        <SeansDurationInput id={`seans-duration-student-${ogrenci.id}`} value={schedule.sure_dakika} onChange={value => updateSelectedSchedule(ogrenci.id, 'sure_dakika', value)} />
                      </div>
                      <button className="btn btn-ghost btn-icon" title="Çıkar" onClick={() => toggleStudent(ogrenci.id)}>✕</button>
                    </div>
                  );
                })}
              </div>
            )}
            <div style={{ padding: 14, borderTop: '1px solid var(--border)' }}>
              <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }} disabled={saving || !selectedIds.length} onClick={handleCreateSessions}>
                {saving ? 'Oluşturuluyor...' : 'Seansları Onayla ve Oluştur'}
              </button>
            </div>
          </div>
        </div>
      </div>
      )}

      {activeTab === 'planlama' && mode === 'haftalik' && (
        <div className="seans-week-shell animate-fadeInUp stagger-2">
          <div className="card seans-pool-card">
            <div style={{ marginBottom: 12 }}>
              <h3 style={{ fontSize: '1rem' }}>Öğrenci Havuzu</h3>
              <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>Kartı takvimdeki saate sürükleyin.</div>
            </div>
            <div className="form-group" style={{ marginBottom: 10 }}>
              <label className="form-label">Ara</label>
              <input className="form-input" placeholder="Ad, soyad, no" value={arama} onChange={(e) => setArama(e.target.value)} />
            </div>
            <div className="form-group" style={{ marginBottom: 12 }}>
              <label className="form-label">Sınıf</label>
              <select className="form-input" value={sinifFilter} onChange={(e) => setSinifFilter(e.target.value)}>
                <option value="">Tüm sınıflar</option>
                {siniflar.map((sinif) => <option key={sinif.id} value={sinif.id}>{sinif.ad || sinif.name}</option>)}
              </select>
            </div>
            <div className="seans-pool-list">
              {loading ? (
                Array(6).fill(0).map((_, index) => <div key={index} className="skeleton" style={{ height: 58 }} />)
              ) : filteredOgrenciler.length === 0 ? (
                <div className="empty-state" style={{ padding: 20 }}>Öğrenci bulunamadı.</div>
              ) : filteredOgrenciler.map((ogrenci) => (
                <div
                  key={ogrenci.id}
                  className="seans-pool-item"
                  draggable
                  onDragStart={(event) => handleStudentDragStart(event, ogrenci)}
                >
                  <div style={{ fontWeight: 800, color: 'var(--text-primary)' }}>{getOgrenciAdSoyad(ogrenci)}</div>
                  <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)' }}>No: {ogrenci.ogrenci_no || ogrenci.numara || '-'} · {getOgrenciSinifAdi(ogrenci)}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <div>
                <h3 style={{ fontSize: '1rem' }}>Haftalık Plan</h3>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>Öğrenci kartını hücreye bırakın veya hücreye tıklayın.</div>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input className="form-input" style={{ width: 160 }} type="date" value={weekStart} onChange={(e) => setWeekStart(getWeekStartIso(e.target.value))} />
                <button className="btn btn-outline btn-sm" onClick={() => setWeeklySessions([])}>Planı Temizle</button>
              </div>
            </div>
            <div className="seans-week-table-wrap">
              <table className="seans-week-table">
                <thead>
                  <tr>
                    {weekDates.map((day) => (
                      <th key={day.date}>{day.label}<div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: 2 }}>{formatShortDate(day.date)}</div></th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {EXCEL_PLAN_ROWS.map((rowIndex) => (
                    <tr key={rowIndex}>
                      {weekDates.map((day) => {
                        const cellKey = `${day.date}__row-${rowIndex}`;
                        const session = weeklySessions.find(item => item.cellKey === cellKey);
                        return (
                          <td key={cellKey}>
                            <button
                              className={`seans-week-cell drop-ready ${session ? 'filled' : ''}`}
                              onDragOver={(event) => event.preventDefault()}
                              onDrop={(event) => handleCalendarDrop(event, day.date, rowIndex)}
                              onClick={() => openPlannerCell(day.date, rowIndex)}
                            >
                              {session ? (
                                <>
                                  <span className="seans-week-student">{session.ogrenci_ad_soyad}</span>
                                  <span className="seans-week-meta">{session.saat} · {session.sinif_ad} · {session.sure_dakika} dk</span>
                                </>
                              ) : (
                                <span className="seans-week-meta">Boş hücre</span>
                              )}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="seans-side">
            <div className="card">
              <h3 style={{ fontSize: '1rem', marginBottom: 12 }}>Görüşme Detayları</h3>
              <div className="form-group" style={{ marginBottom: 10 }}>
                <label className="form-label">Konu</label>
                <input className="form-input" value={form.konu} onChange={(e) => setForm({ ...form, konu: e.target.value })} />
                <div className="seans-chip-row">
                  {SEANS_KONULARI.map((konu) => (
                    <button key={konu} className={`seans-chip ${form.konu === konu ? 'active' : ''}`} onClick={() => setForm({ ...form, konu })}>{konu}</button>
                  ))}
                </div>
              </div>
              <div className="form-group" style={{ marginBottom: 12 }}>
                <label className="form-label">Not</label>
                <textarea className="form-textarea" style={{ minHeight: 70 }} placeholder="Görüşme ön notu" value={form.not} onChange={(e) => setForm({ ...form, not: e.target.value })} />
              </div>
            </div>

            <div className="card">
              <h3 style={{ fontSize: '1rem', marginBottom: 12 }}>Takvim Ayarları</h3>
              <div className="form-group" style={{ marginBottom: 12 }}>
                <label className="form-label">Varsayılan Süre</label>
                <SeansDurationInput id="seans-duration-weekly" value={form.sure_dakika} onChange={sure_dakika => setForm(current => ({ ...current, sure_dakika }))} />
              </div>
              <label className="seans-switch-row">
                <span>Öğrenciye WhatsApp bildirimi gönder</span>
                <input type="checkbox" checked={form.notify_student} onChange={(e) => setForm({ ...form, notify_student: e.target.checked })} style={{ display: 'none' }} />
                <span className={`seans-switch ${form.notify_student ? 'on' : ''}`}><span /></span>
              </label>
              <label className="seans-switch-row">
                <span>Veliye WhatsApp bildirimi gönder</span>
                <input type="checkbox" checked={form.notify_parent} onChange={(e) => setForm({ ...form, notify_parent: e.target.checked })} style={{ display: 'none' }} />
                <span className={`seans-switch ${form.notify_parent ? 'on' : ''}`}><span /></span>
              </label>
              <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }} disabled={saving || !weeklySessions.length} onClick={handleCreateSessions}>
                {saving ? 'Oluşturuluyor...' : 'Haftalık Planı Oluştur'}
              </button>
            </div>

            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ fontSize: '1rem' }}>Planlananlar</h3>
                <span className="badge badge-muted">{weeklySessions.length}</span>
              </div>
              {weeklySessions.length === 0 ? (
                <div className="empty-state" style={{ padding: 24 }}>Tablodan hücre seçin.</div>
              ) : weeklySessions
                .slice()
                .sort((a, b) => `${a.tarih} ${a.saat}`.localeCompare(`${b.tarih} ${b.saat}`))
                .map((item) => (
                  <div key={item.cellKey} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 10, padding: '10px 14px', borderBottom: '1px solid var(--border)', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontWeight: 800 }}>{item.ogrenci_ad_soyad}</div>
                      <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)' }}>{formatSeansDateTime(item.tarih, item.saat)} · {item.sure_dakika} dk</div>
                    </div>
                    <button className="btn btn-ghost btn-icon" title="Çıkar" onClick={() => removeWeeklySession(item.cellKey)}>✕</button>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

      {plannerCell && (
        <div className="modal-backdrop" onClick={() => setPlannerCell(null)}>
          <div className="modal" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <div className="modal-title">Hücreyi Düzenle</div>
                <div className="modal-subtitle" style={{ marginBottom: 0 }}>
                  {formatSeansDateTime(plannerCell.date, plannerCell.time)}
                </div>
              </div>
              <button className="modal-close" onClick={() => setPlannerCell(null)}>✕</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div className="form-group">
                <label className="form-label">Sınıf</label>
                <select className="form-input" value={sinifFilter} onChange={(e) => setSinifFilter(e.target.value)}>
                  <option value="">Tüm sınıflar</option>
                  {siniflar.map((sinif) => <option key={sinif.id} value={sinif.id}>{sinif.ad || sinif.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Öğrenci</label>
                <select
                  className="form-input"
                  value={plannerCell.ogrenci_id}
                  onChange={(e) => setPlannerCell(prev => ({ ...prev, ogrenci_id: e.target.value }))}
                >
                  <option value="">Öğrenci seçin</option>
                  {filteredOgrenciler.map((ogrenci) => (
                    <option key={ogrenci.id} value={ogrenci.id}>
                      {getOgrenciAdSoyad(ogrenci)} - {getOgrenciSinifAdi(ogrenci)}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div className="form-group">
                  <label className="form-label">Saat</label>
                  <input className="form-input" type="time" value={plannerCell.time} onChange={(e) => setPlannerCell(prev => ({ ...prev, time: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Süre</label>
                  <SeansDurationInput id="seans-duration-planner" value={plannerCell.sure_dakika} onChange={sure_dakika => setPlannerCell(prev => ({ ...prev, sure_dakika }))} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button className="btn btn-primary" style={{ flex: 1, justifyContent: 'center' }} onClick={handlePlannerSave}>Kaydet</button>
                <button className="btn btn-outline" onClick={() => setPlannerCell(null)}>İptal</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {summaryOpen && (
        <>
          <div className="seans-drawer-backdrop" onClick={() => setSummaryOpen(false)} />
          <aside className="seans-drawer">
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', marginBottom: 16 }}>
              <div>
                <h3 style={{ fontSize: '1.05rem' }}>Son Oluşturulan Seanslar</h3>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{seanslar.length} kayıt</div>
              </div>
              <button className="btn btn-ghost btn-icon" onClick={() => setSummaryOpen(false)}>✕</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {seanslar.slice(0, 10).map((seans) => (
                <div key={seans.id} style={{ border: '1px solid var(--border)', borderRadius: 12, padding: 12 }}>
                  <div style={{ fontWeight: 800 }}>{seans.ogrenci_ad_soyad}</div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{formatSeansDateTime(seans.tarih, seans.saat)} · {seans.sure_dakika} dk</div>
                  <div style={{ fontSize: '0.78rem', marginTop: 4 }}>{seans.konu}</div>
                </div>
              ))}
            </div>
          </aside>
        </>
      )}

      {activeTab === 'seanslar' && (
      <div className="card animate-fadeInUp stagger-3" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '18px 20px', borderBottom: '1px solid var(--border)' }}>
          <h3 style={{ fontSize: '1rem' }}>Oluşturulan Seanslar</h3>
        </div>
        <div className="table-wrapper" style={{ border: 'none', borderRadius: 0, boxShadow: 'none' }}>
          <table className="table">
            <thead>
              <tr>
                <th>Öğrenci</th>
                <th>Sınıf</th>
                <th>Tarih / Saat</th>
                <th>Konu</th>
                <th>Durum</th>
                <th style={{ textAlign: 'right' }}>İşlem</th>
              </tr>
            </thead>
            <tbody>
              {seanslar.length === 0 ? (
                <tr><td colSpan="6" className="empty-state" style={{ padding: 30, textAlign: 'center' }}>Henüz seans oluşturulmadı.</td></tr>
              ) : seanslar.map((seans) => (
                <tr key={seans.id}>
                  <td style={{ fontWeight: 800, color: 'var(--text-primary)' }}>{seans.ogrenci_ad_soyad}</td>
                  <td>{seans.sinif_ad}</td>
                  <td>{formatSeansDateTime(seans.tarih, seans.saat)} · {seans.sure_dakika} dk</td>
                  <td>{seans.konu}</td>
                  <td>
                    <span className={`badge ${seans.durum === 'gelmedi' ? 'badge-danger' : 'badge-success'}`}>
                      {seans.durum === 'gelmedi' ? 'Seansa Gelmedi' : 'Planlandı'}
                    </span>
                    <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 4 }}>{seans.bildirim}</div>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {seans.durum !== 'gelmedi' && (
                      <button className="btn btn-danger btn-sm" onClick={() => handleGelmedi(seans)}>Seansa Gelmedi</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      )}
    </div>
  );
}
// ── Görüşme Günlüğü ───────────────────────────────────────────
export function GorusmelerPage() {
  const [gorusmeler, setGorusmeler] = useState([]);
  const [ogrenciler, setOgrenciler] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({
    ogrenci_id: '', tarih: new Date().toISOString().slice(0, 10),
    konu: '', notlar: '', sonraki_adimlar: '', durum: 'planli',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [gRes, oRes] = await Promise.all([
        axios.get('/rehberlik/gorusmeler'),
        axios.get('/ogrenciler?limit=200'),
      ]);
      if (gRes.data.success) setGorusmeler(gRes.data.data);
      if (oRes.data.success) setOgrenciler(oRes.data.data);
    } catch { toast.error('Yükleme hatası'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!form.ogrenci_id || !form.konu) { toast.error('Öğrenci ve konu zorunlu'); return; }
    try {
      await axios.post('/rehberlik/gorusmeler', form);
      toast.success('Görüşme kaydedildi!');
      setShowModal(false);
      setForm({ ogrenci_id: '', tarih: new Date().toISOString().slice(0, 10), konu: '', notlar: '', sonraki_adimlar: '', durum: 'planli' });
      load();
    } catch { toast.error('Kayıt hatası'); }
  };

  const DURUM_COLORS = { planli: '#3B82F6', tamamlandi: '#10B981', iptal: '#EF4444' };
  const DURUM_LABELS = { planli: '📅 Planlı', tamamlandi: '✅ Tamamlandı', iptal: '❌ İptal' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div className="section-header animate-fadeInUp">
        <div>
          <h2 style={{ fontFamily: 'var(--font-ui)', fontSize: '1.3rem' }}>💬 Görüşme Günlüğü</h2>
          <div className="section-subtitle">{gorusmeler.length} görüşme kaydı</div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>+ Yeni Görüşme</button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} className="animate-fadeInUp stagger-1">
        {loading ? (
          Array(4).fill(0).map((_, i) => (
            <div key={i} className="skeleton" style={{ height: 100, borderRadius: 16 }} />
          ))
        ) : gorusmeler.length === 0 ? (
          <div className="card">
            <div className="empty-state">
              <div style={{ fontSize: '2.5rem' }}>💬</div>
              <h3>Görüşme kaydı yok</h3>
              <p>Yeni görüşme ekleyerek başlayın.</p>
            </div>
          </div>
        ) : gorusmeler.map((g, i) => (
          <div key={i} className="card" style={{ borderLeft: `3px solid ${DURUM_COLORS[g.durum] || '#94A3B8'}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div className="avatar avatar-sm" style={{ background: 'rgba(139,92,246,0.15)', color: '#8B5CF6' }}>
                  {g.ogrenci_ad?.[0]}{g.ogrenci_soyad?.[0]}
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>{g.ogrenci_ad} {g.ogrenci_soyad}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                    {new Date(g.tarih).toLocaleDateString('tr-TR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                  </div>
                </div>
              </div>
              <span style={{
                background: DURUM_COLORS[g.durum] + '20',
                color: DURUM_COLORS[g.durum],
                padding: '3px 10px', borderRadius: 99,
                fontSize: '0.75rem', fontWeight: 600,
              }}>
                {DURUM_LABELS[g.durum]}
              </span>
            </div>
            <div style={{ marginBottom: 8 }}>
              <span style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-primary)' }}>Konu: </span>
              <span style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>{g.konu}</span>
            </div>
            {g.notlar && (
              <div style={{ padding: '8px 12px', background: 'var(--bg-elevated)', borderRadius: 8, fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 8 }}>
                📝 {g.notlar}
              </div>
            )}
            {g.sonraki_adimlar && (
              <div style={{ padding: '8px 12px', background: 'rgba(16,185,129,0.06)', borderRadius: 8, border: '1px solid rgba(16,185,129,0.15)', fontSize: '0.8rem', color: '#10B981' }}>
                → Sonraki adımlar: {g.sonraki_adimlar}
              </div>
            )}
          </div>
        ))}
      </div>

      {showModal && (
        <div className="modal-backdrop" onClick={() => setShowModal(false)}>
          <div className="modal" style={{ maxWidth: 580 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">💬 Yeni Görüşme Ekle</div>
              <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">Öğrenci *</label>
                  <select className="form-select" value={form.ogrenci_id} onChange={e => setForm({ ...form, ogrenci_id: e.target.value })}>
                    <option value="">Seçin</option>
                    {ogrenciler.map(o => <option key={o.id} value={o.id}>{o.ad} {o.soyad}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Tarih</label>
                  <input className="form-input" type="date" value={form.tarih} onChange={e => setForm({ ...form, tarih: e.target.value })} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Konu *</label>
                <input className="form-input" placeholder="Üniversite seçimi, motivasyon..." value={form.konu} onChange={e => setForm({ ...form, konu: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Görüşme Notları</label>
                <textarea className="form-textarea" placeholder="Görüşme detayları..." value={form.notlar} onChange={e => setForm({ ...form, notlar: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Sonraki Adımlar</label>
                <textarea className="form-textarea" placeholder="Öğrenciye verilen görevler, bir sonraki görüşme..." value={form.sonraki_adimlar} onChange={e => setForm({ ...form, sonraki_adimlar: e.target.value })} style={{ minHeight: 70 }} />
              </div>
              <div className="form-group">
                <label className="form-label">Durum</label>
                <select className="form-select" value={form.durum} onChange={e => setForm({ ...form, durum: e.target.value })}>
                  <option value="planli">Planlı</option>
                  <option value="tamamlandi">Tamamlandı</option>
                  <option value="iptal">İptal</option>
                </select>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button className="btn btn-primary" style={{ flex: 1, justifyContent: 'center' }} onClick={handleCreate}>✓ Kaydet</button>
                <button className="btn btn-secondary" onClick={() => setShowModal(false)}>İptal</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Çalışma Programı ─────────────────────────────────────────
const PROGRAM_GUNLERI = [
  { key: 'Pazartesi', label: 'Pazartesi' },
  { key: 'Sali', label: 'Salı' },
  { key: 'Carsamba', label: 'Çarşamba' },
  { key: 'Persembe', label: 'Perşembe' },
  { key: 'Cuma', label: 'Cuma' },
  { key: 'Cumartesi', label: 'Cumartesi' },
  { key: 'Pazar', label: 'Pazar' },
];

const PROGRAM_TURLERI = [
  { value: 'haftalik', label: 'Haftalık Program Hazırla', helper: '7 gün boyunca oturum bazlı plan oluştur.' },
  { value: 'gunluk', label: 'Günlük Program Hazırla', helper: 'Tek bir tarih için oturum listesi hazırlayın.' },
  { value: 'calisma', label: 'Çalışma Programı Ayarla', helper: 'Ders, konu, not ve çalışma gün sayısı belirleyin.' },
];

const PROGRAM_TURU_BILGILERI = {
  haftalik: { label: 'Haftalık', title: 'Haftalık Çalışma Programı', badge: 'badge-info' },
  gunluk: { label: 'Günlük', title: 'Günlük Çalışma Programı', badge: 'badge-success' },
  calisma: { label: 'Çalışma', title: 'Çalışma Programı', badge: 'badge-warning' },
};

const DEFAULT_OTURUM_SAYISI = 3;
const MIN_OTURUM_SAYISI = 1;
const MAX_OTURUM_SAYISI = 20;

const EMPTY_PROGRAM_CELL = {
  ders_id: '',
  konu: '',
  soru_sayisi: '',
  sure_dakika: '',
  aciklama: '',
  rehber_notu: '',
  slot_etiketi: '',
};

function todayIso() {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function addDaysToIso(dateStr, days) {
  if (!dateStr) return '';
  const date = new Date(`${dateStr}T12:00:00`);
  if (Number.isNaN(date.getTime())) return '';
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
}

function formatDateLabel(dateStr) {
  const normalized = normalizeDateInput(dateStr);
  if (!normalized) return '—';
  const date = new Date(`${normalized}T12:00:00`);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' });
}

function getProgramDayFromDate(dateStr) {
  const normalized = normalizeDateInput(dateStr);
  if (!normalized) return PROGRAM_GUNLERI[0];
  const date = new Date(`${normalized}T12:00:00`);
  if (Number.isNaN(date.getTime())) return PROGRAM_GUNLERI[0];
  const dayIndex = (date.getDay() + 6) % 7;
  return PROGRAM_GUNLERI[dayIndex];
}

function getCellKey(gun, slotNo) {
  return `${gun}__${slotNo}`;
}

function getProgramDayKey(value) {
  return PROGRAM_GUNLERI.find(gun => gun.key === value || gun.label === value)?.key || value;
}

function getProgramDayLabel(value) {
  return PROGRAM_GUNLERI.find(gun => gun.key === value || gun.label === value)?.label || value || '';
}

function getProgramTimeStatus(program = {}) {
  const today = todayIso();
  const startDate = normalizeDateInput(program.baslangic_tarihi);
  const endDate = normalizeDateInput(program.bitis_tarihi || program.baslangic_tarihi);
  if (endDate && endDate < today) return 'gecmis';
  if (startDate && startDate > today) return 'gelecek';
  return 'guncel';
}

function normalizeDateInput(value) {
  if (!value) return '';
  const toLocalInput = (dateObj) => {
    if (!dateObj || Number.isNaN(dateObj.getTime())) return '';
    const yyyy = dateObj.getFullYear();
    const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
    const dd = String(dateObj.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };
  if (typeof value !== 'string') {
    return toLocalInput(new Date(value));
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  if (value.includes('T')) {
    return toLocalInput(new Date(value));
  }
  return toLocalInput(new Date(value));
}

function normalizeCell(cell = {}) {
  return {
    ders_id: cell.ders_id ?? '',
    konu: cell.konu ?? '',
    soru_sayisi: cell.soru_sayisi ?? '',
    sure_dakika: cell.sure_dakika ?? '',
    aciklama: cell.aciklama ?? '',
    rehber_notu: cell.rehber_notu ?? '',
    slot_etiketi: cell.slot_etiketi ?? '',
  };
}

function isCellMeaningful(cell = {}) {
  return Boolean(cell.ders_id || cell.konu || cell.soru_sayisi || cell.sure_dakika || cell.aciklama || cell.rehber_notu);
}

function buildAciklama(aciklama, rehberNotu) {
  const parts = [];
  if (aciklama?.trim()) parts.push(aciklama.trim());
  if (rehberNotu?.trim()) parts.push(`Rehber notu: ${rehberNotu.trim()}`);
  return parts.join('\n');
}

export function CalismaPrograPage() {
  return <WeeklyStudyProgramBuilder />;
}

function LegacyCalismaPrograPage() {
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth <= 900 : false);
  const [mobilGun, setMobilGun] = useState(PROGRAM_GUNLERI[0].key);
  const initialDate = todayIso();
  const [programlar, setProgramlar] = useState([]);
  const [ogrenciler, setOgrenciler] = useState([]);
  const [siniflar, setSiniflar] = useState([]);
  const [dersler, setDersler] = useState([]);
  const [topicBank, setTopicBank] = useState(() => loadTopicBank());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [detayLoading, setDetayLoading] = useState(false);
  const [seciliProgramDetay, setSeciliProgramDetay] = useState(null);
  const [editingProgramId, setEditingProgramId] = useState(null);
  const [ogrenciArama, setOgrenciArama] = useState('');
  const [ogrenciSinifFilter, setOgrenciSinifFilter] = useState('');
  const [form, setForm] = useState({
    ogrenci_id: '',
    program_turu: '',
    baslangic_tarihi: initialDate,
    bitis_tarihi: addDaysToIso(initialDate, 6),
    baslik: '',
    hedef: '',
    calisma_ders_id: '',
    calisma_konu: '',
    calisma_notu: '',
    calisma_gun_sayisi: '',
    calisma_detaylar: [],
  });
  const [gridData, setGridData] = useState({});
  const [activeCellKey, setActiveCellKey] = useState(null);
  const [oturumSayisi, setOturumSayisi] = useState(DEFAULT_OTURUM_SAYISI);
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [selectedCellKeys, setSelectedCellKeys] = useState([]);
  const [bulkCell, setBulkCell] = useState({ ...EMPTY_PROGRAM_CELL });
  const [cellDragState, setCellDragState] = useState(null);
  const cancelCellDragRef = useRef(null);
  const programTableWrapRef = useRef(null);
  const [gecmisProgramlarOpen, setGecmisProgramlarOpen] = useState(false);
  const [gecmisSinifFilter, setGecmisSinifFilter] = useState('');
  const [gecmisOgrenciFilter, setGecmisOgrenciFilter] = useState('');
  const [gecmisDurumFilter, setGecmisDurumFilter] = useState('gecmis');
  const [gecmisArama, setGecmisArama] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, oRes, dRes, sRes] = await Promise.all([
        axios.get('/rehberlik/programlar'),
        axios.get('/ogrenciler?limit=1000'),
        axios.get('/dersler'),
        axios.get('/siniflar'),
      ]);
      if (pRes.data.success) setProgramlar(pRes.data.data || []);
      if (oRes.data.success) setOgrenciler(oRes.data.data || []);
      if (dRes.data.success) setDersler(dRes.data.data || []);
      if (sRes.data.success) setSiniflar(sRes.data.data || []);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Program verileri yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const refreshTopicBank = () => setTopicBank(loadTopicBank());
    window.addEventListener('storage', refreshTopicBank);
    window.addEventListener('focus', refreshTopicBank);
    return () => {
      window.removeEventListener('storage', refreshTopicBank);
      window.removeEventListener('focus', refreshTopicBank);
    };
  }, []);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 900);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!form.program_turu || !form.baslangic_tarihi) return;
    setForm(prev => {
      if (prev.program_turu === 'haftalik') {
        const nextBitis = addDaysToIso(prev.baslangic_tarihi, 6);
        if (prev.bitis_tarihi === nextBitis) return prev;
        return { ...prev, bitis_tarihi: nextBitis };
      }
      if (prev.program_turu === 'calisma') {
        const gunSayisi = Math.max(1, parseInt(prev.calisma_gun_sayisi, 10) || 1);
        const nextBitis = addDaysToIso(prev.baslangic_tarihi, gunSayisi - 1);
        if (prev.bitis_tarihi === nextBitis) return prev;
        return { ...prev, bitis_tarihi: nextBitis };
      }
      if (prev.bitis_tarihi === prev.baslangic_tarihi) return prev;
      return { ...prev, bitis_tarihi: prev.baslangic_tarihi };
    });
  }, [form.program_turu, form.baslangic_tarihi, form.calisma_gun_sayisi]);

  const seciliOgrenci = ogrenciler.find(o => String(o.id) === String(form.ogrenci_id));
  const seciliProgramTuru = PROGRAM_TURU_BILGILERI[form.program_turu];
  const seciliOgrenciSinifAdi = seciliOgrenci
    ? String(seciliOgrenci.sinif_ad || seciliOgrenci.sinif_adi || seciliOgrenci.sinif?.ad || seciliOgrenci.sinif || '')
    : '';
  const calismaDersAdi = dersler.find(item => String(item.id) === String(form.calisma_ders_id))?.ad || '';
  const calismaKonuOnerileri = useMemo(
    () => getTopicSuggestions(topicBank, calismaDersAdi, seciliOgrenciSinifAdi),
    [topicBank, calismaDersAdi, seciliOgrenciSinifAdi]
  );
  const ogrenciSearch = ogrenciArama.trim().toLowerCase();
  const selectedSinifName = siniflar.find(s => String(s.id) === String(ogrenciSinifFilter))?.ad?.toLowerCase() || '';
  const filteredOgrenciler = ogrenciler.filter(ogr => {
    if (ogrenciSinifFilter) {
      const sinifMatch =
        String(ogr.sinif_id || '') === String(ogrenciSinifFilter) ||
        String(ogr.sinif?.id || '') === String(ogrenciSinifFilter);
      if (!sinifMatch && selectedSinifName) {
        const ogrSinifName = String(
          ogr.sinif_ad ||
          ogr.sinif_adi ||
          ogr.sinif ||
          ogr.sinif?.ad ||
          ''
        ).toLowerCase();
        if (!ogrSinifName.includes(selectedSinifName)) return false;
      }
      if (!sinifMatch && !selectedSinifName) return false;
    }
    if (ogrenciSearch) {
      const adSoyad = `${ogr.ad || ''} ${ogr.soyad || ''}`.toLowerCase();
      const no = String(ogr.ogrenci_no || ogr.numara || '');
      if (!adSoyad.includes(ogrenciSearch) && !no.includes(ogrenciSearch)) return false;
    }
    return true;
  });
  const gecmisFiltreliOgrenciler = ogrenciler.filter(ogrenci => (
    !gecmisSinifFilter || String(getOgrenciSinifId(ogrenci)) === String(gecmisSinifFilter)
  ));
  const gecmisProgramlar = programlar
    .filter(program => {
      const programOgrencisi = ogrenciler.find(ogrenci => String(ogrenci.id) === String(program.ogrenci_id));
      const programSinifId = program.sinif_id || (programOgrencisi ? getOgrenciSinifId(programOgrencisi) : '');
      if (gecmisSinifFilter && String(programSinifId) !== String(gecmisSinifFilter)) return false;
      if (gecmisOgrenciFilter && String(program.ogrenci_id) !== String(gecmisOgrenciFilter)) return false;
      if (gecmisDurumFilter !== 'tumu' && getProgramTimeStatus(program) !== gecmisDurumFilter) return false;
      const search = gecmisArama.trim().toLocaleLowerCase('tr-TR');
      if (!search) return true;
      const searchable = [
        program.baslik,
        program.ogrenci_ad,
        program.ogrenci_soyad,
        programOgrencisi?.ogrenci_no,
        programOgrencisi ? getOgrenciSinifAdi(programOgrencisi) : program.sinif_ad,
      ].filter(Boolean).join(' ').toLocaleLowerCase('tr-TR');
      return searchable.includes(search);
    })
    .sort((a, b) => String(b.baslangic_tarihi || '').localeCompare(String(a.baslangic_tarihi || '')));

  const gorunenGunler = useMemo(() => (
    form.program_turu === 'haftalik'
      ? PROGRAM_GUNLERI
      : form.program_turu === 'gunluk'
        ? [getProgramDayFromDate(form.baslangic_tarihi)]
        : []
  ), [form.program_turu, form.baslangic_tarihi]);
  const aktifMobilGun = gorunenGunler.some(g => g.key === mobilGun) ? mobilGun : (gorunenGunler[0]?.key || PROGRAM_GUNLERI[0].key);
  const tabloGunler = isMobile && form.program_turu === 'haftalik'
    ? gorunenGunler.filter(g => g.key === aktifMobilGun)
    : gorunenGunler;
  const programOturumlari = useMemo(
    () => Array.from({ length: oturumSayisi }, (_, index) => ({
      slot_no: index + 1,
      slot_etiketi: `${index + 1}. Oturum`,
    })),
    [oturumSayisi]
  );

  useEffect(() => {
    if (!gorunenGunler.length) {
      setActiveCellKey(null);
      return;
    }
    const firstKey = getCellKey(gorunenGunler[0].key, programOturumlari[0].slot_no);
    setActiveCellKey(prev => prev || firstKey);
  }, [form.program_turu, form.baslangic_tarihi, gorunenGunler, programOturumlari]);

  const activeCellMeta = (() => {
    if (!activeCellKey) return null;
    const [gunKey, slotNoRaw] = activeCellKey.split('__');
    const day = gorunenGunler.find(item => item.key === gunKey) || PROGRAM_GUNLERI.find(item => item.key === gunKey);
    const slot = programOturumlari.find(item => String(item.slot_no) === String(slotNoRaw));
    if (!day || !slot) return null;
    return { gunKey, gunLabel: day.label, slotNo: slot.slot_no, slotEtiketi: slot.slot_etiketi };
  })();

  const activeCell = activeCellMeta ? normalizeCell(gridData[activeCellKey]) : EMPTY_PROGRAM_CELL;
  const activeCellDersAdi = dersler.find(item => String(item.id) === String(activeCell.ders_id))?.ad || '';
  const activeCellKonuOnerileri = useMemo(
    () => getTopicSuggestions(topicBank, activeCellDersAdi, seciliOgrenciSinifAdi),
    [topicBank, activeCellDersAdi, seciliOgrenciSinifAdi]
  );
  const bulkCellDersAdi = dersler.find(item => String(item.id) === String(bulkCell.ders_id))?.ad || '';
  const bulkCellKonuOnerileri = useMemo(
    () => getTopicSuggestions(topicBank, bulkCellDersAdi, seciliOgrenciSinifAdi),
    [topicBank, bulkCellDersAdi, seciliOgrenciSinifAdi]
  );
  const editorCell = multiSelectMode ? bulkCell : activeCell;
  const editorCellKonuOnerileri = multiSelectMode ? bulkCellKonuOnerileri : activeCellKonuOnerileri;

  const updateCellField = (field, value) => {
    if (!activeCellMeta) return;
    setGridData(prev => {
      const next = { ...prev };
      const current = normalizeCell(prev[activeCellKey]);
      const updated = { ...current, [field]: value };
      if (!isCellMeaningful(updated)) delete next[activeCellKey];
      else next[activeCellKey] = updated;
      return next;
    });
  };

  const updateEditorField = (field, value) => {
    if (multiSelectMode) {
      setBulkCell(prev => ({ ...prev, [field]: value }));
      return;
    }
    updateCellField(field, value);
  };

  const clearActiveCell = () => {
    if (!activeCellMeta) return;
    setGridData(prev => {
      const next = { ...prev };
      delete next[activeCellKey];
      return next;
    });
  };

  const handleCellClick = (key) => {
    setActiveCellKey(key);
    if (!multiSelectMode) return;
    setSelectedCellKeys(prev => (
      prev.includes(key) ? prev.filter(item => item !== key) : [...prev, key]
    ));
  };

  const showCellCopyToast = (targetKey, previousCell, hadPreviousCell) => {
    toast.success((toastItem) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span>Çalışma hücreye kopyalandı</span>
        <button
          type="button"
          onClick={() => {
            setGridData(prev => {
              const next = { ...prev };
              if (hadPreviousCell) next[targetKey] = { ...previousCell };
              else delete next[targetKey];
              return next;
            });
            toast.dismiss(toastItem.id);
          }}
          style={{
            border: 'none',
            background: 'transparent',
            color: 'var(--accent)',
            fontWeight: 800,
            cursor: 'pointer',
            padding: 0,
          }}
        >
          Geri Al
        </button>
      </div>
    ), { duration: 7000 });
  };

  const copyCellToTarget = (sourceCell, targetKey) => {
    const hadPreviousCell = Object.prototype.hasOwnProperty.call(gridData, targetKey);
    const previousCell = hadPreviousCell ? normalizeCell(gridData[targetKey]) : null;
    if (previousCell && isCellMeaningful(previousCell)) {
      if (!window.confirm('Bu hücrede mevcut çalışma var. Üzerine yazılsın mı?')) return;
    }

    const copiedCell = normalizeCell({
      ders_id: sourceCell.ders_id,
      konu: sourceCell.konu,
      soru_sayisi: sourceCell.soru_sayisi,
      sure_dakika: sourceCell.sure_dakika,
      aciklama: sourceCell.aciklama,
      slot_etiketi: sourceCell.slot_etiketi,
    });
    setGridData(prev => ({ ...prev, [targetKey]: copiedCell }));
    setActiveCellKey(targetKey);
    showCellCopyToast(targetKey, previousCell, hadPreviousCell);
  };

  const handleCellDragPointerDown = (event, sourceKey, sourceCell, dersAdi) => {
    if (multiSelectMode || !isCellMeaningful(sourceCell)) return;
    if (event.button !== undefined && event.button !== 0) return;

    event.preventDefault();
    event.stopPropagation();
    cancelCellDragRef.current?.();

    const [, sourceSlotNo] = sourceKey.split('__');
    const operation = {
      pointerId: event.pointerId,
      pointerType: event.pointerType,
      sourceKey,
      sourceCell: normalizeCell({
        ...sourceCell,
        slot_etiketi: sourceCell.slot_etiketi || `${sourceSlotNo}. Oturum`,
      }),
      dersAdi,
      startX: event.clientX,
      startY: event.clientY,
      x: event.clientX,
      y: event.clientY,
      targetKey: null,
      active: event.pointerType !== 'touch',
      longPressTimer: null,
    };

    const renderDragState = () => setCellDragState({
      sourceKey: operation.sourceKey,
      targetKey: operation.targetKey,
      x: operation.x,
      y: operation.y,
      pointerType: operation.pointerType,
      dersAdi: operation.dersAdi,
      konu: operation.sourceCell.konu,
    });

    const cleanup = () => {
      if (operation.longPressTimer) window.clearTimeout(operation.longPressTimer);
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerEnd);
      window.removeEventListener('pointercancel', handlePointerCancel);
      setCellDragState(null);
      if (cancelCellDragRef.current === cleanup) cancelCellDragRef.current = null;
    };

    const handlePointerMove = (moveEvent) => {
      if (moveEvent.pointerId !== operation.pointerId) return;
      operation.x = moveEvent.clientX;
      operation.y = moveEvent.clientY;

      if (!operation.active) {
        const distance = Math.hypot(operation.x - operation.startX, operation.y - operation.startY);
        if (distance > 12) cleanup();
        return;
      }

      moveEvent.preventDefault();
      const tableWrap = programTableWrapRef.current;
      if (tableWrap) {
        const wrapRect = tableWrap.getBoundingClientRect();
        if (operation.x > wrapRect.right - 44) tableWrap.scrollLeft += 16;
        else if (operation.x < wrapRect.left + 44) tableWrap.scrollLeft -= 16;
      }
      if (operation.y > window.innerHeight - 52) window.scrollBy(0, 14);
      else if (operation.y < 52) window.scrollBy(0, -14);

      const elementUnderPointer = document.elementFromPoint(operation.x, operation.y);
      const mobileDayButton = elementUnderPointer?.closest?.('[data-program-day-key]');
      if (mobileDayButton && isMobile) {
        setMobilGun(mobileDayButton.dataset.programDayKey);
        operation.targetKey = null;
        renderDragState();
        return;
      }

      const targetElement = elementUnderPointer?.closest?.('[data-program-cell-key]');
      const hoveredKey = targetElement?.dataset?.programCellKey || null;
      operation.targetKey = hoveredKey && hoveredKey !== operation.sourceKey ? hoveredKey : null;
      renderDragState();
    };

    const handlePointerEnd = (upEvent) => {
      if (upEvent.pointerId !== operation.pointerId) return;
      const { active, targetKey, sourceCell: copiedSourceCell } = operation;
      cleanup();
      if (active && targetKey) copyCellToTarget(copiedSourceCell, targetKey);
    };

    const handlePointerCancel = (cancelEvent) => {
      if (cancelEvent.pointerId === operation.pointerId) cleanup();
    };

    window.addEventListener('pointermove', handlePointerMove, { passive: false });
    window.addEventListener('pointerup', handlePointerEnd);
    window.addEventListener('pointercancel', handlePointerCancel);
    cancelCellDragRef.current = cleanup;

    if (operation.active) {
      renderDragState();
    } else {
      operation.longPressTimer = window.setTimeout(() => {
        operation.active = true;
        renderDragState();
      }, 450);
    }
  };

  useEffect(() => () => cancelCellDragRef.current?.(), []);

  const handleMultiSelectToggle = () => {
    cancelCellDragRef.current?.();
    setMultiSelectMode(prev => !prev);
    setSelectedCellKeys([]);
    setBulkCell({ ...EMPTY_PROGRAM_CELL });
  };

  const handleTumGunleriSec = () => {
    const allCellKeys = gorunenGunler.flatMap(gun => (
      programOturumlari.map(slot => getCellKey(gun.key, slot.slot_no))
    ));
    setSelectedCellKeys(allCellKeys);
  };

  const handleOturumunTamaminiSec = () => {
    const slotNo = activeCellMeta?.slotNo || programOturumlari[0]?.slot_no;
    if (!slotNo) return;
    setSelectedCellKeys(prev => {
      const rowKeys = gorunenGunler.map(gun => getCellKey(gun.key, slotNo));
      return Array.from(new Set([...prev, ...rowKeys]));
    });
  };

  const handleBulkApply = () => {
    if (selectedCellKeys.length === 0) return toast.error('Uygulamak için en az bir hücre seçin');
    if (!bulkCell.ders_id) return toast.error('Ders seçin');
    const filledCellCount = selectedCellKeys.filter(key => isCellMeaningful(gridData[key])).length;
    if (
      filledCellCount > 0 &&
      !window.confirm(`${filledCellCount} seçili hücrede mevcut bilgi var. Bu hücrelerin üzerine yazılsın mı?`)
    ) return;

    const nextCell = normalizeCell(bulkCell);
    setGridData(prev => {
      const next = { ...prev };
      selectedCellKeys.forEach(key => {
        next[key] = { ...nextCell };
      });
      return next;
    });
    toast.success(`Bilgiler ${selectedCellKeys.length} hücreye uygulandı`);
  };

  const handleOturumArtir = () => {
    setOturumSayisi(prev => Math.min(MAX_OTURUM_SAYISI, prev + 1));
  };

  const handleOturumAzalt = () => {
    cancelCellDragRef.current?.();
    if (oturumSayisi <= MIN_OTURUM_SAYISI) return;
    const kaldirilacakSlot = oturumSayisi;
    const kaldirilacakAnahtarlar = Object.keys(gridData).filter(key => (
      Number(key.split('__')[1]) === kaldirilacakSlot
    ));
    const veriVar = kaldirilacakAnahtarlar.some(key => isCellMeaningful(gridData[key]));
    if (veriVar && !window.confirm(`${kaldirilacakSlot}. oturumda veri var. Oturum ve içindeki bilgiler kaldırılsın mı?`)) return;

    setGridData(prev => Object.fromEntries(
      Object.entries(prev).filter(([key]) => Number(key.split('__')[1]) !== kaldirilacakSlot)
    ));
    setSelectedCellKeys(prev => prev.filter(key => Number(key.split('__')[1]) !== kaldirilacakSlot));
    if (activeCellMeta?.slotNo === kaldirilacakSlot) {
      setActiveCellKey(gorunenGunler[0] ? getCellKey(gorunenGunler[0].key, kaldirilacakSlot - 1) : null);
    }
    setOturumSayisi(prev => Math.max(MIN_OTURUM_SAYISI, prev - 1));
  };

  const resetBuilder = () => {
    cancelCellDragRef.current?.();
    const nextDate = todayIso();
    setForm({
      ogrenci_id: '',
      program_turu: '',
      baslangic_tarihi: nextDate,
      bitis_tarihi: addDaysToIso(nextDate, 6),
      baslik: '',
      hedef: '',
      calisma_ders_id: '',
      calisma_konu: '',
      calisma_notu: '',
      calisma_gun_sayisi: '',
      calisma_detaylar: [],
    });
    setGridData({});
    setActiveCellKey(null);
    setOturumSayisi(DEFAULT_OTURUM_SAYISI);
    setMultiSelectMode(false);
    setSelectedCellKeys([]);
    setBulkCell({ ...EMPTY_PROGRAM_CELL });
    setEditingProgramId(null);
  };

  const parseAciklama = (raw = '') => {
    if (!raw) return { aciklama: '', rehber_notu: '' };
    const lines = String(raw).split('\n');
    let rehberNotu = '';
    const remaining = lines.filter(line => {
      if (/^rehber notu:/i.test(line.trim())) {
        rehberNotu = line.replace(/^rehber notu:/i, '').trim();
        return false;
      }
      return true;
    });
    return { aciklama: remaining.join('\n').trim(), rehber_notu: rehberNotu };
  };

  const mapDetaylarToGrid = (detaylar = []) => {
    const next = {};
    detaylar.forEach(detay => {
      const dayObj = detay.gun ? { key: getProgramDayKey(detay.gun) } : getProgramDayFromDate(detay.tarih);
      const gunKey = dayObj?.key || PROGRAM_GUNLERI[0].key;
      const slotNo = detay.slot_no || 1;
      const key = getCellKey(gunKey, slotNo);
      const { aciklama, rehber_notu } = parseAciklama(detay.aciklama || '');
      next[key] = {
        ders_id: detay.ders_id || '',
        konu: detay.konu || '',
        soru_sayisi: detay.soru_sayisi || '',
        sure_dakika: detay.sure_dakika || '',
        aciklama,
        rehber_notu,
        slot_etiketi: detay.slot_etiketi || `${slotNo}. Oturum`,
      };
    });
    return next;
  };

  const mapDetaylarToCalismaList = (detaylar = []) => detaylar.map((detay, index) => {
    const { aciklama } = parseAciklama(detay.aciklama || '');
    return {
      id: detay.id || `edit-${index}`,
      ders_id: detay.ders_id ? String(detay.ders_id) : '',
      ders_adi: detay.ders_adi || detay.ders?.ad || '',
      konu: detay.konu || '',
      not: aciklama,
      gun_sayisi: detay.calisma_gun_sayisi || detay.soru_sayisi || '',
    };
  });

  const calismaGunSayisi = Math.max(0, parseInt(form.calisma_gun_sayisi, 10) || 0);
  const calismaDetaylari = form.program_turu === 'calisma'
    ? form.calisma_detaylar
      .map((detay, index) => ({
        gun: 'Calisma',
        slot_no: index + 1,
        slot_etiketi: detay.gun_sayisi ? `${detay.gun_sayisi} Günlük Çalışma` : 'Çalışma',
        ders_id: detay.ders_id ? parseInt(detay.ders_id, 10) : null,
        konu: detay.konu?.trim() || '',
        soru_sayisi: detay.gun_sayisi ? parseInt(detay.gun_sayisi, 10) : null,
        aciklama: detay.not?.trim() || '',
      }))
      .filter(detay => detay.ders_id)
    : [];

  const tabloDetaylari = Object.entries(gridData)
    .map(([key, cell]) => {
      const [gun, slotNoRaw] = key.split('__');
      return {
        gun,
        slot_no: parseInt(slotNoRaw, 10),
        slot_etiketi: cell.slot_etiketi || programOturumlari.find(item => item.slot_no === parseInt(slotNoRaw, 10))?.slot_etiketi || `${slotNoRaw}. Oturum`,
        ders_id: cell.ders_id ? parseInt(cell.ders_id, 10) : null,
        konu: cell.konu?.trim() || '',
        soru_sayisi: cell.soru_sayisi ? parseInt(cell.soru_sayisi, 10) : null,
        sure_dakika: cell.sure_dakika ? parseInt(cell.sure_dakika, 10) : null,
        aciklama: buildAciklama(cell.aciklama, cell.rehber_notu),
      };
    })
    .filter(item => (
      item.ders_id &&
      item.slot_no >= MIN_OTURUM_SAYISI &&
      item.slot_no <= oturumSayisi &&
      PROGRAM_GUNLERI.some(gun => gun.key === item.gun)
    ));
  const olusturulanDetaylar = form.program_turu === 'calisma' ? calismaDetaylari : tabloDetaylari;

  const doluHucreSayisi = olusturulanDetaylar.length;

  const handleCalismaDetayEkle = () => {
    if (!form.calisma_ders_id) return toast.error('Ders seçin');
    if (!form.calisma_konu?.trim()) return toast.error('Konu girin');
    if (!calismaGunSayisi || calismaGunSayisi < 1) return toast.error('Çalışılacak gün sayısını girin');
    const ders = dersler.find(item => String(item.id) === String(form.calisma_ders_id));
    setForm(prev => ({
      ...prev,
      calisma_detaylar: [
        ...prev.calisma_detaylar,
        {
          id: `${Date.now()}-${prev.calisma_detaylar.length}`,
          ders_id: prev.calisma_ders_id,
          ders_adi: ders?.ad || '',
          konu: prev.calisma_konu.trim(),
          not: prev.calisma_notu?.trim() || '',
          gun_sayisi: prev.calisma_gun_sayisi,
        },
      ],
      calisma_ders_id: '',
      calisma_konu: '',
      calisma_notu: '',
      calisma_gun_sayisi: '',
    }));
  };

  const handleCalismaDetayDuzenle = (detayId) => {
    const detay = form.calisma_detaylar.find(item => item.id === detayId);
    if (!detay) return;
    setForm(prev => ({
      ...prev,
      calisma_ders_id: detay.ders_id,
      calisma_konu: detay.konu,
      calisma_notu: detay.not,
      calisma_gun_sayisi: detay.gun_sayisi,
      calisma_detaylar: prev.calisma_detaylar.filter(item => item.id !== detayId),
    }));
  };

  const handleCalismaDetaySil = (detayId) => {
    setForm(prev => ({
      ...prev,
      calisma_detaylar: prev.calisma_detaylar.filter(item => item.id !== detayId),
    }));
  };

  const handleProgramTuruSelect = (type) => {
    cancelCellDragRef.current?.();
    const nextGunSayisi = type === 'calisma' ? (form.calisma_gun_sayisi || '1') : form.calisma_gun_sayisi;
    setForm(prev => ({
      ...prev,
      program_turu: type,
      calisma_gun_sayisi: nextGunSayisi,
      bitis_tarihi: type === 'haftalik'
        ? addDaysToIso(prev.baslangic_tarihi, 6)
        : type === 'calisma'
          ? addDaysToIso(prev.baslangic_tarihi, Math.max(1, parseInt(nextGunSayisi, 10) || 1) - 1)
          : prev.baslangic_tarihi,
      baslik: prev.baslik || PROGRAM_TURU_BILGILERI[type]?.title || 'Çalışma Programı',
    }));
    setGridData({});
    setActiveCellKey(null);
    setOturumSayisi(DEFAULT_OTURUM_SAYISI);
    setMultiSelectMode(false);
    setSelectedCellKeys([]);
    setBulkCell({ ...EMPTY_PROGRAM_CELL });
  };

  const handleKaydet = async () => {
    if (!form.ogrenci_id) return toast.error('Önce öğrenci seçin');
    if (!form.program_turu) return toast.error('Program türü seçin');
    if (!form.baslangic_tarihi) return toast.error('Tarih seçin');
    if (form.program_turu === 'haftalik' && !form.bitis_tarihi) return toast.error('Bitiş tarihi oluşturulamadı');
    if (form.program_turu === 'haftalik' && form.bitis_tarihi < form.baslangic_tarihi) return toast.error('Bitiş tarihi başlangıçtan önce olamaz');
    if (form.program_turu === 'calisma') {
      if (form.calisma_ders_id || form.calisma_konu?.trim() || form.calisma_notu?.trim() || form.calisma_gun_sayisi) {
        return toast.error('Kaydetmeden önce girilen dersi programa ekleyin');
      }
      if (form.calisma_detaylar.length === 0) return toast.error('En az bir ders ekleyin');
    }
    if (olusturulanDetaylar.length === 0) return toast.error(form.program_turu === 'calisma' ? 'Çalışma bilgilerini doldurun' : 'En az bir hücreye ders ekleyin');

    const payload = {
      ogrenci_id: parseInt(form.ogrenci_id, 10),
      program_turu: form.program_turu,
      baslangic_tarihi: form.baslangic_tarihi,
      bitis_tarihi: form.program_turu === 'gunluk' ? form.baslangic_tarihi : form.bitis_tarihi,
      baslik: form.baslik?.trim() || seciliProgramTuru?.title || 'Çalışma Programı',
      hedef: form.hedef?.trim() || '',
      oturum_sayisi: oturumSayisi,
      detaylar: olusturulanDetaylar,
    };

    setSaving(true);
    try {
      if (editingProgramId) {
        const { data } = await axios.put(`/rehberlik/programlar/${editingProgramId}`, payload);
        toast.success(data.message || 'Çalışma programı güncellendi');
      } else {
        const { data } = await axios.post('/rehberlik/programlar', payload);
        toast.success(data.message || 'Çalışma programı oluşturuldu');
      }
      resetBuilder();
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Program kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  const handleProgramDetay = async (id) => {
    setDetayLoading(true);
    try {
      const { data } = await axios.get(`/rehberlik/programlar/${id}`);
      if (data.success) setSeciliProgramDetay(data.data);
      else toast.error(data.message || 'Program detayı alınamadı');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Program detayı alınamadı');
    } finally {
      setDetayLoading(false);
    }
  };

  const handleProgramEdit = async (id) => {
    cancelCellDragRef.current?.();
    setDetayLoading(true);
    try {
      const { data } = await axios.get(`/rehberlik/programlar/${id}`);
      if (!data.success) {
        toast.error(data.message || 'Program detayı alınamadı');
        return false;
      }
      const program = data.data;
      const calismaDetayListesi = program.program_turu === 'calisma' ? mapDetaylarToCalismaList(program.detaylar || []) : [];
      const detayOturumSayisi = Math.max(0, ...(program.detaylar || []).map(detay => Number(detay.slot_no) || 0));
      const nextOturumSayisi = Math.min(
        MAX_OTURUM_SAYISI,
        Math.max(MIN_OTURUM_SAYISI, Number(program.oturum_sayisi) || detayOturumSayisi || DEFAULT_OTURUM_SAYISI)
      );
      setEditingProgramId(id);
      setForm({
        ogrenci_id: String(program.ogrenci_id || ''),
        program_turu: program.program_turu || '',
        baslangic_tarihi: normalizeDateInput(program.baslangic_tarihi) || todayIso(),
        bitis_tarihi: normalizeDateInput(program.bitis_tarihi || program.baslangic_tarihi) || todayIso(),
        baslik: program.baslik || '',
        hedef: program.hedef || '',
        calisma_ders_id: '',
        calisma_konu: '',
        calisma_notu: '',
        calisma_gun_sayisi: '',
        calisma_detaylar: calismaDetayListesi,
      });
      setGridData(program.program_turu === 'calisma' ? {} : mapDetaylarToGrid(program.detaylar || []));
      setActiveCellKey(null);
      setOturumSayisi(nextOturumSayisi);
      setMultiSelectMode(false);
      setSelectedCellKeys([]);
      setBulkCell({ ...EMPTY_PROGRAM_CELL });
      toast.success('Program düzenleme moduna alındı');
      return true;
    } catch (err) {
      toast.error(err.response?.data?.message || 'Program detayı alınamadı');
      return false;
    } finally {
      setDetayLoading(false);
    }
  };

  const handleGecmisProgramlariAc = () => {
    if (seciliOgrenci) {
      setGecmisOgrenciFilter(String(seciliOgrenci.id));
      setGecmisSinifFilter(String(getOgrenciSinifId(seciliOgrenci) || ''));
    }
    setGecmisProgramlarOpen(true);
  };

  const handleGecmisProgramDetay = (id) => {
    setGecmisProgramlarOpen(false);
    handleProgramDetay(id);
  };

  const handleGecmisProgramEdit = async (id) => {
    const success = await handleProgramEdit(id);
    if (!success) return;
    setGecmisProgramlarOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleProgramDelete = async (id) => {
    if (!window.confirm('Bu programı silmek istediğinizden emin misiniz?')) return;
    try {
      const { data } = await axios.delete(`/rehberlik/programlar/${id}`);
      if (data?.success) {
        toast.success('Program silindi');
        if (editingProgramId === id) resetBuilder();
        if (seciliProgramDetay?.id === id) setSeciliProgramDetay(null);
        load();
      } else {
        toast.error(data?.message || 'Program silinemedi');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Program silinemedi');
    }
  };

  const escapePrintValue = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
    .replace(/\n/g, '<br />');

  const handleProgramPrint = async (program) => {
    let printableProgram = program;
    if (program?.id && !Array.isArray(program.detaylar)) {
      try {
        const { data } = await axios.get(`/rehberlik/programlar/${program.id}`);
        if (data?.success) printableProgram = data.data;
      } catch (err) {
        toast.error(err.response?.data?.message || 'Program detayı alınamadı');
        return;
      }
    }

    const detaylar = printableProgram?.detaylar || [];
    if (detaylar.length === 0) {
      toast.error('Çıktı almak için programda en az bir detay olmalı');
      return;
    }

    const programTuru = PROGRAM_TURU_BILGILERI[printableProgram.program_turu]?.label || 'Program';
    const programOgrenciAdi = `${printableProgram.ogrenci_ad || ''} ${printableProgram.ogrenci_soyad || ''}`.trim();
    const formOgrenciAdi = seciliOgrenci ? `${seciliOgrenci.ad || ''} ${seciliOgrenci.soyad || ''}`.trim() : '';
    const ogrenciAdi = programOgrenciAdi || formOgrenciAdi;
    const detayRows = detaylar.map((detay, index) => `
      <tr>
        <td>${index + 1}</td>
        <td>${escapePrintValue(printableProgram.program_turu === 'calisma' ? 'Çalışma' : `${getProgramDayLabel(detay.gun)} ${detay.slot_etiketi || ''}`.trim())}</td>
        <td>${escapePrintValue(detay.ders_adi || detay.ders?.ad || detay.ders_ad || '')}</td>
        <td>${escapePrintValue(detay.konu || '')}</td>
        <td>${escapePrintValue(printableProgram.program_turu === 'calisma'
          ? (detay.soru_sayisi ? `${detay.soru_sayisi} gün` : '')
          : [detay.soru_sayisi ? `${detay.soru_sayisi} soru` : '', detay.sure_dakika ? `${detay.sure_dakika} dk` : ''].filter(Boolean).join(' · '))}</td>
        <td>${escapePrintValue(detay.aciklama || '')}</td>
      </tr>
    `).join('');

    const html = `
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>${escapePrintValue(printableProgram.baslik || 'Çalışma Programı')}</title>
          <style>
            body { font-family: Arial, sans-serif; color: #172033; margin: 28px; position: relative; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .watermark { position: fixed; inset: 0; display: flex; align-items: center; justify-content: center; pointer-events: none; z-index: 0; }
            .watermark img { width: 62%; max-width: 520px; opacity: 0.08; }
            .print-page { position: relative; z-index: 1; }
            .header { border-bottom: 2px solid #172033; padding-bottom: 14px; margin-bottom: 18px; display: flex; justify-content: space-between; gap: 18px; align-items: flex-start; }
            .header-main { flex: 1; }
            .print-logo { width: 82px; height: 82px; object-fit: contain; border: 1px solid #E2E8F0; border-radius: 12px; padding: 6px; background: #FFFFFF; }
            h1 { font-size: 22px; margin: 0 0 8px; }
            .meta { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px 18px; font-size: 13px; }
            .meta strong { display: inline-block; min-width: 92px; color: #475569; }
            table { width: 100%; border-collapse: collapse; margin-top: 18px; font-size: 12px; }
            th, td { border: 1px solid #CBD5E1; padding: 8px; vertical-align: top; text-align: left; }
            th { background: #F1F5F9; color: #172033; }
            .goal { margin-top: 14px; padding: 10px 12px; background: #F8FAFC; border: 1px solid #E2E8F0; font-size: 13px; }
            @media print {
              body { margin: 16px; }
              .watermark img { opacity: 0.07; }
              .print-logo { width: 76px; height: 76px; }
            }
          </style>
        </head>
        <body>
          <div class="watermark"><img src="${kareKursBulvarLogo}" alt="" /></div>
          <div class="print-page">
            <div class="header">
              <div class="header-main">
                <h1>${escapePrintValue(printableProgram.baslik || 'Çalışma Programı')}</h1>
                <div class="meta">
                  <div><strong>Öğrenci:</strong> ${escapePrintValue(ogrenciAdi)}</div>
                  <div><strong>Program Türü:</strong> ${escapePrintValue(programTuru)}</div>
                  <div><strong>Başlangıç:</strong> ${escapePrintValue(formatDateLabel(printableProgram.baslangic_tarihi))}</div>
                  <div><strong>Bitiş:</strong> ${escapePrintValue(formatDateLabel(printableProgram.bitis_tarihi || printableProgram.baslangic_tarihi))}</div>
                </div>
                ${printableProgram.hedef ? `<div class="goal"><strong>Hedef:</strong> ${escapePrintValue(printableProgram.hedef)}</div>` : ''}
              </div>
              <img class="print-logo" src="${kareKursBulvarLogo}" alt="KareKurs VIP Bulvar" />
            </div>
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Plan</th>
                  <th>Ders</th>
                  <th>Konu</th>
                  <th>${printableProgram.program_turu === 'calisma' ? 'Süre' : 'Soru / Süre'}</th>
                  <th>Not</th>
                </tr>
              </thead>
              <tbody>${detayRows}</tbody>
            </table>
          </div>
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Çıktı penceresi açılamadı. Tarayıcı açılır pencere iznini kontrol edin.');
      return;
    }
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 500);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <style>{`
        .program-summary-grid {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 12px;
        }
        .program-stat-card {
          background: linear-gradient(180deg, rgba(255,255,255,0.98), rgba(240,244,255,0.9));
          border: 1px solid var(--border);
          border-radius: 18px;
          padding: 16px;
          box-shadow: var(--shadow-card);
        }
        .program-builder-shell {
          display: grid;
          grid-template-columns: minmax(0, 1.55fr) minmax(320px, 0.95fr);
          gap: 18px;
          align-items: start;
        }
        .program-chooser-grid {
          display: grid;
          grid-template-columns: minmax(220px, 1.1fr) minmax(260px, 1fr);
          gap: 14px;
        }
        .program-type-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 10px;
        }
        .program-type-card {
          border: 1px solid var(--border);
          border-radius: 16px;
          padding: 14px;
          background: var(--bg-card);
          cursor: pointer;
          transition: all var(--dur-normal) var(--ease);
        }
        .program-type-card:hover {
          transform: translateY(-1px);
          border-color: var(--blue-border);
          box-shadow: var(--shadow-sm);
        }
        .program-type-card.active {
          border-color: var(--accent-border);
          background: linear-gradient(180deg, rgba(232,51,74,0.06), rgba(255,255,255,0.96));
          box-shadow: var(--shadow-red);
        }
        .program-table-wrap {
          overflow-x: auto;
          border: 1px solid var(--border);
          border-radius: 18px;
          background: var(--bg-card);
        }
        .program-table {
          width: 100%;
          min-width: 760px;
          border-collapse: collapse;
        }
        .program-table th,
        .program-table td {
          border-bottom: 1px solid var(--border);
          border-right: 1px solid var(--border);
          padding: 0;
          vertical-align: top;
          background: var(--bg-card);
        }
        .program-table th:last-child,
        .program-table td:last-child {
          border-right: none;
        }
        .program-table tr:last-child td {
          border-bottom: none;
        }
        .program-cell-button {
          width: 100%;
          min-height: 110px;
          background: transparent;
          border: none;
          text-align: left;
          padding: 12px;
          cursor: pointer;
          transition: background var(--dur-normal) var(--ease);
        }
        .program-cell-button:hover {
          background: var(--bg-card-hover);
        }
        .program-cell-button.active {
          background: rgba(37, 99, 235, 0.08);
          box-shadow: inset 0 0 0 2px rgba(37, 99, 235, 0.22);
        }
        .program-cell-button.filled {
          background: linear-gradient(180deg, rgba(16,185,129,0.09), rgba(255,255,255,0.96));
        }
        .program-cell-button.selected {
          background: rgba(37, 99, 235, 0.13);
          box-shadow: inset 0 0 0 3px rgba(37, 99, 235, 0.55);
        }
        .program-cell-button.selected.filled {
          background: linear-gradient(180deg, rgba(37,99,235,0.17), rgba(16,185,129,0.1));
        }
        .program-cell-shell {
          position: relative;
          min-height: 110px;
        }
        .program-cell-shell .program-cell-button {
          min-height: 110px;
          height: 100%;
        }
        .program-cell-drag-handle {
          position: absolute;
          right: 7px;
          bottom: 7px;
          z-index: 2;
          width: 27px;
          height: 27px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border: 1px solid rgba(15, 30, 61, 0.16);
          border-radius: 8px;
          background: rgba(255, 255, 255, 0.92);
          color: var(--text-secondary);
          box-shadow: var(--shadow-sm);
          cursor: grab;
          touch-action: none;
          user-select: none;
          -webkit-user-select: none;
        }
        .program-cell-drag-handle:hover {
          color: var(--accent);
          border-color: var(--accent-border);
        }
        .program-cell-drag-handle:active {
          cursor: grabbing;
        }
        .program-cell-button.drag-source {
          opacity: 0.42;
        }
        .program-cell-button.drag-valid-target {
          background: rgba(59, 130, 246, 0.09);
          box-shadow: inset 0 0 0 2px rgba(59, 130, 246, 0.28);
        }
        .program-cell-button.drag-over {
          background: rgba(37, 99, 235, 0.18);
          box-shadow: inset 0 0 0 4px rgba(37, 99, 235, 0.72);
        }
        .program-cell-drag-preview {
          position: fixed;
          z-index: 10000;
          width: min(230px, calc(100vw - 32px));
          padding: 11px 13px;
          border: 2px solid rgba(37, 99, 235, 0.5);
          border-radius: 12px;
          background: rgba(255, 255, 255, 0.86);
          box-shadow: 0 16px 38px rgba(15, 30, 61, 0.22);
          pointer-events: none;
          opacity: 0.82;
          transform: translate(14px, 14px);
        }
        .program-table-header-actions,
        .program-selection-toolbar,
        .program-session-control {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
        }
        .program-session-control {
          padding: 4px;
          border: 1px solid var(--border);
          border-radius: 12px;
          background: var(--bg-elevated);
          flex-wrap: nowrap;
        }
        .program-session-control .btn {
          min-width: 34px;
          padding: 6px 9px;
          justify-content: center;
        }
        .program-session-count {
          min-width: 78px;
          text-align: center;
          font-size: 0.78rem;
          font-weight: 800;
          white-space: nowrap;
        }
        .program-selection-toolbar {
          padding: 10px 14px;
          border-bottom: 1px solid var(--border);
          background: rgba(37, 99, 235, 0.04);
        }
        .program-side-card {
          position: sticky;
          top: 88px;
        }
        .program-detail-list {
          display: flex;
          flex-direction: column;
          gap: 10px;
          margin-top: 12px;
        }
        .program-detail-row {
          padding: 12px;
          border-radius: 14px;
          background: var(--bg-elevated);
          border: 1px solid var(--border);
        }
        .program-card-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 16px;
        }
        .program-history-backdrop {
          left: var(--sidebar-w);
          z-index: 900;
          padding: 24px;
          background: rgba(15, 30, 61, 0.42);
          backdrop-filter: blur(4px);
        }
        .main-content.collapsed .program-history-backdrop {
          left: var(--sidebar-collapsed);
        }
        .program-history-modal {
          width: min(940px, 100%);
          max-width: 940px;
          max-height: calc(100vh - 48px);
          padding: 0;
          overflow: hidden;
          border: 1px solid rgba(148, 163, 184, 0.28);
          border-radius: 22px;
          background: #F8FAFC;
          box-shadow: 0 28px 80px rgba(15, 30, 61, 0.3);
        }
        .program-history-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 18px;
          padding: 22px 24px 18px;
          border-bottom: 1px solid var(--border);
          background: linear-gradient(135deg, #FFFFFF 0%, #F1F5FF 100%);
        }
        .program-history-title-wrap {
          display: flex;
          align-items: center;
          gap: 13px;
          min-width: 0;
        }
        .program-history-icon {
          width: 42px;
          height: 42px;
          flex: 0 0 42px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 13px;
          background: linear-gradient(135deg, rgba(37, 99, 235, 0.14), rgba(124, 58, 237, 0.12));
          color: var(--blue);
          font-size: 1.2rem;
        }
        .program-history-header .modal-title {
          margin: 0;
          font-size: 1.18rem;
        }
        .program-history-close {
          width: 36px;
          height: 36px;
          flex: 0 0 36px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border: 1px solid var(--border);
          border-radius: 11px;
          background: #FFFFFF;
          color: var(--text-secondary);
          cursor: pointer;
          font-size: 1rem;
          transition: all var(--dur-normal) var(--ease);
        }
        .program-history-close:hover {
          color: var(--accent);
          border-color: var(--accent-border);
          background: var(--accent-dim);
        }
        .program-history-body {
          max-height: calc(100vh - 145px);
          overflow-y: auto;
          display: flex;
          flex-direction: column;
          gap: 14px;
          padding: 20px 24px 24px;
        }
        .program-history-filters {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 14px 16px;
          padding: 18px;
          border: 1px solid var(--border);
          border-radius: 16px;
          background: #FFFFFF;
          box-shadow: var(--shadow-sm);
        }
        .program-history-filters .form-group {
          min-width: 0;
        }
        .program-history-filters .form-label {
          margin-bottom: 6px;
          color: var(--text-secondary);
          font-size: 0.72rem;
          letter-spacing: 0.02em;
          text-transform: none;
        }
        .program-history-filters .form-select,
        .program-history-filters .form-input {
          width: 100%;
          min-height: 44px;
          padding: 10px 12px;
          border: 1.5px solid var(--border-strong);
          border-radius: 11px;
          outline: none;
          background-color: #FFFFFF;
          color: var(--text-primary);
          font-family: var(--font-ui);
          font-size: 0.86rem;
          transition: border-color var(--dur-normal), box-shadow var(--dur-normal), background var(--dur-normal);
        }
        .program-history-filters .form-select {
          cursor: pointer;
        }
        .program-history-filters .form-select:focus,
        .program-history-filters .form-input:focus {
          border-color: var(--blue);
          background: #FFFFFF;
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
        }
        .program-history-summary {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 10px;
          min-height: 36px;
          padding: 0 2px;
          flex-wrap: wrap;
        }
        .program-history-count {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          color: var(--text-secondary);
          font-size: 0.82rem;
        }
        .program-history-count strong {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 28px;
          height: 28px;
          padding: 0 8px;
          border-radius: 9px;
          background: var(--info-dim);
          color: var(--blue);
        }
        .program-history-empty {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 24px;
          border: 1px dashed var(--border-strong);
          border-radius: 16px;
          background: rgba(255, 255, 255, 0.72);
        }
        .program-history-empty-icon {
          width: 50px;
          height: 50px;
          flex: 0 0 50px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 15px;
          background: var(--bg-elevated);
          font-size: 1.45rem;
        }
        .program-history-empty h3 {
          margin: 0 0 3px;
          font-size: 0.98rem;
        }
        .program-history-empty p {
          margin: 0;
          color: var(--text-muted);
          font-size: 0.8rem;
        }
        .program-history-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 12px;
        }
        .program-history-card {
          padding: 15px;
          border: 1px solid var(--border);
          border-radius: 16px;
          background: var(--bg-card);
          box-shadow: var(--shadow-sm);
        }
        @media (max-width: 1100px) {
          .program-summary-grid,
          .program-card-grid,
          .program-history-grid,
          .program-chooser-grid,
          .program-type-grid,
          .program-builder-shell {
            grid-template-columns: 1fr;
          }
          .program-side-card {
            position: static;
          }
        }
        @media (max-width: 900px) {
          .program-history-filters {
            grid-template-columns: 1fr;
          }
          .program-table-header-actions,
          .program-selection-toolbar {
            width: 100%;
          }
          .program-table-header-actions .btn,
          .program-selection-toolbar .btn {
            flex: 1 1 auto;
          }
          .program-session-control {
            flex: 1 1 100%;
            justify-content: space-between;
          }
          .program-table {
            min-width: 100%;
          }
          .program-cell-button {
            min-height: 88px;
            padding: 10px;
          }
          .program-cell-shell,
          .program-cell-shell .program-cell-button {
            min-height: 88px;
          }
        }
        @media (max-width: 768px) {
          .program-history-backdrop,
          .main-content.collapsed .program-history-backdrop {
            left: 0;
            padding: 10px;
            align-items: flex-end;
            z-index: 1100;
          }
          .program-history-modal {
            width: 100%;
            max-height: calc(100vh - 20px);
            border-radius: 20px;
          }
          .program-history-header {
            padding: 18px;
          }
          .program-history-body {
            max-height: calc(100vh - 104px);
            padding: 16px;
          }
          .program-history-filters {
            padding: 14px;
          }
          .program-history-empty {
            align-items: flex-start;
            padding: 18px;
          }
        }
      `}</style>

      {cellDragState && (
        <div
          className="program-cell-drag-preview"
          style={{
            left: cellDragState.x,
            top: cellDragState.y,
            transform: cellDragState.pointerType === 'touch' ? 'translate(-50%, calc(-100% - 18px))' : 'translate(14px, 14px)',
          }}
          aria-hidden="true"
        >
          <div style={{ fontWeight: 800, fontSize: '0.84rem' }}>{cellDragState.dersAdi}</div>
          {cellDragState.konu && (
            <div style={{ marginTop: 4, color: 'var(--text-secondary)', fontSize: '0.76rem' }}>
              {cellDragState.konu}
            </div>
          )}
          <div style={{ marginTop: 5, color: 'var(--text-muted)', fontSize: '0.7rem' }}>Kopyalanıyor</div>
        </div>
      )}

      <div className="section-header animate-fadeInUp" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        <div>
          <h2 style={{ fontFamily: 'var(--font-ui)', fontSize: '1.3rem' }}>📋 Çalışma Programları</h2>
          <div className="section-subtitle">Öğrenci seç, program türünü belirle, tabloyu doldur ve kaydet.</div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary" onClick={handleGecmisProgramlariAc}>
            Geçmiş Programlar
          </button>
          <button className="btn btn-outline" onClick={resetBuilder}>Formu Sıfırla</button>
        </div>
      </div>

      <div className="program-summary-grid animate-fadeInUp stagger-1">
        <div className="program-stat-card">
          <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: 6 }}>Toplam Program</div>
          <div style={{ fontSize: '1.8rem', fontWeight: 800 }}>{programlar.length}</div>
        </div>
        <div className="program-stat-card">
          <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: 6 }}>Seçili Öğrenci</div>
          <div style={{ fontSize: '1rem', fontWeight: 700 }}>{seciliOgrenci ? `${seciliOgrenci.ad} ${seciliOgrenci.soyad}` : 'Henüz seçilmedi'}</div>
        </div>
        <div className="program-stat-card">
          <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: 6 }}>Program Türü</div>
          <div style={{ fontSize: '1rem', fontWeight: 700 }}>{seciliProgramTuru?.label || 'Henüz seçilmedi'}</div>
        </div>
        <div className="program-stat-card">
          <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: 6 }}>Dolu Hücre</div>
          <div style={{ fontSize: '1.8rem', fontWeight: 800 }}>{doluHucreSayisi}</div>
        </div>
      </div>

      <div className="card animate-fadeInUp stagger-2" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div className="program-chooser-grid">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="form-group">
              <label className="form-label">1. Öğrenci Seç *</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                <input
                  className="form-input"
                  placeholder="İsim veya numara ara"
                  value={ogrenciArama}
                  onChange={e => setOgrenciArama(e.target.value)}
                />
                <select
                  className="form-select"
                  value={ogrenciSinifFilter}
                  onChange={e => setOgrenciSinifFilter(e.target.value)}
                >
                  <option value="">Tüm Sınıflar</option>
                  {siniflar.map(sinif => (
                    <option key={sinif.id} value={sinif.id}>{sinif.ad}</option>
                  ))}
                </select>
              </div>
              <select
                className="form-select"
                value={form.ogrenci_id}
                onChange={e => setForm(prev => ({ ...prev, ogrenci_id: e.target.value }))}
              >
                <option value="">Öğrenci seçin</option>
                {filteredOgrenciler.map(ogr => (
                  <option key={ogr.id} value={ogr.id}>
                    {ogr.ad} {ogr.soyad}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Program Başlığı</label>
              <input
                className="form-input"
                value={form.baslik}
                onChange={e => setForm(prev => ({ ...prev, baslik: e.target.value }))}
                placeholder="TYT Haftalık Program"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Hedef</label>
              <textarea
                className="form-input"
                rows={3}
                value={form.hedef}
                onChange={e => setForm(prev => ({ ...prev, hedef: e.target.value }))}
                placeholder="Paragraf ve problem eksiğini kapat"
              />
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="form-group">
              <label className="form-label">2. Program Türü *</label>
              <div className="program-type-grid">
                {PROGRAM_TURLERI.map(item => (
                  <button
                    key={item.value}
                    type="button"
                    className={`program-type-card ${form.program_turu === item.value ? 'active' : ''}`}
                    onClick={() => handleProgramTuruSelect(item.value)}
                  >
                    <div style={{ fontWeight: 800, fontSize: '0.92rem', marginBottom: 4 }}>{item.label}</div>
                    <div style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>{item.helper}</div>
                  </button>
                ))}
              </div>
            </div>

            {form.program_turu !== 'calisma' && (
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : (form.program_turu === 'haftalik' ? '1fr 1fr' : '1fr'), gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">{form.program_turu === 'gunluk' ? 'Program Tarihi *' : 'Başlangıç Tarihi *'}</label>
                  <input
                    className="form-input"
                    type="date"
                    value={form.baslangic_tarihi}
                    onChange={e => {
                      const nextDate = e.target.value;
                      setForm(prev => ({
                        ...prev,
                        baslangic_tarihi: nextDate,
                        bitis_tarihi: prev.program_turu === 'haftalik' ? addDaysToIso(nextDate, 6) : nextDate,
                      }));
                      setGridData({});
                      setActiveCellKey(null);
                    }}
                  />
                </div>
                {form.program_turu === 'haftalik' && (
                  <div className="form-group">
                    <label className="form-label">Bitiş Tarihi</label>
                    <input className="form-input" type="date" value={form.bitis_tarihi} readOnly />
                  </div>
                )}
              </div>
            )}

            {form.program_turu && (
              <div style={{ padding: 14, borderRadius: 14, background: 'var(--bg-elevated)', color: 'var(--text-secondary)', fontSize: '0.82rem' }}>
                {form.program_turu === 'haftalik' ? (
                  <span>Bu plan <strong>{formatDateLabel(form.baslangic_tarihi)}</strong> ile <strong>{formatDateLabel(form.bitis_tarihi)}</strong> arasını kapsar.</span>
                ) : form.program_turu === 'calisma' ? (
                  <span>Çalışma programında hafta günü seçilmez; öğretmen ders, konu, not ve kaç gün çalışılacağını belirler.</span>
                ) : (
                  <span>Günlük plan <strong>{formatDateLabel(form.baslangic_tarihi)}</strong> tarihi için hazırlanır.</span>
                )}
              </div>
            )}
          </div>
        </div>

        {!form.ogrenci_id || !form.program_turu ? (
          <div className="empty-state" style={{ padding: '32px 16px', border: '1px dashed var(--border-strong)', borderRadius: 16 }}>
            <div style={{ fontSize: '2rem' }}>🧭</div>
            <h3>Önce akışı tamamlayın</h3>
            <p>Devam etmek için öğrenci ve program türü seçimi zorunlu.</p>
          </div>
        ) : form.program_turu === 'calisma' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: 18, border: '1px solid var(--border)', borderRadius: 16, background: 'var(--bg-card)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: '1rem' }}>Çalışma Programı Ayarla</div>
                <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginTop: 4 }}>
                  Hafta günü veya oturum tablosu olmadan tek bir çalışma görevi oluşturun.
                </div>
              </div>
              <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', alignSelf: 'center' }}>
                {seciliOgrenci ? `${seciliOgrenci.ad} ${seciliOgrenci.soyad}` : ''}
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label className="form-label">Ders *</label>
                <select
                  className="form-select"
                  value={form.calisma_ders_id}
                  onChange={e => setForm(prev => ({ ...prev, calisma_ders_id: e.target.value }))}
                >
                  <option value="">Ders seçin</option>
                  {dersler.map(ders => (
                    <option key={ders.id} value={ders.id}>{ders.ad}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Kaç Gün Çalışacak? *</label>
                <input
                  className="form-input"
                  type="number"
                  min="1"
                  value={form.calisma_gun_sayisi}
                  onChange={e => setForm(prev => ({ ...prev, calisma_gun_sayisi: e.target.value }))}
                  placeholder="Örn. 5"
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Konu *</label>
              <input
                className="form-input"
                list="calisma-konu-onerileri"
                value={form.calisma_konu}
                onChange={e => setForm(prev => ({ ...prev, calisma_konu: e.target.value }))}
                placeholder="Paragraf, problem, fonksiyonlar..."
              />
              <datalist id="calisma-konu-onerileri">
                {calismaKonuOnerileri.map(item => (
                  <option key={item.id} value={item.konu}>{item.donem} • {item.unite}</option>
                ))}
              </datalist>
              {calismaKonuOnerileri.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                  {calismaKonuOnerileri.slice(0, 6).map(item => (
                    <button
                      key={item.id}
                      type="button"
                      className="btn btn-ghost"
                      style={{ fontSize: '0.76rem', padding: '6px 10px' }}
                      onClick={() => setForm(prev => ({ ...prev, calisma_konu: item.konu }))}
                    >
                      {item.konu}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">Not</label>
              <textarea
                className="form-input"
                rows={4}
                value={form.calisma_notu}
                onChange={e => setForm(prev => ({ ...prev, calisma_notu: e.target.value }))}
                placeholder="Öğrenciye iletilecek çalışma notu..."
              />
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button className="btn btn-outline" onClick={() => setForm(prev => ({ ...prev, calisma_ders_id: '', calisma_konu: '', calisma_notu: '', calisma_gun_sayisi: '' }))}>
                Alanları Temizle
              </button>
              <button className="btn btn-secondary" onClick={handleCalismaDetayEkle}>
                Dersi Programa Ekle
              </button>
            </div>

            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
                <div>
                  <div style={{ fontWeight: 800, fontSize: '0.95rem' }}>Eklenen Dersler</div>
                  <div style={{ color: 'var(--text-muted)', fontSize: '0.78rem', marginTop: 2 }}>
                    Programı kaydetmeden önce birden fazla ders ekleyebilirsiniz.
                  </div>
                </div>
                <span className="badge badge-muted">{form.calisma_detaylar.length} ders</span>
              </div>

              {form.calisma_detaylar.length === 0 ? (
                <div className="empty-state" style={{ padding: '18px 10px', border: '1px dashed var(--border)', borderRadius: 14 }}>
                  <p>Henüz ders eklenmedi.</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {form.calisma_detaylar.map((detay, index) => (
                    <div key={detay.id} style={{ padding: 12, borderRadius: 14, border: '1px solid var(--border)', background: 'var(--bg-elevated)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                        <div>
                          <div style={{ fontWeight: 800 }}>{index + 1}. {detay.ders_adi || dersler.find(ders => String(ders.id) === String(detay.ders_id))?.ad || 'Ders'}</div>
                          <div style={{ color: 'var(--text-secondary)', fontSize: '0.82rem', marginTop: 4 }}>{detay.konu}</div>
                        </div>
                        <span className="badge badge-success">{detay.gun_sayisi} gün</span>
                      </div>
                      {detay.not && (
                        <div style={{ color: 'var(--text-muted)', fontSize: '0.78rem', marginTop: 8, whiteSpace: 'pre-line' }}>{detay.not}</div>
                      )}
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
                        <button className="btn btn-outline btn-sm" onClick={() => handleCalismaDetayDuzenle(detay.id)}>Düzenle</button>
                        <button className="btn btn-outline btn-sm" style={{ color: 'var(--danger)' }} onClick={() => handleCalismaDetaySil(detay.id)}>Sil</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button className="btn btn-outline" onClick={() => handleProgramPrint({
                ...form,
                ogrenci_ad: seciliOgrenci?.ad,
                ogrenci_soyad: seciliOgrenci?.soyad,
                detaylar: calismaDetaylari.map((detay, index) => ({
                  ...detay,
                  ders_adi: form.calisma_detaylar[index]?.ders_adi || dersler.find(ders => String(ders.id) === String(detay.ders_id))?.ad,
                })),
              })} disabled={form.calisma_detaylar.length === 0}>
                Çıktı Al
              </button>
              <button className="btn btn-primary" onClick={handleKaydet} disabled={saving || form.calisma_detaylar.length === 0}>
                {saving ? 'Kaydediliyor...' : 'Programı Kaydet'}
              </button>
            </div>
          </div>
        ) : (
          <div className="program-builder-shell">
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ padding: '16px 18px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontWeight: 800, fontSize: '1rem' }}>
                    {form.program_turu === 'haftalik' ? 'Haftalık Program Tablosu' : 'Günlük Program Tablosu'}
                  </div>
                  <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                    {multiSelectMode
                      ? 'Birden fazla hücre seçin, bilgileri bir kez girip seçime uygulayın.'
                      : 'Hücreye tıklayıp ders, konu, soru sayısı, süre ve not alanlarını doldurun.'}
                  </div>
                </div>
                <div className="program-table-header-actions">
                  {seciliOgrenci && (
                    <span style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
                      {seciliOgrenci.ad} {seciliOgrenci.soyad}
                    </span>
                  )}
                  {form.program_turu === 'haftalik' && (
                    <>
                      <button
                        type="button"
                        className={`btn btn-sm ${multiSelectMode ? 'btn-primary' : 'btn-outline'}`}
                        onClick={handleMultiSelectToggle}
                        aria-pressed={multiSelectMode}
                      >
                        {multiSelectMode ? 'Çoklu Seçimi Kapat' : 'Çoklu Seçim'}
                      </button>
                      <div className="program-session-control" aria-label="Oturum sayısı">
                        <button
                          type="button"
                          className="btn btn-outline btn-sm"
                          onClick={handleOturumAzalt}
                          disabled={oturumSayisi <= MIN_OTURUM_SAYISI}
                          aria-label="Oturum sayısını azalt"
                        >
                          −
                        </button>
                        <span className="program-session-count">{oturumSayisi} oturum</span>
                        <button
                          type="button"
                          className="btn btn-outline btn-sm"
                          onClick={handleOturumArtir}
                          disabled={oturumSayisi >= MAX_OTURUM_SAYISI}
                          aria-label="Oturum sayısını artır"
                        >
                          +
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {multiSelectMode && (
                <div className="program-selection-toolbar">
                  <span className="badge badge-info">{selectedCellKeys.length} hücre seçili</span>
                  <button type="button" className="btn btn-outline btn-sm" onClick={handleTumGunleriSec}>
                    Tüm günleri seç
                  </button>
                  <button type="button" className="btn btn-outline btn-sm" onClick={handleOturumunTamaminiSec}>
                    Oturumun tamamını seç
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => setSelectedCellKeys([])}
                    disabled={selectedCellKeys.length === 0}
                  >
                    Seçimi temizle
                  </button>
                </div>
              )}

              {isMobile && form.program_turu === 'haftalik' && gorunenGunler.length > 0 && (
                <div style={{ display: 'flex', gap: 8, padding: '10px 14px 0', overflowX: 'auto' }}>
                  {gorunenGunler.map(gun => (
                    <button
                      key={gun.key}
                      type="button"
                      className="btn btn-sm"
                      data-program-day-key={gun.key}
                      onClick={() => setMobilGun(gun.key)}
                      style={{
                        flex: '0 0 auto',
                        borderRadius: 999,
                        border: '1px solid var(--border)',
                        background: aktifMobilGun === gun.key ? 'var(--accent)' : 'var(--bg-elevated)',
                        color: aktifMobilGun === gun.key ? '#fff' : 'var(--text-secondary)',
                        fontWeight: 700,
                      }}
                    >
                      {gun.label}
                    </button>
                  ))}
                </div>
              )}

              <div className="program-table-wrap" ref={programTableWrapRef}>
                <table className="program-table">
                  <thead>
                    <tr>
                      <th style={{ minWidth: 120, padding: '14px 12px', background: 'var(--bg-elevated)', fontSize: '0.78rem', textAlign: 'left' }}>Oturum</th>
                      {tabloGunler.map(gun => (
                        <th key={gun.key} style={{ minWidth: form.program_turu === 'haftalik' ? 170 : 260, padding: '14px 12px', background: 'var(--bg-elevated)', fontSize: '0.78rem', textAlign: 'left' }}>
                          <div style={{ fontWeight: 800 }}>{gun.label}</div>
                          {form.program_turu === 'gunluk' && (
                            <div style={{ fontWeight: 500, color: 'var(--text-muted)', marginTop: 2 }}>{formatDateLabel(form.baslangic_tarihi)}</div>
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {programOturumlari.map(slot => (
                      <tr key={slot.slot_no}>
                        <td style={{ padding: '14px 12px', background: 'rgba(15,30,61,0.03)' }}>
                          <div style={{ fontWeight: 800, fontSize: '0.82rem' }}>{slot.slot_etiketi}</div>
                          <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>Günün çalışma bloğu</div>
                        </td>
                        {tabloGunler.map(gun => {
                          const key = getCellKey(gun.key, slot.slot_no);
                          const cell = normalizeCell(gridData[key]);
                          const ders = dersler.find(item => String(item.id) === String(cell.ders_id));
                          const filled = isCellMeaningful(cell);
                          const selected = selectedCellKeys.includes(key);
                          const isDragSource = cellDragState?.sourceKey === key;
                          const isDragTarget = cellDragState?.targetKey === key;
                          const isValidDragTarget = Boolean(cellDragState && !isDragSource);
                          return (
                            <td key={key}>
                              <div className="program-cell-shell" data-program-cell-key={key}>
                                <button
                                  type="button"
                                  className={`program-cell-button ${!multiSelectMode && activeCellKey === key ? 'active' : ''} ${filled ? 'filled' : ''} ${selected ? 'selected' : ''} ${isDragSource ? 'drag-source' : ''} ${isValidDragTarget ? 'drag-valid-target' : ''} ${isDragTarget ? 'drag-over' : ''}`}
                                  onClick={() => handleCellClick(key)}
                                  aria-pressed={multiSelectMode ? selected : undefined}
                                >
                                  {filled ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                                        <strong style={{ fontSize: '0.84rem' }}>{ders?.ad || 'Ders seçili'}</strong>
                                        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                          {cell.soru_sayisi ? <span className="badge badge-success">{cell.soru_sayisi} soru</span> : null}
                                          {cell.sure_dakika ? <span className="badge badge-info">{cell.sure_dakika} dk</span> : null}
                                        </div>
                                      </div>
                                      {cell.konu && <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>{cell.konu}</div>}
                                      {(cell.aciklama || cell.rehber_notu) && (
                                        <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)' }}>
                                          {(cell.aciklama || cell.rehber_notu).slice(0, 70)}
                                          {(cell.aciklama || cell.rehber_notu).length > 70 ? '…' : ''}
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>Ders ve çalışma bilgisi ekle</div>
                                  )}
                                </button>
                                {filled && !multiSelectMode && (
                                  <button
                                    type="button"
                                    className="program-cell-drag-handle"
                                    aria-label="Çalışmayı başka hücreye kopyala"
                                    title={isMobile ? 'Kopyalamak için uzun basıp sürükleyin' : 'Kopyalamak için sürükleyin'}
                                    onClick={event => {
                                      event.preventDefault();
                                      event.stopPropagation();
                                    }}
                                    onContextMenu={event => event.preventDefault()}
                                    onPointerDown={event => handleCellDragPointerDown(event, key, cell, ders?.ad || 'Ders')}
                                  >
                                    ⠿
                                  </button>
                                )}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="card program-side-card" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: '1rem' }}>Hücre Düzenleyici</div>
                <div style={{ color: 'var(--text-muted)', fontSize: '0.78rem', marginTop: 4 }}>
                  {multiSelectMode
                    ? `${selectedCellKeys.length} seçili hücre için toplu düzenleme`
                    : activeCellMeta
                      ? `${activeCellMeta.gunLabel} · ${activeCellMeta.slotEtiketi}`
                      : 'Tablodan bir hücre seçin'}
                </div>
              </div>

              {multiSelectMode || activeCellMeta ? (
                <>
                  <div className="form-group">
                    <label className="form-label">Ders *</label>
                    <select
                      className="form-select"
                      value={editorCell.ders_id}
                      onChange={e => updateEditorField('ders_id', e.target.value)}
                    >
                      <option value="">Ders seçin</option>
                      {dersler.map(ders => (
                        <option key={ders.id} value={ders.id}>{ders.ad}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Konu / Çalışma Açıklaması</label>
                    <input
                      className="form-input"
                      list="hucre-konu-onerileri"
                      value={editorCell.konu}
                      onChange={e => updateEditorField('konu', e.target.value)}
                      placeholder="Paragraf, problem, fonksiyonlar..."
                    />
                    <datalist id="hucre-konu-onerileri">
                      {editorCellKonuOnerileri.map(item => (
                        <option key={item.id} value={item.konu}>{item.donem} • {item.unite}</option>
                      ))}
                    </datalist>
                    {editorCellKonuOnerileri.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                        {editorCellKonuOnerileri.slice(0, 5).map(item => (
                          <button
                            key={item.id}
                            type="button"
                            className="btn btn-ghost"
                            style={{ fontSize: '0.74rem', padding: '6px 9px' }}
                            onClick={() => updateEditorField('konu', item.konu)}
                          >
                            {item.konu}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="form-label">Soru Sayısı</label>
                    <input
                      className="form-input"
                      type="number"
                      min="0"
                      value={editorCell.soru_sayisi}
                      onChange={e => updateEditorField('soru_sayisi', e.target.value)}
                      placeholder="40"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Süre (Dakika)</label>
                    <input
                      className="form-input"
                      type="number"
                      min="1"
                      value={editorCell.sure_dakika}
                      onChange={e => updateEditorField('sure_dakika', e.target.value)}
                      placeholder="60"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Çalışma Açıklaması</label>
                    <textarea
                      className="form-input"
                      rows={3}
                      value={editorCell.aciklama}
                      onChange={e => updateEditorField('aciklama', e.target.value)}
                      placeholder="Yanlış soruları tekrar et, süre tut..."
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Rehber Öğretmen Notu</label>
                    <textarea
                      className="form-input"
                      rows={3}
                      value={editorCell.rehber_notu}
                      onChange={e => updateEditorField('rehber_notu', e.target.value)}
                      placeholder="Motivasyon notu, dikkat edilmesi gerekenler..."
                    />
                  </div>

                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    {multiSelectMode ? (
                      <button
                        className="btn btn-secondary"
                        style={{ flex: '1 1 180px', justifyContent: 'center' }}
                        onClick={handleBulkApply}
                        disabled={selectedCellKeys.length === 0}
                      >
                        Seçili Hücrelere Uygula
                      </button>
                    ) : (
                      <button className="btn btn-outline" style={{ flex: '1 1 140px', justifyContent: 'center' }} onClick={clearActiveCell}>
                        Hücreyi Temizle
                      </button>
                    )}
                    <button className="btn btn-primary" style={{ flex: 1, justifyContent: 'center' }} onClick={handleKaydet} disabled={saving}>
                      {saving ? 'Kaydediliyor...' : 'Programı Kaydet'}
                    </button>
                  </div>
                </>
              ) : (
                <div className="empty-state" style={{ padding: '20px 10px' }}>
                  <p>Detay girmek için tablodan bir hücre seçin.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="card animate-fadeInUp stagger-3" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: '1rem' }}>Oluşturulan Programlar</div>
            <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>Aynı ekranda mevcut programları görüntüleyin.</div>
          </div>
        </div>

        {loading ? (
          <div className="program-card-grid">
            {Array(4).fill(0).map((_, index) => <div key={index} className="skeleton" style={{ height: 170, borderRadius: 18 }} />)}
          </div>
        ) : programlar.length === 0 ? (
          <div className="empty-state" style={{ padding: '32px 12px' }}>
            <div style={{ fontSize: '2rem' }}>📚</div>
            <h3>Henüz program yok</h3>
            <p>İlk çalışma programını yukarıdaki tablo ile oluşturabilirsiniz.</p>
          </div>
        ) : (
          <div className="program-card-grid">
            {programlar.map(program => (
              <div key={program.id} className="card card-accent" style={{ padding: 18 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', marginBottom: 12 }}>
                  <div>
                    <div style={{ fontWeight: 800, fontSize: '0.96rem' }}>{program.baslik || 'Çalışma Programı'}</div>
                    <div style={{ color: 'var(--text-muted)', fontSize: '0.78rem', marginTop: 4 }}>
                      {program.ogrenci_ad} {program.ogrenci_soyad}
                    </div>
                  </div>
                  <span className={`badge ${PROGRAM_TURU_BILGILERI[program.program_turu]?.badge || 'badge-muted'}`}>
                    {PROGRAM_TURU_BILGILERI[program.program_turu]?.label || 'Program'}
                  </span>
                </div>

                <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', marginBottom: 12 }}>
                  {formatDateLabel(program.baslangic_tarihi)}
                  {program.bitis_tarihi ? ` - ${formatDateLabel(program.bitis_tarihi)}` : ''}
                </div>

                {program.hedef && (
                  <div style={{ padding: '10px 12px', background: 'var(--bg-elevated)', borderRadius: 12, fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: 12 }}>
                    🎯 {program.hedef}
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                  <div style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>
                    {program.program_turu === 'calisma'
                      ? `${program.detaylar?.[0]?.soru_sayisi || program.detay_sayisi || 0} gün çalışma`
                      : `${program.detay_sayisi || program.detaylar?.length || 0} oturum planlandı`}
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-outline btn-sm" onClick={() => handleProgramDetay(program.id)} disabled={detayLoading}>
                      Detayı Gör
                    </button>
                    <button className="btn btn-outline btn-sm" onClick={() => handleProgramEdit(program.id)} disabled={detayLoading}>
                      Düzenle
                    </button>
                    <button className="btn btn-outline btn-sm" onClick={() => handleProgramPrint(program)} disabled={detayLoading}>
                      Çıktı Al
                    </button>
                    <button className="btn btn-outline btn-sm" style={{ color: 'var(--danger)' }} onClick={() => handleProgramDelete(program.id)}>
                      Sil
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {gecmisProgramlarOpen && (
        <div className="modal-backdrop program-history-backdrop" onClick={() => setGecmisProgramlarOpen(false)}>
          <div className="modal program-history-modal" onClick={event => event.stopPropagation()}>
            <div className="program-history-header">
              <div className="program-history-title-wrap">
                <div className="program-history-icon">🗂️</div>
                <div style={{ minWidth: 0 }}>
                  <div className="modal-title">Geçmiş Programlar</div>
                  <div style={{ color: 'var(--text-muted)', fontSize: '0.78rem', marginTop: 3 }}>
                    Önceki programı bulun, düzenleyin ve yeniden çıktı alın.
                  </div>
                </div>
              </div>
              <button
                type="button"
                className="program-history-close"
                aria-label="Geçmiş programları kapat"
                onClick={() => setGecmisProgramlarOpen(false)}
              >
                ✕
              </button>
            </div>

            <div className="program-history-body">
              <div className="program-history-filters">
                <div className="form-group">
                  <label className="form-label">Sınıf</label>
                  <select
                    className="form-select"
                    value={gecmisSinifFilter}
                    onChange={event => {
                      setGecmisSinifFilter(event.target.value);
                      setGecmisOgrenciFilter('');
                    }}
                  >
                    <option value="">Tüm Sınıflar</option>
                    {siniflar.map(sinif => (
                      <option key={sinif.id} value={sinif.id}>{sinif.ad}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Öğrenci</label>
                  <select
                    className="form-select"
                    value={gecmisOgrenciFilter}
                    onChange={event => setGecmisOgrenciFilter(event.target.value)}
                  >
                    <option value="">Tüm Öğrenciler</option>
                    {gecmisFiltreliOgrenciler.map(ogrenci => (
                      <option key={ogrenci.id} value={ogrenci.id}>
                        {getOgrenciAdSoyad(ogrenci)}{ogrenci.ogrenci_no ? ` · ${ogrenci.ogrenci_no}` : ''}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Dönem</label>
                  <select
                    className="form-select"
                    value={gecmisDurumFilter}
                    onChange={event => setGecmisDurumFilter(event.target.value)}
                  >
                    <option value="gecmis">Geçmiş Programlar</option>
                    <option value="guncel">Güncel Programlar</option>
                    <option value="gelecek">Gelecek Programlar</option>
                    <option value="tumu">Tüm Programlar</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Ara</label>
                  <input
                    className="form-input"
                    value={gecmisArama}
                    onChange={event => setGecmisArama(event.target.value)}
                    placeholder="Başlık, öğrenci veya numara"
                  />
                </div>
              </div>

              <div className="program-history-summary">
                <div className="program-history-count">
                  <strong>{gecmisProgramlar.length}</strong> program bulundu
                </div>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => {
                    setGecmisSinifFilter('');
                    setGecmisOgrenciFilter('');
                    setGecmisDurumFilter('gecmis');
                    setGecmisArama('');
                  }}
                >
                  Filtreleri Temizle
                </button>
              </div>

              {loading ? (
                <div className="program-history-grid">
                  {Array(4).fill(0).map((_, index) => <div key={index} className="skeleton" style={{ height: 180, borderRadius: 16 }} />)}
                </div>
              ) : gecmisProgramlar.length === 0 ? (
                <div className="program-history-empty">
                  <div className="program-history-empty-icon">📭</div>
                  <div>
                    <h3>Program bulunamadı</h3>
                    <p>Filtreleri değiştirerek öğrencinin diğer programlarını görüntüleyebilirsiniz.</p>
                  </div>
                </div>
              ) : (
                <div className="program-history-grid">
                  {gecmisProgramlar.map(program => {
                    const programOgrencisi = ogrenciler.find(ogrenci => String(ogrenci.id) === String(program.ogrenci_id));
                    const zamanDurumu = getProgramTimeStatus(program);
                    const zamanBilgisi = {
                      gecmis: { label: 'Geçmiş', className: 'badge-muted' },
                      guncel: { label: 'Güncel', className: 'badge-success' },
                      gelecek: { label: 'Gelecek', className: 'badge-info' },
                    }[zamanDurumu];
                    return (
                      <div key={program.id} className="program-history-card">
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' }}>
                          <div>
                            <div style={{ fontWeight: 800, fontSize: '0.94rem' }}>{program.baslik || 'Çalışma Programı'}</div>
                            <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', marginTop: 4 }}>
                              {program.ogrenci_ad || programOgrencisi?.ad} {program.ogrenci_soyad || programOgrencisi?.soyad}
                            </div>
                            <div style={{ color: 'var(--text-muted)', fontSize: '0.74rem', marginTop: 2 }}>
                              {programOgrencisi ? getOgrenciSinifAdi(programOgrencisi) : (program.sinif_ad || 'Sınıf bilgisi yok')}
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                            <span className={`badge ${zamanBilgisi.className}`}>{zamanBilgisi.label}</span>
                            <span className={`badge ${PROGRAM_TURU_BILGILERI[program.program_turu]?.badge || 'badge-muted'}`}>
                              {PROGRAM_TURU_BILGILERI[program.program_turu]?.label || 'Program'}
                            </span>
                          </div>
                        </div>

                        <div style={{ marginTop: 12, padding: '9px 11px', borderRadius: 11, background: 'var(--bg-elevated)', color: 'var(--text-secondary)', fontSize: '0.78rem' }}>
                          {formatDateLabel(program.baslangic_tarihi)}
                          {program.bitis_tarihi ? ` - ${formatDateLabel(program.bitis_tarihi)}` : ''}
                        </div>

                        {program.hedef && (
                          <div style={{ marginTop: 9, color: 'var(--text-muted)', fontSize: '0.76rem' }}>
                            🎯 {program.hedef}
                          </div>
                        )}

                        <div style={{ display: 'flex', gap: 7, justifyContent: 'flex-end', flexWrap: 'wrap', marginTop: 13 }}>
                          <button className="btn btn-outline btn-sm" onClick={() => handleGecmisProgramDetay(program.id)} disabled={detayLoading}>
                            Detayı Gör
                          </button>
                          <button className="btn btn-secondary btn-sm" onClick={() => handleGecmisProgramEdit(program.id)} disabled={detayLoading}>
                            Düzenle
                          </button>
                          <button className="btn btn-outline btn-sm" onClick={() => handleProgramPrint(program)} disabled={detayLoading}>
                            Yeniden Çıktı Al
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {seciliProgramDetay && (
        <div className="modal-backdrop" onClick={() => setSeciliProgramDetay(null)}>
          <div className="modal" style={{ maxWidth: 760 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">{seciliProgramDetay.baslik || 'Program Detayı'}</div>
              <button className="modal-close" onClick={() => setSeciliProgramDetay(null)}>✕</button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
                <div style={{ padding: 12, borderRadius: 12, background: 'var(--bg-elevated)' }}>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 4 }}>Öğrenci</div>
                  <div style={{ fontWeight: 700 }}>{seciliProgramDetay.ogrenci_ad} {seciliProgramDetay.ogrenci_soyad}</div>
                </div>
                <div style={{ padding: 12, borderRadius: 12, background: 'var(--bg-elevated)' }}>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 4 }}>Tarih Aralığı</div>
                  <div style={{ fontWeight: 700 }}>
                    {formatDateLabel(seciliProgramDetay.baslangic_tarihi)}
                    {seciliProgramDetay.bitis_tarihi ? ` - ${formatDateLabel(seciliProgramDetay.bitis_tarihi)}` : ''}
                  </div>
                </div>
              </div>

              {seciliProgramDetay.hedef && (
                <div style={{ padding: 14, borderRadius: 14, background: 'var(--accent-dim)', color: 'var(--accent)', fontSize: '0.84rem' }}>
                  🎯 {seciliProgramDetay.hedef}
                </div>
              )}

              <div>
                <div style={{ fontWeight: 800, fontSize: '0.92rem' }}>Program Detayları</div>
                <div className="program-detail-list">
                  {(seciliProgramDetay.detaylar || []).map(detay => (
                    <div key={`${detay.gun}-${detay.slot_no}-${detay.id || ''}`} className="program-detail-row">
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                        <div style={{ fontWeight: 700 }}>
                          {seciliProgramDetay.program_turu === 'calisma' ? 'Çalışma Programı' : `${getProgramDayLabel(detay.gun)} · ${detay.slot_etiketi}`}
                        </div>
                        <div style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>
                          {seciliProgramDetay.program_turu === 'calisma'
                            ? (detay.soru_sayisi ? `${detay.soru_sayisi} gün` : 'Gün sayısı girilmedi')
                            : [
                              detay.soru_sayisi ? `${detay.soru_sayisi} soru` : '',
                              detay.sure_dakika ? `${detay.sure_dakika} dk` : '',
                            ].filter(Boolean).join(' · ') || 'Soru ve süre girilmedi'}
                        </div>
                      </div>
                      <div style={{ marginTop: 6, color: 'var(--text-secondary)', fontSize: '0.84rem' }}>
                        <strong>{detay.ders_adi || detay.ders?.ad || 'Ders'}</strong>
                        {detay.konu ? ` · ${detay.konu}` : ''}
                      </div>
                      {detay.aciklama && (
                        <div style={{ marginTop: 8, color: 'var(--text-muted)', fontSize: '0.8rem', whiteSpace: 'pre-line' }}>
                          {detay.aciklama}
                        </div>
                      )}
                    </div>
                  ))}
                  {(seciliProgramDetay.detaylar || []).length === 0 && (
                    <div className="empty-state" style={{ padding: '18px 0' }}>
                      <p>Bu program için detay bulunamadı.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Etüt Talepleri ────────────────────────────────────────────
export function EtutlerPage() {
  const [talepler, setTalepler] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get('/rehberlik/etut-talepleri').then(res => {
      if (res.data.success) setTalepler(res.data.data || []);
    }).finally(() => setLoading(false));
  }, []);

  const handleDurumGuncelle = async (id, yeniDurum) => {
    try {
      await axios.put(`/rehberlik/etut-talepleri/${id}`, { durum: yeniDurum });
      setTalepler(prev => prev.map(t => t.id === id ? { ...t, durum: yeniDurum } : t));
      toast.success('Güncellendi!');
    } catch { toast.error('Hata'); }
  };

  const DURUM_COLORS = { bekliyor: 'var(--accent)', onaylandi: '#10B981', reddedildi: '#EF4444', tamamlandi: '#3B82F6' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div className="animate-fadeInUp">
        <h2 style={{ fontFamily: 'var(--font-ui)', fontSize: '1.3rem' }}>📚 Etüt Talepleri</h2>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: 4 }}>Öğrencilerden gelen etüt talepleri</p>
      </div>

      {loading ? <div className="page-loader"><div className="spinner" /></div> :
        talepler.length === 0 ? (
          <div className="card"><div className="empty-state"><div style={{ fontSize: '2.5rem' }}>📚</div><h3>Etüt talebi yok</h3></div></div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} className="animate-fadeInUp stagger-1">
            {talepler.map((t, i) => (
              <div key={i} className="card" style={{ borderLeft: `3px solid ${DURUM_COLORS[t.durum] || '#94A3B8'}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                    <div className="avatar avatar-sm" style={{ background: 'var(--accent-dim)', color: 'var(--accent)' }}>
                      {t.ogrenci_ad?.[0]}{t.ogrenci_soyad?.[0]}
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>{t.ogrenci_ad} {t.ogrenci_soyad}</div>
                      <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', marginTop: 2 }}>
                        <strong>{t.ders_adi}</strong> — {t.konu || 'Genel etüt'}
                      </div>
                      {t.aciklama && <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 4 }}>{t.aciklama}</div>}
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 4 }}>
                        {new Date(t.created_at).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                    <span style={{
                      background: DURUM_COLORS[t.durum] + '20',
                      color: DURUM_COLORS[t.durum],
                      padding: '3px 10px', borderRadius: 99, fontSize: '0.75rem', fontWeight: 600,
                    }}>
                      {t.durum === 'bekliyor' ? '⏳ Bekliyor' : t.durum === 'onaylandi' ? '✅ Onaylandı' : t.durum === 'reddedildi' ? '❌ Reddedildi' : '✓ Tamamlandı'}
                    </span>
                    {t.durum === 'bekliyor' && (
                      <>
                        <button className="btn btn-success btn-sm" onClick={() => handleDurumGuncelle(t.id, 'onaylandi')}>Onayla</button>
                        <button className="btn btn-danger btn-sm" onClick={() => handleDurumGuncelle(t.id, 'reddedildi')}>Reddet</button>
                      </>
                    )}
                    {t.durum === 'onaylandi' && (
                      <button className="btn btn-ghost btn-sm" onClick={() => handleDurumGuncelle(t.id, 'tamamlandi')}>Tamamlandı</button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      }
    </div>
  );
}

// ── Randevu Yönetimi (Paylaşımlı) ────────────────────────────
export function RandevularPage({ mod = 'rehber' }) {
  const [randevular, setRandevular] = useState([]);
  const [ogretmenler, setOgretmenler] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [planModal, setPlanModal] = useState({ open: false, randevuId: null, tarih: '', saat: '', sure_dakika: 30 });
  const [form, setForm] = useState({
    ogretmen_id: '', tarih: '', saat: '', sure_dakika: 30, konu: '', notlar: '',
  });

  const saatNormalize = (val = '') => String(val).slice(0, 5);

  const apiErrorToast = (err, fallback = 'İşlem sırasında hata oluştu') => {
    const status = err?.response?.status;
    const message = err?.response?.data?.message || err?.response?.data?.error || fallback;

    if (status === 400) {
      toast.error(`Geçersiz veri/saat: ${message}`);
      return;
    }
    if (status === 409) {
      toast.error(`Seçilen saat dolu veya çakışıyor: ${message}`);
      return;
    }
    toast.error(message);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rRes = await axios.get('/randevular').catch(() => ({ data: { success: false, data: [] } }));
      const oRes = (mod === 'ogrenci' || mod === 'veli')
        ? await axios.get('/ogretmenler-listesi').catch(() => ({ data: { success: false, data: [] } }))
        : { data: { success: false, data: [] } };
      if (rRes.data.success) setRandevular(rRes.data.data || []);
      if (oRes.data.success) {
        setOgretmenler(
          (oRes.data.data || []).map((o, i) => ({
            ...o,
            full_name: `${o.ad || ''} ${o.soyad || ''}`.trim(),
            _randevu_target_id: o.ogretmen_id ?? o.user_id ?? o.og_id ?? o.id ?? i,
          }))
        );
      }
    } catch (e) {
      console.error('Randevular load error:', e.message);
    }
    finally { setLoading(false); }
  }, [mod]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (mod === 'ogrenci' && !form.ogretmen_id) {
      toast.error('Öğretmen/Rehber seçimi zorunlu');
      return;
    }

    if (!form.konu) {
      toast.error('Konu zorunlu');
      return;
    }

    try {
      const seciliOgretmen = ogretmenler.find((o) => String(o._randevu_target_id) === String(form.ogretmen_id));
      const targetId = seciliOgretmen?._randevu_target_id ?? form.ogretmen_id;

      const payload = mod === 'ogrenci'
        ? {
            ogretmen_id: Number(targetId),
            konu: form.konu,
            notlar: form.notlar || undefined,
          }
        : {
            ogretmen_id: form.ogretmen_id ? Number(targetId) : undefined,
            tarih: form.tarih,
            saat: form.saat,
            sure_dakika: Number(form.sure_dakika),
            konu: form.konu,
          };
      await axios.post('/randevular', payload);
      toast.success('Randevu talebi gönderildi!');
      setShowModal(false);
      setForm({ ogretmen_id: '', tarih: '', saat: '', sure_dakika: 30, konu: '', notlar: '' });
      load();
    } catch (err) {
      apiErrorToast(err, 'Randevu oluşturulamadı');
    }
  };

  const handleDurumGuncelle = async (id, durum) => {
    try {
      await axios.put(`/randevular/${id}`, { durum });
      toast.success('Randevu güncellendi!');
      load();
    } catch (err) { toast.error(err?.response?.data?.message || 'Hata'); }
  };

  const openPlanlaOnayModal = (r) => {
    setPlanModal({
      open: true,
      randevuId: r.id,
      tarih: r.tarih ? String(r.tarih).slice(0, 10) : '',
      saat: r.saat ? String(r.saat).slice(0, 5) : '',
      sure_dakika: Number(r.sure_dakika) || 30,
    });
  };

  const handlePlanlaVeOnayla = async () => {
    if (!planModal.randevuId) return;
    if (!planModal.tarih || !planModal.saat || !planModal.sure_dakika) {
      toast.error('Tarih, saat ve süre zorunlu');
      return;
    }

    try {
      await axios.put(`/randevular/${planModal.randevuId}`, {
        durum: 'onaylandi',
        tarih: planModal.tarih,
        saat: planModal.saat,
        sure_dakika: Number(planModal.sure_dakika),
      });
      toast.success('Randevu planlanıp onaylandı');
      setPlanModal({ open: false, randevuId: null, tarih: '', saat: '', sure_dakika: 30 });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Onaylama hatası');
    }
  };

  const DURUM_COLORS = { bekliyor: 'var(--accent)', onaylandi: '#10B981', reddedildi: '#EF4444', tamamlandi: '#3B82F6', iptal: '#94A3B8' };
  const DURUM_ICONS = { bekliyor: '⏳', onaylandi: '✅', reddedildi: '❌', tamamlandi: '✓', iptal: '⊘' };
  const DURUM_LABELS = { bekliyor: 'Bekliyor', onaylandi: 'Onaylandı', reddedildi: 'Reddedildi', iptal: 'İptal', tamamlandi: 'Tamamlandı' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div className="section-header animate-fadeInUp">
        <div>
          <h2 style={{ fontFamily: 'var(--font-ui)', fontSize: '1.3rem' }}>📆 Randevular</h2>
          <div className="section-subtitle">{randevular.length} randevu</div>
        </div>
        {(mod === 'veli' || mod === 'ogrenci') && (
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>+ Randevu Al</button>
        )}
      </div>

      {loading ? <div className="page-loader"><div className="spinner" /></div> :
        randevular.length === 0 ? (
          <div className="card"><div className="empty-state"><div style={{ fontSize: '2.5rem' }}>📆</div><h3>Randevu yok</h3><p>Henüz randevu oluşturulmamış.</p></div></div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} className="animate-fadeInUp stagger-1">
            {randevular.map((r, i) => (
              <div key={i} className="card" style={{ borderLeft: `3px solid ${DURUM_COLORS[r.durum] || '#94A3B8'}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                      <span style={{ fontSize: '1.1rem' }}>{DURUM_ICONS[r.durum]}</span>
                      <span style={{ fontWeight: 700 }}>{r.talep_eden_adi} → {r.ogretmen_adi || 'Öğretmen'}</span>
                    </div>
                    <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                      {r.tarih ? `📅 ${new Date(r.tarih).toLocaleDateString('tr-TR')}` : '📅 Tarih öğretmen tarafından belirlenecek'}
                      {r.saat ? ` · ⏰ ${r.saat}` : ''}
                    </div>
                    {r.konu && <div style={{ fontSize: '0.82rem', marginTop: 6 }}>Konu: {r.konu}</div>}
                    {r.notlar && <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 4 }}>{r.notlar}</div>}
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                    <span style={{
                      background: DURUM_COLORS[r.durum] + '20',
                      color: DURUM_COLORS[r.durum],
                      padding: '4px 12px', borderRadius: 99, fontSize: '0.78rem', fontWeight: 600,
                    }}>
                      {DURUM_LABELS[r.durum] || r.durum}
                    </span>
                    {(mod === 'rehber' || mod === 'ogretmen') && r.durum === 'bekliyor' && (
                      <>
                        <button className="btn btn-success btn-sm" onClick={() => openPlanlaOnayModal(r)}>Onayla</button>
                        <button className="btn btn-danger btn-sm" onClick={() => handleDurumGuncelle(r.id, 'reddedildi')}>Reddet</button>
                      </>
                    )}
                    {r.durum === 'onaylandi' && (mod === 'rehber' || mod === 'ogretmen') && (
                      <button className="btn btn-ghost btn-sm" onClick={() => handleDurumGuncelle(r.id, 'tamamlandi')}>Tamamlandı</button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      }

      {showModal && (
        <div className="modal-backdrop" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">📆 Randevu Al</div>
              <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div className="form-group">
                <label className="form-label">Öğretmen / Rehber *</label>
                <select
                  className="form-select"
                  value={form.ogretmen_id}
                  onChange={(e) => setForm({ ...form, ogretmen_id: e.target.value, tarih: '', saat: '' })}
                >
                  <option value="">Seçin</option>
                  {ogretmenler.map((o, i) => (
                    <option key={o._randevu_target_id || i} value={o._randevu_target_id}>
                      {o.ad} {o.soyad}{o.rol ? ` (${o.rol === 'rehber_ogretmen' ? 'Rehber' : 'Öğretmen'})` : ''}
                    </option>
                  ))}
                </select>
              </div>
              {mod !== 'ogrenci' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                  <div className="form-group">
                    <label className="form-label">Tarih *</label>
                    <input
                      className="form-input"
                      type="date"
                      value={form.tarih}
                      onChange={(e) => setForm({ ...form, tarih: e.target.value, saat: '' })}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Süre (dk) *</label>
                    <select
                      className="form-select"
                      value={form.sure_dakika}
                      onChange={(e) => setForm({ ...form, sure_dakika: Number(e.target.value), saat: '' })}
                    >
                      {[15, 30, 45, 60].map((d) => <option key={d} value={d}>{d} dk</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Saat *</label>
                    <input className="form-input" type="time" value={form.saat} onChange={e => setForm({ ...form, saat: saatNormalize(e.target.value) })} />
                  </div>
                </div>
              )}
              {mod === 'ogrenci' && (
                <div style={{ padding: '10px 12px', border: '1px dashed var(--border)', borderRadius: 12, fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                  Bu ekranda sadece görüşme talebi oluşturursun. Gün ve saat seçimini öğretmen/rehber onay aşamasında belirler.
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Konu *</label>
                <input className="form-input" placeholder="Görüşme konusu" value={form.konu} onChange={e => setForm({ ...form, konu: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Notlar</label>
                <textarea className="form-textarea" placeholder="Ek bilgi..." value={form.notlar} onChange={e => setForm({ ...form, notlar: e.target.value })} style={{ minHeight: 70 }} />
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button className="btn btn-primary" style={{ flex: 1, justifyContent: 'center' }} onClick={handleCreate}>✓ Talep Gönder</button>
                <button className="btn btn-secondary" onClick={() => setShowModal(false)}>İptal</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {planModal.open && (
        <div className="modal-backdrop" onClick={() => setPlanModal({ open: false, randevuId: null, tarih: '', saat: '', sure_dakika: 30 })}>
          <div className="modal" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">🗓️ Randevu Planla ve Onayla</div>
              <button className="modal-close" onClick={() => setPlanModal({ open: false, randevuId: null, tarih: '', saat: '', sure_dakika: 30 })}>✕</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 14 }}>
              <div className="form-group">
                <label className="form-label">Tarih *</label>
                <input className="form-input" type="date" value={planModal.tarih} onChange={(e) => setPlanModal((p) => ({ ...p, tarih: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Saat *</label>
                <input className="form-input" type="time" value={planModal.saat} onChange={(e) => setPlanModal((p) => ({ ...p, saat: String(e.target.value).slice(0, 5) }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Süre (dk) *</label>
                <select className="form-select" value={planModal.sure_dakika} onChange={(e) => setPlanModal((p) => ({ ...p, sure_dakika: Number(e.target.value) }))}>
                  {[15, 30, 45, 60].map((d) => <option key={d} value={d}>{d} dk</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-success" style={{ flex: 1, justifyContent: 'center' }} onClick={handlePlanlaVeOnayla}>Onayla</button>
              <button className="btn btn-secondary" onClick={() => setPlanModal({ open: false, randevuId: null, tarih: '', saat: '', sure_dakika: 30 })}>İptal</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
